/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 10 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useId, useMemo, useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  SlickgridReact,
  SlickRowDetailView,
  ExtensionName,
  SlickEventHandler,
  type SlickgridReactInstance,
  type GridOption,
  type Column,
  type SlickDataView,
  type SlickGrid as SlickGridType,
  type OnScrollEventArgs,
  type OnColumnsResizedEventArgs,
  type Grouping,
  type ItemMetadata,
  type GetDataValueCallback,
  type SlickEventData,
  type OnHeaderCellRenderedEventArgs,
  type OnHeaderMouseEventArgs,
  type HeaderButtonOnCommandArgs,
} from "slickgrid-react";
import "@slickgrid-universal/common/dist/styles/css/slickgrid-theme-material.css";
import PagerComponent from "./Pagination";
import CircularProgress from "@mui/material/CircularProgress";
import { GridShimmer } from "./Shimmer";
import { ExcelExportService } from "@slickgrid-universal/excel-export";
import ToolBar from "./ToolBar";
import get from "lodash/get";
import dayjs, { type Dayjs } from "dayjs";
import OptionDropDown, { type VirtualAnchorElement } from "./OptionDropDown";
import { useTranslation, type TranslatedString } from "@i18n";
import Text from "../Text/Text";
import { getFilterPredicates } from "./Modals/FilterModal";
import { compareValues as compareSortValues } from "./sortUtils";
import { getConditionMet, applyLogicToResult, callRemoteSource } from "./filterUtils";
import IconComponent from "@CubeComponents/icons";
import "./index.css";
import { getFormatterByType } from "./Formatters/FormattersRegistery";
import { inferIconForColumn, HeaderWithIcon } from "./Formatters/Formatters";
import { renderToStaticMarkup } from "react-dom/server";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { createRoot } from "react-dom/client";
import "@CubeComponents/html/gridCellTooltip";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { getBackendUrl, getImageUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { GetSelectedViewerLayoutResponse } from "@ServerTypes/layoutView/layoutView.schema";
import type { ViewsNewFlowProps } from "@Types/layout";
import { Box } from "@mui/material";
import DOMPurify from "dompurify";
import { sanitizeCsvCell } from "@Utils/sanitize";
import type { Formatter } from "@slickgrid-universal/common";
import type { BasePubSubService, EventPubSubService } from "@slickgrid-universal/event-pub-sub";
import type { FormatterColDef, FormatterDateColDef, GridLike as FormatterGridLike } from "./Formatters/Formatters";

/** Shaped so it is assignable to slickgrid's `ItemMetadata` (whose `columns` is keyed by column id
 *  OR index) — that is what `dataView.getItemMetadata` has to return. */
interface RowMetadata {
  columns?: Record<string | number, { rowspan: number }>;
  cssClasses?: string;
}

interface RowSpanResult<T extends GridRowBase> {
  newDataset: T[];
  metadata: Record<string, RowMetadata>;
}

export interface FilterCondition {
  id: string;
  type: FilterType;
  option: QueryPredicate["operator"];
  text: unknown;
  logic?: string;
}

interface RowMoveData {
  /** Nullable: this describes the args slickgrid's RowMoveManager hands over, and the drop that
   *  lands above the first row arrives without one — both handlers below check for it. */
  insertBefore: number | null;
  rows: number[];
  grid?: SlickGridType;
}

/** Internal type for column reorder event args */
interface ColumnsReorderedArgs {
  impactedColumns: Column[];
}

/** Dropdown option type for column menu */
interface DropdownOption {
  label: string;
  value?: string;
}

/** Scroll event args from SlickGrid */
interface ScrollEventArgs {
  grid: SlickGridType;
  scrollTop: number;
  scrollHeight: number;
  triggeredBy?: string;
}

type SlickEventArgs = Record<string, unknown> & { originalEvent?: Event };

export interface GridClickEvent {
  detail: {
    args: {
      row: number;
      cell: number;
      grid: SlickGridType;
    };
    eventData: {
      target: HTMLElement;
    };
  };
}
type FilterType = "Text" | "Date" | "Number" | "MultiSelect" | "FileSize";
/** Row ID type - supports string, number, or ObjectId-like objects */
type RowId = string | number | { toString(): string };
type ParentId = string | number | null;

/**
 * Narrows a value read out of a row under a key that is only known at runtime (`idMapping`,
 * `parentIdMapping`) to a {@link RowId}. Deliberately truthiness-shaped: every one of these lookups
 * has always treated `""` / `0` / `null` as "this row has no id", and keeping that here means the
 * guard can replace the `as RowId` casts without moving any behaviour.
 */
const isRowId = (value: unknown): value is RowId =>
  Boolean(value) && (typeof value === "string" || typeof value === "number" || typeof value === "object");

/**
 * Base type for SlickGrid row data.
 * Your row interface should include these optional SlickGrid-specific fields.
 */
export type GridRowBase = {
  id?: RowId;
  isRowDisabled?: boolean;
  __isDummy?: boolean;
  __hostRowId?: RowId;
  __parentId?: string;
  disableBorder?: boolean;
  __hasChildren?: boolean;
  __collapsed?: boolean;
  parentId?: RowId | null;
  indentLevel?: number;
  treeLevel?: number;
  hasChild?: boolean;
  isExpanded?: boolean;
  _id?: RowId;
  __group?: boolean;
  __groupTotals?: boolean;
};

export interface ColumnHeaderButton {
  cssClass?: string;
  command?: string;
  tooltip?: string;
}

export interface ColumnHeader {
  buttons?: ColumnHeaderButton[];
}

/**
 * Excel-export options for a column. `valueParserCallback` returns a plain value
 * for the spreadsheet; its `args.dataContext` is the typed row (T), so the
 * callback is aware of the column's row type coming through the dataset.
 */
export interface ExcelExportColumnOptions<T extends GridRowBase> {
  valueParserCallback?: (value: unknown, args: { dataContext: T }) => unknown;
  [key: string]: unknown;
}

/**
 * Column definition interface for SlickGrid columns.
 * The `field` property must be a valid key of your row type.
 * The `formatter` value parameter will be typed based on the field.
 */
export interface ColumnDefinition<T extends GridRowBase, K extends keyof T = keyof T> {
  id: string | number;
  /** Field name - must be a key of the row type T */
  field: K;
  name?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  originalWidth?: number;
  sortable?: boolean;
  /** Alias for sortable */
  allowSorting?: boolean;
  filterable?: boolean;
  /** Allow filtering on this column */
  allowFiltering?: boolean;
  groupable?: boolean;
  type?: string;
  header?: ColumnHeader;
  wrapText?: boolean;
  filterType?: FilterType;
  formatterType?: string;
  clipMode?: string;
  excelExportOptions?: ExcelExportColumnOptions<T>;
  exportWithFormatter?: boolean;
  excludeFromExport?: boolean;
  headerTemplate?: (args: { column: ColumnDefinition<T, K>; onClick: (e: unknown) => void }) => React.ReactNode;
  headerRenderer?: () => string | HTMLElement;
  _customHeaderRenderer?: () => string | HTMLElement;
  /** Formatter function - value type is flexible to support various column types */
  formatter?: (
    row: number,
    cell: number,
    value: T[K],
    columnDef: ColumnDefinition<T, K>,
    dataContext: T,
    grid?: SlickGridType,
  ) => unknown;
  editorTemplate?: (args: { value: T[K]; updatedColumn: ColumnDefinition<T, K>; args: T }) => React.ReactNode;
  /** Custom value getter - returns computed value for sorting/filtering/display */
  _valueGetter?: (row: T) => unknown;

  /**
   * Custom excel value getter - returns a clean, plain-text value for excel export.
   * Takes priority over _valueGetter and formatter for excel export.
   * Use when the display formatter returns HTML/rich content and you need a flat
   * string for the spreadsheet (the "parseback" from formatter to plain text).
   */
  _excelValueGetter?: (row: T) => unknown;
  /**
   * Custom filter value getter — returns the value used for filtering, the filter
   * suggestion-option list, and the search bar. Takes priority over _valueGetter for
   */
  _filterValueGetter?: (row: T) => unknown;
  /** Custom image getter - returns image URL */
  _imageGetter?: (row: T) => string;
  hidden?: boolean;
  initiallyHidden?: boolean;
  /** Whether the column is visible */
  visible?: boolean;
  /** Text alignment for the column */
  textAlign?: "Left" | "Right" | "Center" | string;
  /** Mark column as primary key for row identification */
  isPrimaryKey?: boolean;
  /** CSS class for header */
  headerCssClass?: string;
  /** Column group for grouped headers */
  columnGroup?: string;
  /** Disable menu options for this column */
  disableMenuOptions?: boolean;
  /** Disable sort for this column in toolbar/context menu */
  disableSorting?: boolean;
  /** Disable filter for this column in toolbar/context menu */
  disableFiltering?: boolean;
  /** Disable grouping for this column in toolbar/context menu */
  disableGrouping?: boolean;
  /** Display label for the column (used by toolbar / header rendering). */
  label?: string;
  /** Custom header renderer returning a React node. */
  renderHeader?: () => React.ReactNode;
  /** Standard SlickGrid column flags + grouping/reorder aliases. */
  resizable?: boolean;
  reorderable?: boolean;
  isFrozen?: boolean;
  allowReordering?: boolean;
  allowGrouping?: boolean;
}

/**
 * A column definition as the RUNTIME may present it. Legacy call sites set `_valueGetter` to a
 * field-path STRING rather than a getter (the same arm {@link ColumnWithValueGetters} documents for
 * columns read back off the grid), which the public {@link ColumnDefinition} does not admit.
 * Widening `ColumnDefinition._valueGetter` itself is the real fix, but it would re-check every
 * consumer that calls one (ToolBar, FilesGridHelper, sortUtils, groupUtils, Formatters, …), so the
 * few places in this file that handle the legacy arm read their columns at this type instead — a
 * plain assignment, since `ColumnDefinition<T>` is assignable to it.
 */
type RuntimeColumnDefinition<T extends GridRowBase> = Omit<ColumnDefinition<T>, "_valueGetter"> & {
  _valueGetter?: ((row: T) => unknown) | string;
};

/**
 * The same widening for `id`. {@link ColumnDefinition} declares it REQUIRED, but
 * {@link withDynamicColumns} — the documented escape hatch for columns a feature only builds at
 * runtime — takes `Record<string, unknown>` entries, so a column carrying no `id` at all really
 * does reach this file. Read those columns at this type wherever the `id` is used as a key, so the
 * checks stay meaningful instead of stringifying `undefined` into a field/style name.
 */
type ColumnWithOptionalId<T extends GridRowBase> = Omit<RuntimeColumnDefinition<T>, "id"> & {
  id?: string | number;
};

declare const _ValidatedColumnBrand: unique symbol;
type ValidatedColumnBrandType = typeof _ValidatedColumnBrand;

/**
 * Validated column definition type - can only be created via `typedColumns()`.
 * Plain object literals will NOT be assignable to this type.
 */
export type ValidatedColumnDefinition<T extends GridRowBase> = ColumnDefinition<T, keyof T> & {
  /** @internal Brand to ensure columns are created with typedColumns() */
  readonly __brand: ValidatedColumnBrandType;
};

/**
 * Creates validated, type-safe column definitions for SlickGrid.
 *
 * REQUIRED: All SlickGrid columns must be created with this function
 * to ensure type safety between field names and formatter value types.
 *
 * @example
 * interface ResourceRow extends GridRowBase {
 *   counter: number;
 *   name: string;
 *   selectionSetIds?: string[];
 * }
 *
 * const columns = typedColumns<ResourceRow>([
 *   {
 *     id: "name",
 *     field: "name",  // ✅ TypeScript checks this is keyof ResourceRow
 *     formatter: (row, cell, value, col, data) => {
 *       // value is typed as `string`
 *       return value.toUpperCase();
 *     }
 *   },
 *   {
 *     id: "selectionSetIds",
 *     field: "selectionSetIds",
 *     formatter: (row, cell, value, col, data) => {
 *       // value is typed as `string[] | undefined`
 *       // TypeScript REQUIRES you to check for undefined!
 *       if (!Array.isArray(value)) return "";
 *       return value.join(", ");
 *     }
 *   }
 * ]);
 */

export function typedColumns<TRow extends GridRowBase>(
  columns: Array<{ [K in keyof TRow]: ColumnDefinition<TRow, K> }[keyof TRow]>,
): ValidatedColumnDefinition<TRow>[] {
  return brandColumns<TRow>(columns);
}

/**
 * The single place the {@link ValidatedColumnDefinition} brand is minted.
 *
 * `__brand` is a PHANTOM member: `_ValidatedColumnBrand` is a `declare const unique symbol`, so it
 * has no runtime representation and no runtime value can ever carry it. The assertion below IS the
 * brand — there is nothing to validate at runtime. What actually checks a caller is the PARAMETER
 * type of the exported constructor it went through (`typedColumns` pairs each `field` with its own
 * `formatter` value type via the mapped-union; `withDynamicColumns` states that some entries are
 * only known at runtime). Keeping the mint here means the file contains exactly one such assertion
 * instead of one per constructor.
 */
function brandColumns<TRow extends GridRowBase>(
  columns: Array<ColumnDefinition<TRow, keyof TRow> | ValidatedColumnDefinition<TRow> | Record<string, unknown>>,
): ValidatedColumnDefinition<TRow>[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- phantom brand, see doc comment above: `__brand` has no runtime representation, so no value can carry it
  return columns as ValidatedColumnDefinition<TRow>[];
}

/**
 * Brands a column list that mixes `typedColumns()` output with columns only known at RUNTIME —
 * custom-field columns built by `getSubColumns()` from a form template, whose `field` keys
 * (`customFields.<questionId>`) cannot exist on the row type and so can never satisfy `keyof TRow`.
 *
 * Use it ONLY for that mix, and keep every statically-known column inside `typedColumns()`, which
 * really does check `field` against the row type. Grids with dynamic columns used to assert the same
 * thing inline at the JSX boundary (`columnDefinitions={cols as typeof cols &
 * ValidatedColumnDefinition<Row>[]}`); this keeps that one unavoidable assertion here, next to the
 * brand it is about, instead of in each feature.
 */
export function withDynamicColumns<TRow extends GridRowBase>(
  columns: Array<ValidatedColumnDefinition<TRow> | Record<string, unknown>>,
): ValidatedColumnDefinition<TRow>[] {
  return brandColumns<TRow>(columns);
}

export type QueryOperator =
  | "equal"
  | "greaterthan"
  | "greaterthanorequal"
  | "lessthan"
  | "lessthanorequal"
  | "notequal"
  | "null"
  | "notnull"
  | "startswith"
  | "endswith"
  | "contains"
  | "doesnotstartwith"
  | "doesnotendwith"
  | "doesnotcontain"
  | "like"
  | "empty"
  | "notempty"
  | "between";

export interface QueryPredicate {
  field: string;
  operator: QueryOperator;
  value: unknown;
  isComplex?: boolean;
  ignoreCase?: boolean;
  ignoreAccent?: boolean;

  condition?: string;
  predicates?: QueryPredicate[];
}

export interface QuerySortInfo {
  name: string;
  direction: "ascending" | "descending";
}

export interface QueryShape {
  where?: Array<QueryPredicate | Record<string, unknown>>;
  skip?: number;
  take?: number;
  requiresCounts?: boolean;
  sorted?: QuerySortInfo[];
  nextPageToken?: string | null;
  select?: string[];
  expand?: string[];
  search?: string;
  params?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * `triggerUpdateCurrentView` and the saved-view payloads arrive as untyped bags (index signature /
 * server JSON), so a query carried in one is checked rather than asserted. QueryShape is an open
 * record — every one of its members is optional — so "is a plain object" is the whole contract.
 */
const isQueryShape = (value: unknown): value is QueryShape =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export interface ExcelExportConfig {
  filename?: string;
  sheetName?: string;
  exportWithFormatter?: boolean;
}

/** Sort condition for local sorting */
export interface SortCondition {
  column: string;
  direction: "asc" | "desc";
}

/** Group condition for grouping */
export interface GroupCondition {
  column: string;
  direction?: "asc" | "desc";
  preserveOrder?: boolean;
}

/** Payload of the index-signature-carried `onConditionsChange` prop (see its call site). */
type ConditionsChangePayload = {
  sortConditions: SortCondition[];
  filterConditions: Record<string, FilterCondition>;
  groupConditions: GroupCondition[];
};

/** Applied filter condition for toolbar display */
export interface AppliedFilterCondition {
  field: string;
  operator: string;
  value: unknown;
  name?: string;
  logic?: string;
}

/**
 * Date filter value can be a Dayjs object or an object with a `date` property. `null`/`undefined`
 * are part of the type because the filter text this is called with comes off a stored filter
 * condition (`FilterCondition.text` is `unknown`, and saved views deserialise into it), so a
 * missing value really does reach here — see the null handling in applyDateFilter.
 *
 * The object arm is `{ date?: unknown }`, not `{ date: Dayjs }`: the same slot also holds a bare
 * `Date` and the `{ startDate, endDate }` bag a range filter saves, and a JSON round trip leaves
 * whatever it left. Declaring `date` as present-and-Dayjs would make applyDateFilter's `"date" in`
 * test look redundant when it is the only thing keeping those bags out of the unwrap path.
 */
type DateFilterValue = Dayjs | { date?: unknown } | Date | string | number | null | undefined;

/** dayjs' own input union. A stored value that is none of these (an object bag, a boolean) was
 *  handed to dayjs whole before this guard existed and came back Invalid; `NaN` keeps that exact
 *  result. `null`/`undefined` pass through untouched so dayjs keeps its own meaning for them. */
const toDayjsInput = (value: unknown): string | number | Date | Dayjs | null | undefined =>
  value === null ||
  value === undefined ||
  typeof value === "string" ||
  typeof value === "number" ||
  value instanceof Date ||
  dayjs.isDayjs(value)
    ? value
    : Number.NaN;

/**
 * Date-range filter text as it is STORED. It lives in `FilterCondition.text`, which is `unknown`,
 * and a saved view round-trips it through JSON — so each member is read unvalidated and handed to
 * `toValidDayjs`, which is what turns it into a real date (or drops it).
 */
interface DateRangeText {
  startDate?: unknown;
  endDate?: unknown;
  date?: unknown;
}

/** `FilterCondition.text` is `unknown`; for a Date filter it holds a {@link DateRangeText} bag.
 *  Anything that is not an object simply carries no dates, which `toValidDayjs` then rejects. */
const isDateRangeText = (value: unknown): value is DateRangeText => typeof value === "object" && value !== null;

export interface LayoutData {
  /** Mongo id of the saved view document — resolveInitialViews dedupes repeat signals by it */
  _id?: string;
  columns?: Array<string | number>;
  /** Per-column widths keyed by column id; optional so views saved before CUBE-29114 stay valid */
  columnWidths?: Record<string, number>;
  groupConditions?: GroupCondition[];
  pinnedColumns?: string[];
  enableFiltering?: boolean;
  enableGrouping?: boolean;
  enableSorting?: boolean;
  sortConditions?: SortCondition[];
  filterConditions?: Record<string, FilterCondition>;
  enableColumnChooser?: boolean;
  queryConditions?: QueryShape | null;
  /** Row height in px picked via the row-resize option; optional/opt-in (CUBE-29114) */
  rowHeight?: number;
  /** Text-wrapping state picked via the row-resize option; optional/opt-in (CUBE-29114) */
  enableWrapText?: boolean;
  isDefault?: boolean;
  data?: LayoutData;
}

export interface ToolbarItem {
  id?: string;
  text?: string;
  prefixIcon?: string;
  suffixIcon?: string;
  cssClass?: string;
  disabled?: boolean;
  visible?: boolean;
  align?: "Left" | "Right";
  // ToolBar.tsx buckets items by these flags (right-aligned / pinned).
  rightAllign?: boolean;
  sticky?: boolean;
  items?: ToolbarItem[];
  // Custom-rendered toolbar entries: ToolBar.tsx invokes `template(props)` to render the item.
  template?: (props: Record<string, unknown>) => React.ReactNode;
  props?: Record<string, unknown>;
}

/**
 * Type-safe constructor for a custom-template toolbar entry. Captures the template
 * component's props type `P` so `template` and `props` are checked against each other,
 * then erases to the heterogeneous {@link ToolbarItem} the toolbar array holds. This is
 * the existential-pairing analogue of {@link typedColumns} for toolbar templates.
 */
export function toolbarItem<P extends Record<string, unknown>>(config: {
  id?: string;
  template: (props: P) => React.ReactNode;
  props: P;
  rightAllign?: boolean;
  sticky?: boolean;
}): ToolbarItem {
  // The erasure is done by CLOSING OVER the checked pair rather than asserting it away: ToolBar.tsx
  // renders an item as `item.template?.(item.props ?? {})`, and the argument it would pass is
  // exactly `config.props`, so ignoring the parameter here is behaviourally identical while keeping
  // `template`/`props` checked against each other.
  return { ...config, template: () => config.template(config.props) };
}

export interface SlickGridProps<T extends GridRowBase> {
  id?: string;
  defaultPinnedColumns?: string[];
  showPageCount?: boolean;
  rowUnitLabel?: TranslatedString;
  totalRowCount?: number;
  infiniteScroll?: boolean;
  showLoader?: boolean;
  disableHeader?: boolean;
  disableMarginForNoData?: boolean;
  hideNoDataIllustration?: boolean;
  noDataIllustration?: () => React.ReactElement;
  enableWrapText?: boolean;
  setEnableWrapText?: (value: boolean) => void;
  /**
   * Truncate the tree (title) column's text with an ellipsis when it overflows the
   * column width, instead of letting it clip or push the layout. Opt-in per grid via
   * the `tree-row-ellipsis` container class (see the cssClasses effect). Responsive —
   * the title flexes to the available cell width and re-truncates as the column resizes.
   * Pairs with a native browser tooltip on hover for the full text (see enableTreeEllipsis
   * handling in reactGridReady); mutually exclusive in effect with `enableWrapText`.
   */
  enableTreeEllipsis?: boolean;
  disableRepaintOnDataChange?: boolean;
  dataset?: T[];
  datasetHierarchical?: T[];
  surpassedDataset?: T[];
  /**
   * Column definitions - MUST be created with `typedColumns<T>()` for type safety.
   *
   * @example
   * import { typedColumns } from "@CubeComponents/html/SlickGrid1";
   *
   * const columns = typedColumns<MyRowType>([
   *   { id: "name", field: "name", name: "Name" },
   *   { id: "count", field: "count", name: "Count" }
   * ]);
   *
   * <SlickGrid dataset={data} columnDefinitions={columns} />
   */
  // Wrappers (e.g. EstimateCostDatabaseItemsGridNew) pass nullable variants of these
  // props because they hold them in `useState` whose initial value is null.
  // The runtime guard `gridColumns && treeGridData()` ensures the grid only
  // mounts after columns are populated, so widening these to allow null at
  // the prop type level keeps callers honest without changing behaviour.
  columnDefinitions: ValidatedColumnDefinition<T>[] | null;
  query?: QueryShape | { [key: string]: unknown };
  /**
   * Hook to transform the remote payload before it is bound. The parameter is the union of what the
   * two call sites actually pass — `processRemoteResponse` passes the whole `RemoteDataResponse<T>`,
   * while the paginated path passes the already-unwrapped `T[] | { result: T[] }`. It was declared
   * `unknown`, which forced every consumer to re-invent the shape and assert or guard it; handlers
   * still written as `(resp: unknown) => T[]` remain assignable, so this only adds information.
   */
  beforeDataBound?: (resp: RemoteDataResponse<T> | T[] | { result: T[] }) => T[];
  remoteDataSource?: string | null | ((body: { value: QueryShape }) => Promise<RemoteDataResponse<T>>);
  allowPagination?: boolean;
  allowGrouping?: boolean;
  gridOptions?: Partial<GridOption>;
  levelPropName?: string;
  enableFiltering?: boolean;
  disableCustomSorting?: boolean;
  disableSorting?: boolean;
  enableColumnChooser?: boolean;
  enableMultiSelect?: boolean;
  onSelectedRowIdsChanged?: (args: SlickEventArgs) => void;
  disabledIds?: Array<RowId>;
  selectCheckBoxOnRowClick?: boolean;
  selectAtmostOneCheckBoxOnRowClick?: boolean;
  /** Disable checkbox selection column */
  disableCheckBoxSelection?: boolean;
  onGroupChanged?: (args: unknown) => void;
  onExtensionRegistered?: (ext: unknown) => void;
  setGridReady?: (ready: boolean, dataCount?: number) => void;
  onReactGridCreated?: (instance: SlickgridReactInstance) => void;
  toolbar?: ToolbarItem[];
  toolbarClickHandler?: (cmd: string) => void;
  showToolbarBorder?: boolean;
  /**
   * Optional DOM node to portal the toolbar into, instead of rendering it inline above the grid. Lets a
   * parent host the (fully-wired) grid toolbar in a shared/common location — e.g. spanning a tree + this
   * grid. The toolbar keeps all of this grid's state/handlers; only its DOM position changes. When omitted
   * (the default), the toolbar renders inline exactly as before.
   */
  toolbarContainer?: HTMLElement | null;
  /** Optional grid ID override - defaults to auto-generated ID */
  gridId?: string;
  /**
   * Visually hide the grid's scrollbars while keeping scrolling fully functional (wheel / drag / keyboard).
   * Useful when the grid sits inside a container that owns the scroll affordance (e.g. a folder tree).
   */
  hideScrollbar?: boolean;
  disableGridMargin?: boolean;
  callbackForVisibleColumnsUpdate?: (columns: ValidatedColumnDefinition<T>[]) => void;
  toolbarHeight?: number | string;
  propertiesPanel?: boolean;
  propertiesDisabled?: boolean;
  excelExport?: boolean;
  excelExportDisabled?: boolean;
  excelExportCallback?: () => void;
  onExcelExportComplete?: () => void;
  isEditMode?: boolean;
  handleUpdateCurrentView?: (payload: {
    columns?: Array<string | number>;
    sortConditions?: SortCondition[];
    filterConditions?: Record<string, FilterCondition>;
    groupConditions?: GroupCondition[];
  }) => void;
  onClick?: (e: GridClickEvent) => void;
  onDblClick?: (e: { detail: { args: { row: number; cell: number } } }) => void;
  onFilterChange?: (activeKeys: string[], conditions: Record<string, FilterCondition>) => void;
  treeGrid?: boolean;
  hasChild?: boolean;
  disableRowBorder?: boolean;
  /** Hide the vertical separator line between columns (the cell/header inline-end borders). */
  disableColumnBorder?: boolean;
  /** Hide the grid's outer container border — for grids hosted inside a card/panel that owns the border. */
  disableOuterBorder?: boolean;
  hasChildMapping?: string;
  idMapping?: string;
  parentIdMapping?: string;
  titleFormatter?: Formatter;
  initialSortColumnId?: string | number;
  dropdownColumn?: number;
  disableChildDataFetch?: boolean;
  childGridComponent?: React.ComponentType<{ row: T }> | React.FC<{ row: T }> | object;
  dragAndDrop?: boolean;
  disableTreeCollapseOnDataChange?: boolean;
  initiallyExpanded?: boolean;
  rowDrop?: (payload: { parentId: ParentId; movedRowData: T }) => Promise<boolean>;
  initialSortDirection?: "ASC" | "DESC";
  enableSelectAllCheckbox?: boolean;
  enablePointerForRows?: boolean;
  preAppliedFilters?: Record<string, FilterCondition>;
  enableGridMenu?: boolean;
  enableColumnPicker?: boolean;
  disableFullHeight?: boolean;
  onColumnsReordered?: (e: Event, args: ColumnsReorderedArgs) => void;
  // KNOWN BUG: `renderOption` is declared on `SlickGridProps` but read nowhere in SlickGrid1/ — it only
  // falls into the `{...props}` spread onto the vendor `SlickgridReact` element below, which has no
  // such prop.
  // Impact: latent - no call site passes it today (every other `renderOption` under src belongs to MUI
  // `AutoComplete` / `SelectDropDown`); a future caller would silently render nothing.
  // Blocked on: deleting `renderOption` from `SlickGridProps` in this file, or actually wiring it to the
  // option list — either is a product decision, not a typing fix.
  renderOption?: (option: unknown) => React.ReactNode;
  /** Disable virtualisation for smaller datasets */
  disableVirtualisation?: boolean;
  /** Disable menu options for all columns */
  disableMenuOptions?: boolean;
  /** Disable the grid's built-in right-click context menu (useful when a custom context menu is provided externally) */
  disableContextMenu?: boolean;
  /** Enable filtering option in toolbar */
  enableFilteringOption?: boolean;
  /** Enable sorting option in toolbar */
  enableSortingOption?: boolean;
  /** Enable grouping option in toolbar */
  enableGroupingOption?: boolean;
  /** Enable refresh option (icon button) in toolbar */
  enableRefreshOption?: boolean;
  /**
   * Callback fired when the toolbar refresh option is clicked. When provided, the grid delegates
   * refresh entirely to it; otherwise the button falls back to the grid's own internal refresh().
   */
  refreshOptionCallBack?: () => void;
  /** Enable views feature */
  enableViews?: boolean;
  /**
   * Views options. `onClickLayout` is deliberately excluded — every call site passes it as its own
   * top-level `onClickLayout` prop instead, which carries this grid's own `LayoutData` shape (see the
   * KNOWN MISMATCH note at the ToolBar forwarding site).
   */
  viewsOptions?: Omit<ViewsNewFlowProps, "onClickLayout">;
  /**
   * Skip the automatic header-icon inference for columns without a headerRenderer/headerTemplate.
   * Used by print/PDF-export grids, where headers must render as plain text.
   */
  disableAutoHeaderIcons?: boolean;
  /** Enable row resize */
  enableRowResize?: boolean;
  /** Handle row resize option click. RowResizeModal invokes it as (size, px) — e.g. ("wrap", 22). */
  handleRowResizeOptionClick?: (size: string, height: number) => void;
  /** Has custom column chooser */
  hasCustomColumnChooser?: boolean;
  /** Callback when layout is clicked */
  onClickLayout?: (params: LayoutData) => void;
  /** Callback when columns are saved */
  onSaveColumns?: (cols: Column[]) => void;
  /** Trigger to update the current view */
  triggerUpdateCurrentView?: Record<string, unknown> | false;
  /** Disable toolbar */
  disableToolbar?: boolean;
  /** Row height in pixels */
  rowHeight?: number;
  // KNOWN BUG: `gridHeight` is declared here but read nowhere in SlickGrid1/ — like `renderOption`
  // above it only falls into the `{...props}` spread onto the vendor `SlickgridReact` element, which
  // has no such prop. The grid sizes itself from `gridOptions` / `disableFullHeight` instead.
  // Impact: every caller that passes it (ExcelImportNew, FileManagerLeftSideTree) gets the default
  // height, not the one it asked for.
  // Blocked on: deciding whether to wire it into `gridOptions.gridHeight` (a real layout change at
  // those call sites) or delete the prop and its callers — a product decision, not a typing fix.
  /** Grid height (e.g., "100%", "200px") */
  gridHeight?: string;
  enableSearchBar?: boolean;
  searchPlaceholder?: string;
  searchFields?: string[];
  searchDebounceMs?: number;
  /** Allow additional props */
  [key: string]: unknown;
  searchCallBack?: (params: string) => void;
}

/** Remote data response structure */
interface RemoteDataResponse<T extends GridRowBase> {
  data?: {
    result?: T[];
    nextPageToken?: string;
    isLast?: boolean;
    count?: number;
  };
}

/** Grid state snapshot for internal tracking */
interface GridStateSnapshot<TRow extends GridRowBase> {
  pageSize?: number;
  currentPage?: number;
  query?: QueryShape | undefined;
  originalDataset?: TRow[] | undefined;
  sortConditions?: SortCondition[];
  appliedFilters?: string[];
  dataset?: TRow[];
  filterConditions?: Record<string, FilterCondition>;
  filterPredicates?: Record<string, unknown> | string;
  currentColumnId?: string;
  totalItemCount?: number;
  groupConditions?: GroupCondition[];
  pinnedColumns?: string[];
  loaderOnPageChange?: boolean;
  loaderForInfiniteScroll?: boolean;
  allPrerequisiteLoaded?: boolean;
  enableWrapText?: boolean | undefined;
  setGroupConditions?: React.Dispatch<React.SetStateAction<GroupCondition[]>>;
  setSortConditions?: React.Dispatch<React.SetStateAction<SortCondition[]>>;
  setFilterConditions?: React.Dispatch<React.SetStateAction<Record<string, FilterCondition>>>;
  setAppliedFilters?: React.Dispatch<React.SetStateAction<string[]>>;
  disableMenuOptions?: boolean | undefined;
  isEditMode?: boolean | undefined;
  appliedFilterConditions?: AppliedFilterCondition[];
  infiniteScroll?: boolean | undefined;
  allowPagination?: boolean | undefined;
}

type SlickgridExtras<T extends GridRowBase> = {
  addRecord?: (rowToAdd: T, position?: number) => void;
  removeRecord?: (position?: number, rowToRemove?: { id: string; parentId?: string | null }) => void;
  refresh?: (preserveSelection?: boolean) => void;
  resetPagination?: (skipApiCall?: boolean) => void;
  appliedFilters?: string[];
  deleteRow?: boolean;
  actual?: unknown;
  applyGrouping?: (conditions: GroupCondition[]) => void;
  gridState?: GridStateSnapshot<T>;
  _lastScrollLeft?: number;
  _scrollTimeout?: ReturnType<typeof setTimeout>;
  toolbarModule?: {
    enableItems: (ids: string[], enabled: boolean) => void;
  };
  setFilterRowVisibility?: (visible: boolean) => void;
  currentViewData?: T[];
  /** Last dataset this grid pushed onto the instance, for consumers that read it back off the ref. */
  dataset?: T[];
  /** Runs fn with view auto-persistence suppressed (same bracket the grid uses when applying a view itself). */
  suppressViewUpdates?: (fn: () => void) => void;
};

export type SlickgridReactInstanceX<T extends GridRowBase> = SlickgridReactInstance & SlickgridExtras<T>;

/** Public ref type for SlickGrid1 with row-type-aware dataView. Use with useRef<SlickGrid1Ref<YourRowType> | null>(null). */
export type SlickGrid1Ref<T extends GridRowBase> = Omit<SlickgridReactInstance, "dataView"> & {
  // KNOWN BUG: `SlickDataView.getAllSelectedItems<T>(): T[]` is a lying vendor declaration — the
  // implementation (@slickgrid-universal/common/src/core/slickDataview.ts:1598) pushes
  // `getItemById(id)` for every id still in `selectedRowIds`, which is `undefined` once that row has
  // left a remotely paged/filtered dataset.
  // Impact: a consumer reading `ref.current.dataView.getAllSelectedItems()` on a grid that does NOT
  // set `enableSearchBar` gets `undefined` holes typed as `T` and crashes on the first property read;
  // grids with `enableSearchBar` are covered by the filtering wrapper installed in `reactGridReady`.
  // Blocked on: `getAllSelectedItems` on `SlickDataView` (@slickgrid-universal/common) declaring
  // `Array<T | undefined>`, or this grid installing that wrapper unconditionally instead of only
  // under `enableSearchBar`.
  dataView: SlickDataView<T>;
} & SlickgridExtras<T>;

const NO_OF_FETCHED_ROWS = 1000000;
const FETCH_SIZE = 30;

const DEFAULT_FILTER_OPTIONS: Record<
  Exclude<FilterType, "MultiSelect">,
  { option: QueryPredicate["operator"]; text: unknown }
> = {
  Text: { option: "startswith", text: "" },
  Date: { option: "equal", text: { date: dayjs() } },
  Number: { option: "equal", text: 0 },
  FileSize: { option: "greaterthan", text: { size: "", unit: "MB" } },
};

/**
 * THE normaliser for a filter-condition map that did not come from the filter UI.
 *
 * A saved view is server JSON that is NOT validated on the way in, so a stored condition can be
 * missing the `option` that `FilterCondition` declares as required. It is filled in here — with the
 * same per-type default the filter UI seeds — so everything downstream can simply trust the
 * declared type instead of re-defending against a missing operator at each use. Both members are
 * read back out at `unknown` precisely because the declared type is not a guarantee at this edge.
 *
 * Every unvalidated entry point runs through it: `processFilterConditions` (views flow),
 * the `props.preAppliedFilters` effect, and `getUpdatedFilterConditions` (handed to the toolbar as
 * an untyped prop). Module scope, so no caller can be ordered before it.
 */
const withResolvedFilterOptions = (conditions: Record<string, FilterCondition>): Record<string, FilterCondition> =>
  Object.fromEntries(
    Object.entries(conditions).map(([key, condition]) => {
      const defaults: Record<string, { option: QueryPredicate["operator"] }> = DEFAULT_FILTER_OPTIONS;
      const storedOption: unknown = condition.option;
      if (typeof storedOption === "string" && storedOption) return [key, condition];
      const storedType: unknown = condition.type;
      const fallback = typeof storedType === "string" ? (defaults[storedType]?.option ?? "equal") : "equal";
      return [key, { ...condition, option: fallback }];
    }),
  );

// @mui/icons-material Check — keep identical to CheckBoxComponent's CustomCheckedIcon glyph
const GRID_CHECKBOX_CHECK_PATH = "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";

function muiCheckboxFormatter<T extends GridRowBase>(
  row: number,
  _cell: number,
  _value: unknown,
  _column: Column<T>,
  dataContext: T,
  grid: SlickGridType,
): HTMLElement {
  const el = document.createElement("div");
  const rowData = dataContext as T & { disableSelection?: boolean };
  const isChecked = grid.getSelectedRows().includes(row);
  const isDisabled = Boolean(rowData.disableSelection);

  // [plain-DOM] pixel-match of CheckBoxComponent (MUI Checkbox + custom 16px icon, styles in
  // index.css .cube-gridcheckbox) with zero React per cell. Rendering current state is enough:
  // the checkbox-selector plugin invalidates changed rows on every selection change.
  const button = document.createElement("span");
  button.className = isDisabled ? "cube-gridcheckbox cube-gridcheckbox--disabled" : "cube-gridcheckbox";

  const input = document.createElement("input");
  input.type = "checkbox";
  input.className = "cube-gridcheckbox__input";
  input.checked = isChecked;
  input.disabled = isDisabled;

  const box = document.createElement("span");
  box.className = [
    "cube-gridcheckbox__box",
    isChecked ? "cube-gridcheckbox__box--checked" : "",
    isDisabled ? "cube-gridcheckbox__box--disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");
  if (isChecked) {
    box.innerHTML = `<svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d="${GRID_CHECKBOX_CHECK_PATH}"/></svg>`;
  }

  button.append(input, box);
  el.appendChild(button);

  el.onmousedown = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  el.onclick = (e) => {
    if (!rowData.disableSelection) {
      e.stopPropagation();
      e.preventDefault();

      const selected = new Set(grid.getSelectedRows());

      if (selected.has(row)) {
        selected.delete(row);
      } else {
        selected.add(row);
      }

      grid.setSelectedRows([...selected]);
    }
  };

  return el;
}

export function muiHeaderCheckboxRenderer(grid: SlickGridType): () => HTMLElement {
  return () => {
    const el = document.createElement("div");
    const root = createRoot(el);

    const update = () => {
      const total = grid.getData().getLength();
      const selectedCount = grid.getSelectedRows().length;

      const checked = total > 0 && selectedCount === total;
      const indeterminate = selectedCount > 0 && selectedCount < total;

      root.render(
        <CheckBoxComponent
          checked={checked}
          indeterminate={indeterminate}
          onChange={(_: React.ChangeEvent<HTMLInputElement>, value: boolean) => {
            if (value) {
              grid.setSelectedRows(Array.from({ length: total }, (_, i) => i));
            } else {
              grid.setSelectedRows([]);
            }
          }}
        />,
      );
    };

    update();
    grid.onSelectedRowsChanged.subscribe(update);

    return el;
  };
}

function sanitizeHtml(input: string): string {
  const doc = new DOMParser().parseFromString(input || "", "text/html");
  return doc.body.textContent || "";
}

interface ExcelColumn<T extends GridRowBase> extends ColumnDefinition<T> {
  exportWithFormatter?: boolean;
}

/**
 * A slickgrid column as it comes back from `getAllColumnDefinitions()`, plus the two value getters
 * this file attaches to its own column definitions. Declared here so a column read straight off the
 * grid and one taken from `props.columnDefinitions` can be used interchangeably.
 */
type ColumnWithValueGetters<T extends GridRowBase> = Column<T> & {
  /** Legacy call sites pass a field-path string instead of a getter — hence the string arm. */
  _valueGetter?: ((row: T) => unknown) | string;
  _excelValueGetter?: (row: T) => unknown;
};

/**
 * Reads a column back off the grid at the type it was mounted with.
 *
 * `slickGrid.getColumns()` and `gridService.getAllColumnDefinitions()` hand back the very objects
 * this component gave SlickGrid from its `columnDefinitions` prop — but slickgrid-react declares
 * them as its own `Column<any>`, which drops the row type AND every member this file puts on a
 * column (`_valueGetter`, `_filterValueGetter`, `_imageGetter`, `formatterType`, `wrapText`, …).
 * `ColumnDefinition<T>` is not structurally reachable from `Column`: slickgrid declares
 * `field: Join<PathsToStringProps<T>, '.'>` (node_modules/@slickgrid-universal/common/dist/types/
 * interfaces/column.interface.d.ts:110) where this file declares `field: K extends keyof T`, and
 * `getColumns(): C[]` (core/slickGrid.d.ts:531) re-states that erased type rather than the type of
 * the array `setColumns` was given — so the round trip cannot be expressed as an assignment.
 * It is therefore stated ONCE here, next to the reason, instead of at each of the ~20 read sites.
 */
function readGridColumn<TRow extends GridRowBase>(
  column: Omit<Column, "formatter"> & { formatter?: unknown },
): ColumnDefinition<TRow> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- slickgrid-react round trip, see doc comment above: same object going back out, re-typed at the library boundary
  return column as unknown as ColumnDefinition<TRow>;
}

/**
 * SlickDataView declares `getItemById()` / `getItem()` as returning the item, but it returns
 * `undefined` for an id that is not in its index and for a row index past the end of its current
 * data. Reading through these keeps the call sites' guards meaningful: assigning the call straight
 * into a `T | undefined` const does not help, because control-flow analysis re-narrows a const to
 * its initialiser's type.
 */
function dataViewItemById<TRow extends GridRowBase>(
  dataView: SlickDataView<TRow>,
  id: string | number,
): TRow | undefined {
  return dataView.getItemById(id);
}

function dataViewItemAt<TRow extends GridRowBase>(dataView: SlickDataView<TRow>, rowIndex: number): TRow | undefined {
  return dataView.getItem(rowIndex);
}

/** `getHeaderColumn()` is declared to return the element, but SlickGrid returns `undefined` when the
 *  column is not currently rendered — the same widening-through-a-function trick as above. */
function headerColumnOf(slickGrid: SlickGridType, columnId: string | number): HTMLElement | undefined {
  return slickGrid.getHeaderColumn(columnId);
}

/** Array form of {@link readGridColumn}. Element identity is preserved, so callers that mutate a
 *  returned column (e.g. re-assigning `formatter` before `setColumns`) still mutate the real one. */
function readGridColumns<TRow extends GridRowBase>(columns: Column[]): ColumnDefinition<TRow>[] {
  return columns.map((column) => readGridColumn<TRow>(column));
}

/**
 * The same round trip in the other direction, for handing this file's columns back to
 * `slickGrid.setColumns()`. See {@link readGridColumn} for why it cannot be an assignment; on top of
 * that, a column on its way out may carry one of this file's formatters, which return `unknown`
 * where slickgrid's `Formatter` (node_modules/@slickgrid-universal/common/dist/types/interfaces/
 * column.interface.d.ts:126, `formatter?: Formatter<T>`) returns a narrower union.
 */
function asSlickColumns<TRow extends GridRowBase>(
  columns: Array<ColumnDefinition<TRow> | (Omit<Column, "formatter"> & { formatter?: unknown })>,
): Column<TRow>[] {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- slickgrid-react round trip, see readGridColumn: these are the columns slickgrid handed us, going straight back in
  return columns as unknown as Column<TRow>[];
}

// Excel-export boundary normalisation: blank data must land as an empty cell,
// never as the text "null"/"undefined". Value getters are written for the grid
// and can stringify missing fields (e.g. template literals), and raw fields can
// be null — neither should leak into the exported file. The return type is
// slickgrid's own cell-value type, which is what every caller has to satisfy.

function normalizeExcelValue(value: unknown): ReturnType<GetDataValueCallback> {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "null" || trimmed === "undefined") return "";
  }
  // Whatever the column's value getter produced. `GetDataValueCallback` is declared
  // `(...) => Date | string | number | ExcelColumnMetadata` (node_modules/@slickgrid-universal/
  // common/dist/types/interfaces/columnExcelExportOption.interface.d.ts:43), but the shipped
  // implementation only passes the result straight through as the cell's `itemData`
  // (excel-export/dist/esm/excelExport.service.js:473) and excel-builder stringifies whatever it
  // gets — so the declaration is narrower than what runs, and coercing here to satisfy it would
  // change the exported cell.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- vendored declaration narrower than the shipped implementation, see comment above
  return value as ReturnType<GetDataValueCallback>;
}

function getFormattedValue<T extends GridRowBase>(
  column: ExcelColumn<T>,
  item: T,
  rowIndex: number,
  grid: SlickGridType,
): unknown {
  // Priority 1: dedicated excel value getter (excelExportValueParseback)
  if (typeof column._excelValueGetter === "function") {
    return normalizeExcelValue(column._excelValueGetter(item));
  }

  // Priority 2: general value getter (used for sorting/filtering/excel)
  if (typeof column._valueGetter === "function") {
    return normalizeExcelValue(column._valueGetter(item));
  }
  // Support legacy string _valueGetter as a field path
  if (typeof column._valueGetter === "string") {
    return normalizeExcelValue(get(item, column._valueGetter as string));
  }

  // Priority 3: formatter (only when explicitly opted in via exportWithFormatter)
  if (column.exportWithFormatter && typeof column.formatter === "function") {
    const fieldValue = item[column.field as keyof T];
    const formatted = column.formatter(rowIndex, 0, fieldValue as T[keyof T], column, item, grid);
    if (formatted instanceof HTMLElement) {
      return normalizeExcelValue(sanitizeHtml(formatted.textContent || formatted.innerText || ""));
    }
    return normalizeExcelValue(typeof formatted === "string" ? sanitizeHtml(formatted) : formatted);
  }

  // Priority 4: raw field value
  return normalizeExcelValue(item[column.field as keyof T]);
}

/**
 * A group as `SlickDataView.getGroups()` actually returns it. slickgrid declares that as
 * `SlickGroup[]`, whose `title` is `string | null` and whose `rows` is `number[]` — neither matches
 * runtime here: this grid renders group titles with a custom formatter (an `HTMLElement`, see
 * `createGroupFormatter`) and `rows` carries the data items themselves.
 */
interface GroupEntry<T extends GridRowBase = GridRowBase> {
  title: HTMLElement | string | null;
  value: unknown;
  count: number;
  groups?: GroupEntry<T>[];
  /**
   * slickgrid declares `SlickGroup.rows` as `number[]`; the DataView actually fills it with the
   * data items themselves. The `number` arm is what slickgrid CLAIMS, kept so its own type stays
   * assignable to this one (no assertion needed at the `getGroups()` boundary); `flattenGroups`
   * checks for it rather than assuming it away.
   */
  rows?: Array<T | number>;
}

/**
 * `item` is `T | undefined` on purpose: it is whatever the DataView hands back — a host `dataset`
 * or a remote page this component never validates — so a hole in that array reaches the export.
 * The row/image loops below skip such an entry rather than formatting `undefined`.
 */
type FlattenedGroupRow<T extends GridRowBase> =
  | { __group: true; level: number; row: string[]; item?: never }
  | { __group: false; level: number; row?: never; item: T | undefined };

function flattenGroups<T extends GridRowBase>(
  groups: GroupEntry<T>[],
  result: FlattenedGroupRow<T>[] = [],
  level = 0,
  _columns: Column<T>[] = [],
): FlattenedGroupRow<T>[] {
  for (const g of groups) {
    const titleElement = g.title;
    const col = typeof titleElement === "string" ? titleElement : (titleElement?.childNodes[0]?.textContent ?? "");

    const groupLabel = `${col}: ${normalizeExcelValue(g.value)} (${g.count} rows)`;

    result.push({
      __group: true as const,
      level,
      row: [groupLabel],
    });

    const hasSubGroups = g.groups && g.groups.length > 0;

    if (hasSubGroups && g.groups) {
      flattenGroups(g.groups, result, level + 1, _columns);
    } else {
      for (const r of g.rows ?? []) {
        // See GroupEntry.rows: a row index here would mean slickgrid's declared shape, not the one
        // the DataView actually produces — there is no item to export in that case.
        if (typeof r === "number") continue;
        result.push({
          __group: false as const,
          level: level + 1,
          item: r,
        });
      }
    }
  }
  return result;
}

function getImageUrlFromItem<T extends GridRowBase>(colDef: ColumnDefinition<T>, dataContext: T): string {
  const imageIdOrUrl = colDef._imageGetter?.(dataContext);
  return imageIdOrUrl ? imageIdOrUrl : `${getImageUrl()}/Default.svg`;
}

export async function exportGroupedExcel<T extends GridRowBase>(
  gridRef: React.RefObject<SlickgridReactInstanceX<T> | null>,
): Promise<void> {
  if (!gridRef.current) return;

  const grid = gridRef.current;
  const dataView = grid.dataView;
  const columns = readGridColumns<T>(grid.slickGrid.getColumns()).filter(
    (col) => !col.excludeFromExport && !col.hidden,
  );
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Export");

  // slickgrid declares `getGroups(): SlickGroup[]`, which is not what this grid stores in a group
  // (see GroupEntry) — but GroupEntry is a WIDENING of SlickGroup, so the call assigns into it
  // with no assertion and no re-declaration of the method. It always yields an array: `getGroups()`
  // returns `this.groups` (node_modules/@slickgrid-universal/common/dist/esm/core/slickDataview.js
  // :723), which the constructor initialises to `[]` (:53) and every (re)grouping resets to `[]`
  // (:301, :1113) — so the old `?? []` had nothing to catch.
  const groups: GroupEntry<T>[] = dataView.getGroups();
  const flatRows: FlattenedGroupRow<T>[] =
    groups.length > 0
      ? flattenGroups<T>(groups, [], 0, asSlickColumns<T>(columns))
      : dataView.getItems().map((item) => ({ __group: false as const, level: 0, item }));

  const uniqueUrls = new Set<string>();
  const imageCols = columns.filter((c) => c.formatterType === "image");

  flatRows.forEach((entry) => {
    if (entry.__group) return;
    const item = entry.item;
    if (!item) return;
    imageCols.forEach((col) => {
      uniqueUrls.add(getImageUrlFromItem(col, item));
    });
  });

  const imageMap = new Map<string, number>();

  await Promise.all(
    Array.from(uniqueUrls).map(async (url) => {
      try {
        const resp = await fetch(url);
        const buffer = await resp.arrayBuffer();
        const ext = url.toLowerCase().endsWith(".png") ? "png" : "jpeg";
        const id = workbook.addImage({ buffer, extension: ext as "png" | "jpeg" });
        imageMap.set(url, id);
      } catch (e) {
        console.log("Failed to pre-fetch", url, e);
        Promise.reject(e);
      }
    }),
  );

  let currentRowIndex = 2;

  for (const entry of flatRows) {
    if (entry.__group) {
      const r = sheet.addRow(entry.row.map((v: string) => sanitizeCsvCell(v)));
      r.font = { bold: true };
      r.outlineLevel = entry.level;
    } else if (entry.item) {
      const item = entry.item;
      const rowValues = columns.map((col) =>
        col.formatterType === "image"
          ? ""
          : sanitizeCsvCell(getFormattedValue(col, item, currentRowIndex, grid.slickGrid)),
      );

      const row = sheet.addRow(rowValues);
      row.outlineLevel = entry.level;
      sheet.getRow(currentRowIndex).height = 65;

      imageCols.forEach((col) => {
        const colIdx = columns.indexOf(col);
        const url = getImageUrlFromItem(col, item);
        const imageId = imageMap.get(url);

        if (imageId !== undefined) {
          sheet.addImage(imageId, {
            tl: { col: colIdx, row: currentRowIndex - 1 },
            ext: { width: 70, height: 45 },
            editAs: "oneCell",
          });
        }
      });
    }
    currentRowIndex++;
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), "fast-export.xlsx");
}
const operatorMapper: Record<QueryOperator, string> = {
  startswith: "Starts With",
  endswith: "Ends With",
  empty: "Empty",
  equal: "Equal",
  contains: "Contains",
  doesnotstartwith: "Does Not Start With",
  doesnotendwith: "Does Not End With",
  doesnotcontain: "Does Not Contain",
  notequal: "Not Equal",
  notempty: "Not Empty",
  greaterthan: "Greater Than",
  greaterthanorequal: "Greater Than Or Equal",
  lessthan: "Less Than",
  lessthanorequal: "Less Than Or Equal",
  null: "Null",
  notnull: "Not Null",
  between: "Between",
  like: "Like",
};

/**
 * {@link operatorMapper} read as the LOOKUP it really is. The key is a stored condition's `option`,
 * declared `QueryOperator` but never validated: `withResolvedFilterOptions` only fills in a MISSING
 * option and passes any non-empty stored string straight through, so a saved view holding a legacy
 * spelling ("Equal", "eq") reaches the lookup and misses. Callers must handle the miss.
 */
const operatorLabels: Record<string, string | undefined> = operatorMapper;

const BASE_PINNED: readonly string[] = ["_checkbox_selector", "_move"] as const;

type DebouncedFunction<TArgs extends unknown[]> = ((...args: TArgs) => void) & { cancel: () => void };

function makeDebounce<TArgs extends unknown[]>(fn: (...args: TArgs) => void, delay: number): DebouncedFunction<TArgs> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: TArgs) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };

  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
  };

  return debounced;
}

interface ObjectWithName {
  name?: string;
  label?: string;
  toString?: () => string;
}

const formatObjectValue = (value: ObjectWithName): string => {
  if (value.name) return String(value.name);
  if (value.label) return String(value.label);

  if (value.toString && value.toString !== Object.prototype.toString) {
    return value.toString();
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "[Object]";
  }
};

const formatGroupValue = (value: unknown): string => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") return formatObjectValue(value as ObjectWithName);
  return String(value);
};

const collectGroupValues = <T extends GridRowBase>(rows: T[], col: ColumnDefinition<T> | null): Set<unknown> => {
  const values = new Set<unknown>();
  for (const row of rows) {
    if (col?._valueGetter) {
      values.add(col._valueGetter(row));
    } else if (col?.field) {
      values.add(row[col.field as keyof T]);
    }
  }
  return values;
};

const getGroupValueText = (values: Set<unknown>): string => {
  if (values.size === 1) {
    const v = Array.from(values)[0];
    return formatGroupValue(v);
  }
  if (values.size > 1) return "varies";
  return "—";
};

interface GroupData<T extends GridRowBase> {
  rows: T[];
  count: number;
  value: unknown;
}

interface GroupElements {
  label: HTMLDivElement;
  value: HTMLSpanElement;
  count: HTMLSpanElement;
}

const createGroupElements = <T extends GridRowBase>(
  col: ColumnDefinition<T> | null,
  g: GroupData<T>,
): GroupElements => {
  const label = document.createElement("div");
  label.className = "group-title-label";
  label.textContent = col?.name || "";

  const value = document.createElement("span");
  value.className = "group-title-value";
  const values = collectGroupValues(g.rows, col);
  value.textContent = getGroupValueText(values);

  const count = document.createElement("span");
  count.className = "slick-group-count";
  count.textContent = `${g.count} ${g.count === 1 ? "row" : "rows"}`;

  return { label, value, count };
};

const createGroupFormatter =
  <T extends GridRowBase>(col: ColumnDefinition<T> | null) =>
  (g: GroupData<T>): HTMLDivElement => {
    const { label, value, count } = createGroupElements(col, g);

    const bottomRow = document.createElement("div");
    bottomRow.className = "group-value-row d-flex align-items-center gap-2";
    bottomRow.appendChild(value);
    bottomRow.appendChild(count);

    const container = document.createElement("div");
    container.className = "grouping-row d-flex flex-column";
    container.appendChild(label);
    container.appendChild(bottomRow);

    return container;
  };

const createGroupComparer =
  <T extends GridRowBase>(col: ColumnDefinition<T> | null, direction: string, preserveOrder: boolean = false) =>
  (a: GroupData<T>, b: GroupData<T>): number => {
    // When preserveOrder is set, groups are ordered by the upstream row order
    // (e.g. backend already sorted by createdDate desc). A no-op comparer keeps
    // them in insertion order under JS's stable sort.
    if (preserveOrder) return 0;

    const aRow = a.rows[0];
    const bRow = b.rows[0];
    const aVal = col?._valueGetter && aRow ? col._valueGetter(aRow) : a.value;
    const bVal = col?._valueGetter && bRow ? col._valueGetter(bRow) : b.value;

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return -1;
    if (bVal == null) return 1;

    const dir = (direction || "asc").toLowerCase() === "asc" ? 1 : -1;
    if (aVal > bVal) return dir;
    if (aVal < bVal) return -dir;
    return 0;
  };

export const applyDateFilter = (
  fieldValue: unknown,
  filterOption: QueryOperator,
  filterValue: DateFilterValue,
): boolean => {
  const format = "MMM D,YYYY [at] hh:mm:ss A";
  const fieldStr = String(fieldValue ?? "");
  const itemDate = Number.isNaN(Number(fieldValue)) ? dayjs(fieldStr, format) : dayjs(Number(fieldValue));

  // Check if the field value is null/undefined/empty BEFORE comparing
  const isNullOrEmpty = !fieldValue || fieldStr.trim() === "";

  // Extract the date value, handling both Dayjs objects and objects with a `date` property.
  // A bare Dayjs answers `"date" in x` as well — `date` is a Dayjs prototype METHOD — so the
  // raw-Dayjs case has to be settled first; unwrapping it as if it were the `{ date }` wrapper
  // yields that method, and every comparison below then runs against an Invalid Date.
  // The `"date" in` test is still what decides whether to unwrap: only the filter UI's `{ date }`
  // wrapper has that slot. A bare `Date`, or the `{ startDate, endDate }` bag a range filter
  // saves, does not — and reading `.date` off one of those yields `undefined`, which dayjs reads
  // as NOW, silently comparing every row against today instead of never matching.
  const filterDate = dayjs.isDayjs(filterValue)
    ? filterValue
    : typeof filterValue === "object" && filterValue !== null && !(filterValue instanceof Date) && "date" in filterValue
      ? dayjs(toDayjsInput(filterValue.date))
      : dayjs(toDayjsInput(filterValue));

  const filters: Record<string, () => boolean> = {
    // For comparison operators, exclude null/empty values (they should not match any date comparison)
    equal: () => !isNullOrEmpty && itemDate.isValid() && itemDate.isSame(filterDate, "day"),
    greaterthan: () => !isNullOrEmpty && itemDate.isValid() && itemDate.isAfter(filterDate, "day"),
    greaterthanorequal: () =>
      !isNullOrEmpty &&
      itemDate.isValid() &&
      (itemDate.isSame(filterDate, "day") || itemDate.isAfter(filterDate, "day")),
    lessthan: () => !isNullOrEmpty && itemDate.isValid() && itemDate.isBefore(filterDate, "day"),
    lessthanorequal: () =>
      !isNullOrEmpty &&
      itemDate.isValid() &&
      (itemDate.isSame(filterDate, "day") || itemDate.isBefore(filterDate, "day")),
    notequal: () => !isNullOrEmpty && itemDate.isValid() && !itemDate.isSame(filterDate, "day"),
    null: () => isNullOrEmpty,
    notnull: () => !isNullOrEmpty,
  };

  return filters[filterOption]?.() ?? false;
};

export const applyTextFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
  const fieldValueStr = String(fieldValue ?? "").toLowerCase();
  const filterValueLower = String(filterValue ?? "").toLowerCase();

  // Check if original value is actually null (not just empty string)
  const isNull = fieldValue === null || fieldValue === undefined;

  const filters: Record<string, () => boolean> = {
    startswith: () => fieldValueStr.startsWith(filterValueLower),
    endswith: () => fieldValueStr.endsWith(filterValueLower),
    empty: () => fieldValueStr === "",
    equal: () => fieldValueStr === filterValueLower,
    contains: () => fieldValueStr.includes(filterValueLower),
    like: () => fieldValueStr.includes(filterValueLower),
    doesnotstartwith: () => !fieldValueStr.startsWith(filterValueLower),
    doesnotendwith: () => !fieldValueStr.endsWith(filterValueLower),
    doesnotcontain: () => !fieldValueStr.includes(filterValueLower),
    notequal: () => fieldValueStr !== filterValueLower,
    notempty: () => fieldValueStr !== "",
    null: () => isNull,
    notnull: () => !isNull,
  };

  return filters[filterOption]?.() ?? false;
};

export const applyNumberFilter = (fieldValue: unknown, filterOption: QueryOperator, filterValue: unknown): boolean => {
  const fieldValueAsNumber = Number(fieldValue);
  const filterValueAsNumber = Number(filterValue);

  // Check if the original value is actually null/undefined/empty (not just converts to 0)
  const isNullOrEmpty =
    fieldValue === null ||
    fieldValue === undefined ||
    fieldValue === "" ||
    (typeof fieldValue === "string" && fieldValue.trim() === "") ||
    Number.isNaN(fieldValueAsNumber);

  const filters: Record<string, () => boolean> = {
    // For comparison operators, exclude null/empty values (they should not match numeric comparisons)
    equal: () => !isNullOrEmpty && fieldValueAsNumber === filterValueAsNumber,
    greaterthan: () => !isNullOrEmpty && fieldValueAsNumber > filterValueAsNumber,
    greaterthanorequal: () => !isNullOrEmpty && fieldValueAsNumber >= filterValueAsNumber,
    lessthan: () => !isNullOrEmpty && fieldValueAsNumber < filterValueAsNumber,
    lessthanorequal: () => !isNullOrEmpty && fieldValueAsNumber <= filterValueAsNumber,
    notequal: () => !isNullOrEmpty && fieldValueAsNumber !== filterValueAsNumber,
    null: () => isNullOrEmpty,
    notnull: () => !isNullOrEmpty,
    startswith: () => true,
  };

  return filters[filterOption]?.() ?? false;
};

export const applyMultiSelectFilter = (fieldValue: unknown, filterValue: unknown): boolean => {
  const selectedValues: unknown[] = Array.isArray(filterValue) ? filterValue : [filterValue];

  if (Array.isArray(fieldValue)) {
    // `Array.isArray()` narrows `unknown` to `any[]`; re-stating the element type as `unknown`
    // keeps that `any` from leaking into the comparison below and makes the `"name" in item`
    // check a real narrowing rather than a no-op on an already-`any` element.
    const items: unknown[] = fieldValue;
    const fieldValues = items.map((item) =>
      typeof item === "object" && item !== null && "name" in item ? item.name : String(item),
    );
    return selectedValues.some((selectedValue) =>
      fieldValues.some((fieldVal) => String(fieldVal).toLowerCase() === String(selectedValue).toLowerCase()),
    );
  }

  return selectedValues.some(
    (selectedValue) => String(fieldValue).toLowerCase() === String(selectedValue).toLowerCase(),
  );
};

/** Convert file size with unit to bytes */
export const convertFileSizeToBytes = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (!value || typeof value !== "object") return 0;

  const sizeValue = value as { size?: string | number; unit?: string };
  const size = Number(sizeValue.size) || 0;
  const unit = (sizeValue.unit || "B").toUpperCase();

  const multipliers: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 * 1024,
    GB: 1024 * 1024 * 1024,
    TB: 1024 * 1024 * 1024 * 1024,
  };

  return size * (multipliers[unit] || 1);
};

export const applyFileSizeFilter = (
  fieldValue: unknown,
  filterOption: QueryOperator,
  filterValue: unknown,
): boolean => {
  const fieldValueAsNumber = Number(fieldValue);
  const filterValueAsNumber = convertFileSizeToBytes(filterValue);

  // Check if the original value is null/undefined (NaN when converted)
  const isNullOrEmpty = fieldValue === null || fieldValue === undefined || Number.isNaN(fieldValueAsNumber);

  const filters: Record<string, () => boolean> = {
    // For comparison operators, exclude null/empty values
    equal: () => !isNullOrEmpty && fieldValueAsNumber === filterValueAsNumber,
    greaterthan: () => !isNullOrEmpty && fieldValueAsNumber > filterValueAsNumber,
    greaterthanorequal: () => !isNullOrEmpty && fieldValueAsNumber >= filterValueAsNumber,
    lessthan: () => !isNullOrEmpty && fieldValueAsNumber < filterValueAsNumber,
    lessthanorequal: () => !isNullOrEmpty && fieldValueAsNumber <= filterValueAsNumber,
    notequal: () => !isNullOrEmpty && fieldValueAsNumber !== filterValueAsNumber,
    // null operator: true for null/undefined/NaN values (0 is a valid file size)
    null: () => isNullOrEmpty,
    notnull: () => !isNullOrEmpty,
  };

  return filters[filterOption]?.() ?? false;
};

const withBasePinned = (arr: string[]): string[] => {
  const rest = arr.filter((id) => !BASE_PINNED.includes(id));
  return [...BASE_PINNED, ...Array.from(new Set(rest))];
};

const mapTreeGridFlags = <T extends GridRowBase>(
  arr: T[] | undefined,
  hasChildMapping: string,
  initiallyExpanded?: boolean,
): T[] => {
  if (!arr) return [];
  const defaultCollapsed = !initiallyExpanded;
  return arr.map((data) => {
    const hasChildren = Boolean((data as Record<string, unknown>)[hasChildMapping]);
    const existingCollapsed = (data as Record<string, unknown>).__collapsed;
    return {
      ...data,
      __hasChildren: hasChildren,
      __collapsed: hasChildren ? (existingCollapsed !== undefined ? existingCollapsed : defaultCollapsed) : undefined,
    };
  });
};

const ensureIds = <T extends GridRowBase>(arr: T[] | undefined, idMapping?: string): T[] => {
  // The `Array.isArray` half guards callers that hand over something that is not an array at all;
  // this function promises `T[]`, so that case yields an empty one.
  if (!arr || !Array.isArray(arr)) return [];

  return arr.map((data, index) => {
    const mappedId = idMapping ? (data as Record<string, unknown>)[idMapping] : undefined;
    // TRUTHINESS, not nullishness, at every step — `isRowId` is the same rule `readRowId` applies
    // when it looks an id back up. A row that arrives with `id: 0` or `id: ""` has no usable id:
    // keeping it would put a row in the DataView that no id-keyed lookup here (idMap/neededIds in
    // includeMissingParentsForTree, persistedSelectedItemsRef, dataViewItemById) can ever reach,
    // so it falls through to `_id` and then to a generated one, exactly as it always has.
    const id: RowId = isRowId(mappedId)
      ? mappedId
      : isRowId(data.id)
        ? data.id
        : isRowId(data._id)
          ? data._id
          : `auto_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 11)}`;
    return { ...data, id };
  });
};

const getRowElement = (
  rowIndex: number,
  evenRowsArray: NodeListOf<Element>,
  oddRowsArray: NodeListOf<Element>,
): HTMLElement | null => {
  // Every caller reads `.style` / `getBoundingClientRect()` off the result, so the row element is
  // checked to really be an HTMLElement here instead of being asserted into one at each call site.
  const element = rowIndex % 2 === 0 ? evenRowsArray[rowIndex / 2] : oddRowsArray[Math.floor(rowIndex / 2)];
  return element instanceof HTMLElement ? element : null;
};

const calculateNewParentId = <T extends GridRowBase>(
  currentTop: number,
  droppedRowElement: HTMLElement | null,
  droppedRowData: T | null,
): ParentId => {
  if (!droppedRowElement) return null;

  const { top: rowTop, height: rowHeight } = droppedRowElement.getBoundingClientRect();
  const midpointY = rowTop + rowHeight / 2;
  const tolerance = 10;

  if (currentTop >= midpointY - tolerance && currentTop <= midpointY + tolerance) {
    const parentId = droppedRowData?._id;
    if (parentId == null) return null;
    // `_id` is a RowId, which also admits an ObjectId-like object; `rowDrop` is declared against
    // `ParentId`, so an object id is flattened to its string form here rather than asserted away.
    return typeof parentId === "number" ? parentId : String(parentId);
  }

  return null;
};

const updateParentChildRelationships = <T extends GridRowBase>(
  treeData: T[],
  movedRowData: T,
  previousParentId: RowId | null | undefined,
  newParentId: RowId | null,
  droppedRowData: T | null,
): void => {
  // These four fields are declared on GridRowBase, so the rows are written through that base view
  // rather than asserted into an ad-hoc intersection per field.
  const moved: GridRowBase = movedRowData;
  moved.parentId = newParentId;
  moved.treeLevel = newParentId ? (droppedRowData?.treeLevel ?? 0) + 1 : 0;

  if (newParentId && droppedRowData) {
    const dropped: GridRowBase = droppedRowData;
    dropped.__hasChildren = true;
    dropped.hasChild = true;
  }

  if (previousParentId) {
    const oldParent = treeData.find((item) => item._id === previousParentId);
    if (oldParent) {
      const hasChildren = treeData.some((item) => item.parentId === oldParent._id);
      if (!hasChildren) {
        const parent: GridRowBase = oldParent;
        parent.__hasChildren = false;
        parent.hasChild = false;
      }
    }
  }
};

const shouldSkipDrag = (dataRow: number | null, insertBefore: number | null, droppedRowIndex: number): boolean => {
  if (dataRow == null) return true;
  if (insertBefore != null && dataRow === insertBefore - 1) return true;
  if (droppedRowIndex === -1) return true;
  return false;
};

interface HeaderCheckboxProps {
  grid: SlickGridType;
}

const HeaderCheckbox: React.FC<HeaderCheckboxProps> = ({ grid }) => {
  const [status, setStatus] = useState<{ checked: boolean; indeterminate: boolean }>({
    checked: false,
    indeterminate: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const updateState = () => {
      // Guard against updates when SlickGrid has already detached our DOM container
      // during header re-renders (e.g. infinite scroll). Without this check, React
      // tries to commit to an orphaned DOM tree and throws a removeChild error.
      if (!isMountedRef.current || !containerRef.current?.isConnected) return;

      const dataLength = grid.getDataLength();
      const selectedRows = grid.getSelectedRows();
      const selectedCount = selectedRows.length;

      const checked = dataLength > 0 && selectedCount === dataLength;
      const indeterminate = selectedCount > 0 && selectedCount < dataLength;

      setStatus({ checked, indeterminate });
    };

    updateState();

    grid.onSelectedRowsChanged.subscribe(updateState);
    (grid.getData() as SlickDataView<GridRowBase>).onRowCountChanged.subscribe(updateState);

    return () => {
      grid.onSelectedRowsChanged.unsubscribe(updateState);
      (grid.getData() as SlickDataView<GridRowBase>).onRowCountChanged.unsubscribe(updateState);
    };
  }, [grid]);

  const toggleAll = (e: React.SyntheticEvent, checked: boolean) => {
    e.stopPropagation();
    e.preventDefault();

    if (checked) {
      const allRowIndices = Array.from({ length: grid.getDataLength() }, (_, i) => i);
      grid.setSelectedRows(allRowIndices);
    } else {
      grid.setSelectedRows([]);
    }
  };

  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center w-full h-full cursor-pointer"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <CheckBoxComponent checked={status.checked} indeterminate={status.indeterminate} onChange={toggleAll} />
    </div>
  );
};

interface ReorderGuidePositions {
  currentTop: number;
  midpointY: number;
}

interface ReorderGuideRowData<T extends GridRowBase> {
  droppedRowData: T | null;
  movedRowData: T;
}

interface ReorderGuideConfig {
  tolerance: number;
  insertBefore: number | null;
}

const shouldHideReorderGuide = <T extends GridRowBase>(
  positions: ReorderGuidePositions,
  rowData: ReorderGuideRowData<T>,
  config: ReorderGuideConfig,
): boolean => {
  const { currentTop, midpointY } = positions;
  const { droppedRowData, movedRowData } = rowData;
  const { tolerance, insertBefore } = config;

  if (!droppedRowData) return true;

  const isDifferentParent = droppedRowData.parentId !== movedRowData.parentId;
  const isNotInMidpoint = !(currentTop >= midpointY - tolerance && currentTop <= midpointY + tolerance);
  const isNotAtStart = insertBefore !== 0;

  return isDifferentParent && isNotInMidpoint && isNotAtStart;
};

const isInMidpointRange = (currentTop: number, midpointY: number, tolerance: number): boolean => {
  return currentTop >= midpointY - tolerance && currentTop <= midpointY + tolerance;
};

function createMainRow<T extends GridRowBase>(item: T, idxCompressed: number | undefined, requiredRows: number): T {
  const rowId = item.id ?? `row_${idxCompressed ?? crypto.randomUUID()}`;
  return {
    ...item,
    id: rowId,
    disableBorder: requiredRows > 1,
  };
}

function createDummyRows<T extends GridRowBase>(
  item: T,
  mainRowId: RowId,
  requiredRows: number,
  gridRef: React.RefObject<SlickgridReactInstanceX<T> | null>,
): T[] {
  const dummies: T[] = [];

  let isParentCollapsed = false;
  if (item.parentId !== null && item.parentId !== undefined) {
    const parentDataView: SlickDataView<GridRowBase> | undefined = gridRef.current?.dataView;
    const parentItem = parentDataView && dataViewItemById(parentDataView, String(item.parentId));
    if (parentItem) isParentCollapsed = !!parentItem.__collapsed;
  }

  for (let iIdx = 1; iIdx < requiredRows; iIdx++) {
    // A dummy is a rowspan FILLER, not a row of `T`: it carries GridRowBase bookkeeping fields
    // only. Nothing ever reads it as a `T` — every formatter returns early on `__isDummy`
    // (see getFormatter), calculateRowSpans/handleWrapUpdate strip them before recomputing, and
    // consumers filter them out of a selection (`record is Row => !record.__isDummy`).
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- filler row, see comment above: it is only ever recognised by `__isDummy`, never read as a `T`
    dummies.push({
      id: `${mainRowId}_dummy_${iIdx}`,
      __hostRowId: mainRowId,
      __isDummy: true,
      __parentId: String(item.parentId),
      parentId: item.parentId,
      treeLevel: item.treeLevel,
      indentLevel: item.indentLevel,
      hasChild: false,
      __hasChildren: false,
      __collapsed: isParentCollapsed,
      disableBorder: iIdx < requiredRows - 1,
    } as T);
  }
  return dummies;
}

function buildMetadataForRow<T extends GridRowBase>(
  columnDefs: ColumnDefinition<T>[] | Column<T>[],
  requiredRows: number,
): RowMetadata {
  const columnsMeta: Record<string | number, { rowspan: number }> = {};
  for (const [colIdx] of columnDefs.entries()) {
    columnsMeta[colIdx] = { rowspan: requiredRows };
  }
  return { columns: columnsMeta };
}

const removeLastIndex = (field: string): string => {
  return field.replace(/_\d+$/, "") || field;
};

// `ForwardRefRenderFunction<T, Omit<P, "ref">>`, and `SlickGridProps` carries a `[key: string]:
// unknown` index signature — so `Omit` widens `keyof SlickGridProps` to `string` and the resulting
// props type loses every named required member, `columnDefinitions` included. The render function
// then fails to match. Removing the index signature is the real fix; until then this cast is what
// lets the call through. The generic it erases is re-stated on the result below.
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-type-assertion -- forwardRef's Omit widens keyof via SlickGridProps' index signature, dropping required members; there is no narrower type that lets this call through
const SlickGridInner = (forwardRef as any)(function SlickGridInner<T extends GridRowBase = GridRowBase>(
  props: SlickGridProps<T>,
  ref: React.ForwardedRef<SlickgridReactInstanceX<T>>,
) {
  const { t } = useTranslation();
  const { showPageCount = true, enableSelectAllCheckbox = true } = props;
  const generatedId = useId();
  const id = props.id || generatedId;
  const standByRef = useRef<SlickgridReactInstanceX<T> | null>(null);
  // A ForwardedRef is one of three things: an object ref, a CALLBACK ref, or null when the consumer
  // passed none. Only an object ref can back the imperative handle this component assigns to
  // (`gridRef.current = reactGrid` in reactGridReady), so anything else falls back to the internal
  // stand-by ref. Asserting the object arm used to swallow the other two: a callback ref became
  // `gridRef` and every `gridRef.current` read on it silently missed.
  const gridRef = ref !== null && typeof ref === "object" ? ref : standByRef;

  // `SlickgridReactInstance` declares `dataView` as `SlickDataView<any>`; this grid only ever binds
  // rows of `T` to it, so reads go through here and the `any` stops at this one line.
  const dataViewOf = (instance: SlickgridReactInstanceX<T>): SlickDataView<T> => instance.dataView;

  /** SlickDataView indexes its rows by the RAW id value, so an id that is already a string or a
   *  number has to go through unchanged; only an ObjectId-like id is looked up by its string form. */
  const toDataViewId = (id: RowId): string | number => (typeof id === "object" ? String(id) : id);

  // Reads a row's id under the caller-configured `idMapping` key (default "id"). The key is only
  // known at runtime, so the value is GUARDED (isRowId keeps the long-standing truthiness rule:
  // an empty/zero id counts as "no id") instead of being asserted into a RowId at each call site.
  const readRowId = (row: GridRowBase): RowId | undefined => {
    const value = (row as Record<string, unknown>)[props.idMapping || "id"];
    return isRowId(value) ? value : undefined;
  };

  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [filterConditions, setFilterConditions] = useState<Record<string, FilterCondition>>({});
  // null = saved-view resolution pending (data fetch must wait for it to avoid a double load),
  // false = resolved with no saved view, LayoutData = resolved view to apply.
  // Views only resolve via onViewsLoaded from ViewsNewFlow, which mounts with the toolbar —
  // so start as pending only when that mount is guaranteed, else the grid would never load.
  const viewsWillResolve = Boolean(props.enableViews) && !props.disableToolbar;
  const [viewsLoaded, setViewsLoaded] = useState<LayoutData | false | null>(viewsWillResolve ? null : false);
  const [loaderOnPageChange, setLoaderOnPageChange] = useState<boolean>(!!props.showLoader);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItemCount, setTotalItemCount] = useState<number>(1);
  const [allPrerequisiteLoaded, setAllPrerequisiteLoaded] = useState<boolean>(false);
  const [cssClasses, setCssClasses] = useState<string>("");
  const [loaderForInfiniteScroll, setLoaderForInfiniteScroll] = useState<boolean>(false);
  const [currentLoadedCount, setCurrentLoadedCount] = useState<number>(0);
  const [appliedFilterConditions, setAppliedFilterConditions] = useState<AppliedFilterCondition[]>([]);
  const [groupConditions, setGroupConditions] = useState<GroupCondition[]>([]);
  const [gridMounted, setGridMounted] = useState<boolean>(false);
  const [tempCondition, setTempCondition] = useState<AppliedFilterCondition | null>(null);
  const [preventUpdate, setPreventUpdate] = useState<boolean>(false);
  const [triggerUpdateCurrentView, setTriggerUpdateCurrentView] = useState<Record<string, unknown> | false>(false);
  const [dataset, setDataset] = useState<T[]>([]);
  const safePropsDataset = useMemo(
    () => ensureIds(props.dataset ?? [], props.idMapping),
    [props.dataset, props.idMapping],
  );
  const [currentColumnId, setCurrentColumnId] = useState<string>("");
  const [filterPredicates, setFilterPredicates] = useState<Record<string, unknown> | string>("");
  const [anchorEl, setAnchorEl] = useState<{ top: number; left: number } | null>(null);
  const [filterType, setFilterType] = useState<FilterType | "">("");
  const [excelExportExtension, setExcelExportExtension] = useState<ExcelExportService | null>(null);
  const [sortConditions, setSortConditions] = useState<SortCondition[]>([]);
  const [insertBefore, setInsertBefore] = useState<number | null>(null);
  const [dataRow, setDataRow] = useState<number | null>(null);
  const [top, setTop] = useState<number | null>(null);
  const [pinnedColumns, setPinnedColumns] = useState<string[]>([]);
  const [showOptionDropdown, setShowOptionDropdown] = useState<boolean>(false);
  const [modalState, setModalState] = useState<string>("");
  const [modalAnchor, setModalAnchor] = useState<HTMLElement | VirtualAnchorElement | null>(null);
  const [selectedItems, setSelectedItems] = useState<number>(0);
  const [toolbarDisabledOverrides, setToolbarDisabledOverrides] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const searchTermRef = useRef<string>("");
  searchTermRef.current = searchTerm;

  const appliedFiltersRef = useRef<string[]>([]);
  appliedFiltersRef.current = appliedFilters;

  const isTreeFilterOrSearchActive = (): boolean =>
    appliedFiltersRef.current.length > 0 || searchTermRef.current.trim().length > 0;
  const persistedSelectedItemsRef = useRef<Map<RowId, T>>(new Map());
  const restoreSelectionAfterSearchRef = useRef<boolean>(false);
  const excelExportServiceRef = useRef<boolean>(false);
  const isInitialLoad = useRef<{ filter: boolean; sort: boolean; group: boolean }>({
    filter: true,
    sort: true,
    group: true,
  });

  // SlickGrid1 shows the search bar by default. It is suppressed for tree grids
  // and while inline editing is active (row filtering interacts poorly with
  // both), unless a caller explicitly sets the `enableSearchBar` prop.
  const enableSearchBar = props.enableSearchBar ?? (!props.treeGrid && !props.isEditMode);

  const isEditModeRef = useRef<boolean>(false);
  const editingRowIndexRef = useRef<number | null>(null);
  const nextPageTokenRef = useRef<string | null>(null);
  const isLastRef = useRef<boolean>(false);
  const isLoadingMoreRef = useRef<boolean>(false);
  const selectAllAfterLoadRef = useRef<boolean>(false);
  const totalItemCountRef = useRef<number>(totalItemCount);
  const preventUpdateCurrentView = useRef<boolean>(false);
  const topOfDragRowRef = useRef<number | null>(null);
  const stateRef = useRef<GridStateSnapshot<T> | null>(null);
  const specialColumnRef = useRef<Column[]>([]);
  const filteredDataRef = useRef<T[]>([]);
  const [queryConditions, setQueryConditions] = useState<QueryShape | null>(null);

  const slickEventHandler = new SlickEventHandler();
  const firstCallRef = useRef<boolean>(true);

  // --- Stable refs for callback props (avoids stale closures in SlickGrid event handlers) ---
  const onSelectedRowIdsChangedRef = useRef(props.onSelectedRowIdsChanged);
  onSelectedRowIdsChangedRef.current = props.onSelectedRowIdsChanged;
  const setGridReadyRef = useRef(props.setGridReady);
  setGridReadyRef.current = props.setGridReady;
  const toolbarClickHandlerRef = useRef(props.toolbarClickHandler);
  toolbarClickHandlerRef.current = props.toolbarClickHandler;
  const onReactGridCreatedRef = useRef(props.onReactGridCreated);
  onReactGridCreatedRef.current = props.onReactGridCreated;
  const onColumnsReorderedRef = useRef(props.onColumnsReordered);
  onColumnsReorderedRef.current = props.onColumnsReordered;
  const onClickRef = useRef(props.onClick);
  onClickRef.current = props.onClick;
  const onDblClickRef = useRef(props.onDblClick);
  onDblClickRef.current = props.onDblClick;
  const onFilterChangeRef = useRef(props.onFilterChange);
  onFilterChangeRef.current = props.onFilterChange;
  const onGroupChangedRef = useRef(props.onGroupChanged);
  onGroupChangedRef.current = props.onGroupChanged;
  const onExcelExportCompleteRef = useRef(props.onExcelExportComplete);
  onExcelExportCompleteRef.current = props.onExcelExportComplete;
  const handleUpdateCurrentViewPropRef = useRef(props.handleUpdateCurrentView);
  handleUpdateCurrentViewPropRef.current = props.handleUpdateCurrentView;
  const callbackForVisibleColumnsUpdateRef = useRef(props.callbackForVisibleColumnsUpdate);
  callbackForVisibleColumnsUpdateRef.current = props.callbackForVisibleColumnsUpdate;
  const rowDropRef = useRef(props.rowDrop);
  rowDropRef.current = props.rowDrop;
  const handleRowResizeOptionClickRef = useRef(props.handleRowResizeOptionClick);
  handleRowResizeOptionClickRef.current = props.handleRowResizeOptionClick;
  const onClickLayoutRef = useRef(props.onClickLayout);
  onClickLayoutRef.current = props.onClickLayout;
  const onSaveColumnsRef = useRef(props.onSaveColumns);
  onSaveColumnsRef.current = props.onSaveColumns;
  const viewsLoadedRef = useRef(viewsLoaded);
  viewsLoadedRef.current = viewsLoaded;

  // Stable wrapper that always delegates to the latest callback via ref
  const stableToolbarClickHandler = useCallback((cmd: string) => {
    toolbarClickHandlerRef.current?.(cmd);
  }, []);

  const searchCallBackRef = useRef(props.searchCallBack);
  searchCallBackRef.current = props.searchCallBack;

  const handleSearchChange = useCallback((term: string) => {
    if (searchCallBackRef.current) {
      searchCallBackRef.current(term);
      return;
    }
    restoreSelectionAfterSearchRef.current = persistedSelectedItemsRef.current.size > 0;
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const [totalPages, setTotalPages] = useState<number>(
    Math.ceil(totalItemCount / pageSize) === 0 ? 1 : Math.ceil(totalItemCount / pageSize),
  );

  const getStateSnapshot = (): GridStateSnapshot<T> => ({
    pageSize,
    currentPage,
    query: props.query,
    originalDataset: props.dataset,
    sortConditions,
    appliedFilters,
    dataset,
    filterConditions,
    filterPredicates,
    currentColumnId,
    totalItemCount,
    groupConditions,
    pinnedColumns,
    loaderOnPageChange,
    loaderForInfiniteScroll,
    allPrerequisiteLoaded,
    enableWrapText: props.enableWrapText,
    setGroupConditions,
    setSortConditions,
    setFilterConditions,
    setAppliedFilters,
    disableMenuOptions: props.disableMenuOptions,
    isEditMode: props.isEditMode,
    infiniteScroll: props.infiniteScroll,
    allowPagination: props.allowPagination,
  });

  const handleUpdateCurrentView = (data: Record<string, unknown>): void => {
    if (!preventUpdateCurrentView.current) {
      setTriggerUpdateCurrentView((prev) => ({
        ...(typeof prev === "object" ? prev : {}),
        ...data,
      }));
    }
  };

  // Snapshot each column's current width keyed by column id (CUBE-29114). Columns without a
  // numeric width (auto-sized, not yet measured) are skipped so we never persist a bogus 0.
  const getColumnWidthsSnapshot = (): Record<string, number> => {
    const cols = gridRef.current?.slickGrid.getColumns() ?? [];
    const widths: Record<string, number> = {};
    for (const col of cols) {
      if (typeof col.width === "number") widths[String(col.id)] = col.width;
    }
    return widths;
  };

  const createNewLayoutData = (): LayoutData => ({
    columns: gridRef.current?.slickGrid.getColumns().map((data) => data.id) ?? [],
    columnWidths: getColumnWidthsSnapshot(),
    groupConditions,
    pinnedColumns,
    enableFiltering: props.enableFilteringOption,
    enableGrouping: props.enableGroupingOption,
    enableSorting: props.enableSortingOption,
    sortConditions,
    filterConditions,
    enableColumnChooser: props.enableColumnChooser,
    queryConditions,
    rowHeight: gridRef.current?.slickGrid.getOptions().rowHeight,
    enableWrapText: !!props.enableWrapText,
  });

  // Persist row-height / wrap into the current view when the user picks a size in RowResizeModal.
  // "wrap" only toggles wrapping (keeps the grid's line-unit height); explicit sizes store px.
  // Then forward to any consumer-provided handler so existing callers behave unchanged.
  const onRowResizeOptionClick = (size: string, height: number): void => {
    if (props.enableViews && !preventUpdateCurrentView.current && viewsLoaded) {
      handleUpdateCurrentView(
        size === "wrap" ? { enableWrapText: true } : { enableWrapText: false, rowHeight: height },
      );
    }
    handleRowResizeOptionClickRef.current?.(size, height);
  };

  useEffect(() => {
    if (!props.triggerUpdateCurrentView) return;
    const ext = props.triggerUpdateCurrentView as Record<string, unknown>;
    handleUpdateCurrentView(ext);
    if ("queryConditions" in ext) {
      setQueryConditions(isQueryShape(ext.queryConditions) ? ext.queryConditions : null);
    }
  }, [props.triggerUpdateCurrentView]);

  interface EllipseFormatterArgs {
    value: unknown;
    column?: ColumnDefinition<T>;
  }

  const ellipseFormatterWithTooltip = (args: EllipseFormatterArgs): HTMLDivElement => {
    const rowheight = gridRef.current?.slickGrid.getOptions().rowHeight;

    let maxLines = 1;
    if (rowheight === 72) {
      maxLines = 3;
    } else if (rowheight === 96) {
      maxLines = 4;
    }

    const cellValue = String(args.value ?? "");

    // Outer node is the flex item inside `.slick-cell` (display:flex; align-items:center).
    // It MUST stay a plain block wrapper: a `-webkit-box` placed directly as that flex item
    // collapses to a single line and mis-aligns under align-items:center. This mirrors the
    // original `<Text grid>` DOM (mount div > Box) exactly, minus the per-cell React root.
    const container = document.createElement("div");
    container.style.width = "100%";
    container.style.overflow = "hidden";

    // [plain-DOM] exact replica of `<Text grid sx={...}>` (cubeComponent/html/Text/Text.tsx):
    // multi-line clamp + ellipsis with zero React per cell. The delegated gridCellTooltip
    // reads `data-cube-tip` and shows the full value only when this box actually clips.
    const inner = document.createElement("div");
    inner.style.overflow = "hidden";
    inner.style.textOverflow = "ellipsis";
    inner.style.verticalAlign = "bottom";
    inner.style.fontFamily = "inherit";
    // Force wrapping so LONG UNBROKEN tokens (e.g. `<script>...` XSS test strings, urls, ids)
    // also clamp to `maxLines` + ellipsis instead of sitting on one clipped line. Normal spaced
    // text is unaffected (only breaks a word when it would otherwise overflow).
    inner.style.wordBreak = "break-word";
    inner.style.setProperty("overflow-wrap", "anywhere");
    inner.style.display = "-webkit-box";
    inner.style.setProperty("-webkit-box-orient", "vertical");
    inner.style.setProperty("-webkit-line-clamp", String(maxLines));
    // Do NOT set an explicit line-height / max-height here. `-webkit-line-clamp`
    // already caps the box to `maxLines` lines on its own, and line spacing is
    // inherited from `.slick-cell` (line-height: 1.3rem) for consistency with the
    // rest of the grid. An explicit rem line-height resolves to a NON-INTEGER pixel
    // value under the viewport-clamped root font-size (index.css
    // `html { font-size: clamp(0.75rem, 1vw, 1rem) }`) combined with browser zoom;
    // the `-webkit-box` line box then rounds up past a max-height pinned to the same
    // rem value, and `overflow:hidden` clips the whole line -> the cell text
    // DISAPPEARS at certain zoom levels (e.g. 67% on macOS Chrome, 100% on Windows).
    inner.style.width = "100%";
    inner.style.height = "100%";
    inner.textContent = cellValue;
    if (cellValue) inner.setAttribute("data-cube-tip", cellValue);

    container.appendChild(inner);
    return container;
  };

  const callRemote = (
    source: string | ((body: { value: QueryShape }) => Promise<RemoteDataResponse<T>>),
    body: { value: QueryShape },
  ): Promise<RemoteDataResponse<T>> => callRemoteSource(source, body);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const navigationKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (navigationKeys.includes(e.code)) {
      e.stopPropagation();
      return;
    }
    // Allow clipboard shortcuts (Cmd+C/V/X on Mac, Ctrl+C/V/X on Windows/Linux)
    // to reach the native input instead of being captured by SlickGrid's ExcelCopyBuffer
    if ((e.metaKey || e.ctrlKey) && ["KeyC", "KeyV", "KeyX", "KeyA", "KeyZ"].includes(e.code)) {
      e.stopPropagation();
    }
  };
  const renderEditorTemplate = (updatedColumn: ColumnDefinition<T>, dataContext: T): HTMLDivElement => {
    const value: unknown = updatedColumn._valueGetter
      ? updatedColumn._valueGetter(dataContext)
      : get(dataContext, String(updatedColumn.field));

    const container = document.createElement("div");
    container.classList.add("w-full");
    const root = createRoot(container);
    root.render(
      <Box className="w-full h-full flex" onKeyDown={handleKeyPress}>
        {updatedColumn.editorTemplate?.({
          // `editorTemplate` is declared against the column's OWN field type, but the value handed
          // to it is resolved at runtime — through `_valueGetter`, or by a `field` that is a dotted
          // path on a column built by `withDynamicColumns` — so it is only statically that type for
          // the plain-key case. Widening `value` to `unknown` on the public `ColumnDefinition` is
          // the real fix and would re-check every `editorTemplate` in the app.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- runtime-resolved cell value, see comment above
          value: value as T[keyof T],
          updatedColumn,
          args: dataContext,
        })}
      </Box>,
    );
    return container;
  };

  // `dataContext` is `T | undefined` on purpose: SlickGrid calls a cell formatter with whatever
  // `getDataItem(row)` returned, which is `undefined` for a row index it is re-rendering past the
  // end of the current data (and for the filler rows this grid splices in). Every body below
  // already guards for it — this states the type those guards are actually written against.
  type FormatterFunction = (
    row: number,
    cell: number,
    value: unknown,
    columnDef: ColumnDefinition<T>,
    dataContext: T | undefined,
    grid?: SlickGridType,
  ) => unknown;

  const getFormatter = (column: ColumnDefinition<T>): FormatterFunction | undefined => {
    if (column.formatterType) {
      const registeredFormatter = getFormatterByType(column.formatterType);
      if (registeredFormatter && typeof registeredFormatter === "function") {
        return function (
          row: number,
          cell: number,
          value: unknown,
          columnDef: ColumnDefinition<T>,
          dataContext: T | undefined,
          grid?: SlickGridType,
        ) {
          if (dataContext?.__isDummy) return "";
          try {
            // FormattersRegistery's formatters are written against UNTYPED rows: `FormatterColDef`
            // declares `field?: string` and `_valueGetter?: ((ctx: Record<string, unknown>) =>
            // unknown) | string` (./Formatters/Formatters.tsx:56-64), so neither this column
            // (`field: keyof T`, `_valueGetter: (row: T) => unknown`) nor this row (`T`, which has
            // no index signature) is assignable to what they declare — even though these are
            // exactly the objects they are handed at runtime, and they read the very same members
            // off them. Making the registry generic is the real fix and belongs in that file.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- untyped-row formatter boundary, see comment above
            const registryColumn = columnDef as unknown as FormatterColDef & FormatterDateColDef;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- untyped-row formatter boundary, see comment above
            const registryRow = dataContext as unknown as Record<string, unknown>;
            return registeredFormatter(
              row,
              cell,
              value,
              registryColumn,
              registryRow,
              grid as FormatterGridLike | undefined,
            );
          } catch (error) {
            console.error(`Formatter error for type "${column.formatterType}":`, error);
            return value ?? "";
          }
        };
      }
    }
    const noCustomRendering =
      !column.formatter && !column.formatterType && (column.clipMode === "EllipsisWithTooltip" || !column.clipMode);

    if (noCustomRendering) {
      return (
        _row: number,
        _cell: number,
        value: unknown,
        columnDef: ColumnDefinition<T>,
        dataContext: T | undefined,
      ) => {
        if (dataContext?.__isDummy) return "";

        let resolvedValue = value;
        if (resolvedValue === undefined && columnDef.field) {
          resolvedValue = get(dataContext, String(columnDef.field));
        }
        const finalValue = dataContext && column._valueGetter ? column._valueGetter(dataContext) : resolvedValue;

        if (props.enableWrapText) {
          const container = document.createElement("div");
          container.style.whiteSpace = "pre-wrap";
          container.style.wordBreak = "break-word";
          container.style.overflow = "hidden";
          container.textContent = String(finalValue ?? "");
          return container;
        }
        return ellipseFormatterWithTooltip({
          value: finalValue,
          column: columnDef,
        });
      };
    }

    if (column.formatter && typeof column.formatter === "function") {
      const base = column.formatter;
      return function (
        row: number,
        cell: number,
        value: unknown,
        columnDef: ColumnDefinition<T>,
        dataContext: T | undefined,
        grid?: SlickGridType,
      ) {
        if (!dataContext || dataContext.__isDummy) return "";
        try {
          // Same boundary as `editorTemplate` above: `ColumnDefinition.formatter` declares `value`
          // as the column's own field type, but what SlickGrid passes is whatever it read off the
          // row at runtime — only statically that type for a plain-key `field`.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- runtime-resolved cell value, see comment above
          return base(row, cell, value as T[keyof T], columnDef, dataContext, grid);
        } catch (error) {
          console.error(`Custom formatter error for column "${columnDef.id}":`, error);
          return value ?? "";
        }
      };
    }
    return (row: number, cell: number, value: unknown) => {
      if (value === null || value === undefined) return "";
      return String(value);
    };
  };

  function applyGrouping(groupConds: GroupCondition[]): void {
    const instance = gridRef.current;
    if (!instance) return;
    const grid = instance.slickGrid;
    const dataView = instance.dataView;
    // IMPORTANT: Use getAllColumnDefinitions() to include hidden columns
    // grid.getColumns() only returns visible columns, which causes grouping to fail
    // when grouping by a hidden column (all rows would group under "-")
    const allColumns = readGridColumns<T>(instance.gridService.getAllColumnDefinitions());

    if (groupConds.length) {
      gridRef.current?.slickGrid.setOptions({ frozenColumn: -1 });
      if (props.enableWrapText) {
        props.setEnableWrapText?.(false);
      }
      const visibleColumns = allColumns.filter((c) => !c.hidden);

      const resolveCol = (key: string): ColumnDefinition<T> | null => {
        // First try visible columns, then fall back to all columns (including hidden)
        let col = visibleColumns.find((c) => c.id === key || c.field === key);
        if (!col) {
          col = allColumns.find((c) => c.id === key || c.field === key);
        }
        return col ?? null;
      };

      const groupings = groupConds.map((gc) => {
        const col = resolveCol(gc.column);
        const getter = (args: T) => col?._valueGetter?.(args) ?? (col?.field ? args[col.field] : "");
        const formatter = createGroupFormatter(col);
        const comparer = createGroupComparer(col, gc.direction ?? "asc", gc.preserveOrder ?? false);

        return {
          getter,
          formatter,
          comparer,
          collapsed: false,
          lazyTotalsCalculation: true,
        };
      });

      // `pluginName`/`init`/`destroy` are slickgrid's `SlickPlugin` contract — stating them lets
      // `getPluginByName` be called at this type instead of asserting its result afterwards.
      type GroupItemMetadataProviderPlugin = {
        pluginName: string;
        init: (grid: SlickGridType) => void;
        destroy: () => void;
        _grid?: SlickGridType;
      };
      const instanceWithMetadata = instance as SlickgridReactInstance & {
        groupItemMetadataProvider?: GroupItemMetadataProviderPlugin;
      };
      if (!instanceWithMetadata.groupItemMetadataProvider) {
        const provider = grid.getPluginByName<GroupItemMetadataProviderPlugin>("GroupItemMetadataProvider");
        if (provider && !provider._grid) provider.init(grid);
      }

      // slickgrid declares `Grouping.formatter` as `(g: GroupingFormatterItem) => string` and
      // `comparer` against `GroupingComparerItem` (node_modules/@slickgrid-universal/common/dist/
      // types/interfaces/grouping.interface.d.ts:18 and :22), but the DataView hands both the
      // SlickGroup itself (which carries `rows`, and is what these read) and renders an HTMLElement
      // return — this grid's group header IS a DOM node, see createGroupFormatter.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- slickgrid grouping callbacks are declared narrower than what the DataView actually passes and accepts, see comment above
      dataView.setGrouping(groupings as unknown as Grouping<T>[]);
      gridRef.current?.slickGrid.invalidate();
    } else {
      const grid = gridRef.current?.slickGrid;
      if (grid && pinnedColumns.length) {
        const currentCols = grid.getColumns();
        const pinnedSet = new Set(pinnedColumns);
        const pinCols = currentCols.filter((c) => pinnedSet.has(String(c.id)));
        const unpinCols = currentCols.filter((c) => !pinnedSet.has(String(c.id)));
        grid.setColumns([...pinCols, ...unpinCols]);
        grid.setOptions({ frozenColumn: pinCols.length ? pinCols.length - 1 : -1 });
      }
      gridRef.current?.dataView.setGrouping([]);
    }
  }

  const reorderColumns = (newPinned: string[]): void => {
    const grid = gridRef.current?.slickGrid;
    const gridService = gridRef.current?.gridService;
    if (!grid || !gridService) return;

    let allCols: ColumnDefinition<T>[] = readGridColumns<T>(gridService.getAllColumnDefinitions());
    allCols = allCols.map((col: ColumnDefinition<T>) => {
      col.formatter = getFormatter(col);
      return col;
    });
    const pinnedSet = new Set(newPinned);
    const pinned = allCols.filter((c: ColumnDefinition<T>) => pinnedSet.has(String(c.id)));
    const unpinned = allCols.filter((c: ColumnDefinition<T>) => !pinnedSet.has(String(c.id)));
    const newOrder = [...pinned, ...unpinned];
    grid.setColumns(asSlickColumns<T>(newOrder));
    grid.setOptions({ frozenColumn: pinned.length ? pinned.length - 1 : -1 });
  };

  const appliedDefaultPinsRef = useRef<string | null>(null);
  useEffect(() => {
    const defaults = props.defaultPinnedColumns;
    if (!defaults || !gridMounted) return;
    const requested = defaults.join("|");
    if (appliedDefaultPinsRef.current === requested) return;
    if (appliedDefaultPinsRef.current === null && defaults.length === 0) return;
    appliedDefaultPinsRef.current = requested;
    setPinnedColumns(withBasePinned(defaults));
  }, [props.defaultPinnedColumns, gridMounted]);

  const applyFilter = (
    data: T[],
    columnId: string,
    filterOption: QueryOperator,
    filterValue: unknown,
    fType: FilterType,
  ): T[] => {
    const columnDefs: RuntimeColumnDefinition<T>[] = props.columnDefinitions ?? [];
    return data.filter((item) => {
      const columnDef = columnDefs.find((col) => col.field === columnId);

      // For FileSize, use _filterValueGetter (raw bytes) or raw field value, not _valueGetter (formatted string)
      let fieldValue: unknown;
      if (fType === "FileSize") {
        const filterValueGetter = columnDef?._filterValueGetter;
        fieldValue = filterValueGetter ? filterValueGetter(item) : get(item, columnId) || 0;
      } else {
        const filterValueGetter = columnDef?._filterValueGetter;
        if (typeof filterValueGetter === "function") {
          // _filterValueGetter wins: filter by the displayed value, not a token like a userTag id
          fieldValue = filterValueGetter(item);
        } else if (typeof columnDef?._valueGetter === "function") {
          fieldValue = columnDef._valueGetter(item);
        } else if (typeof columnDef?._valueGetter === "string") {
          // ?? (not ||) so falsy values like 0 remain filterable
          fieldValue = get(item, columnDef._valueGetter) ?? "";
        } else {
          fieldValue = get(item, columnId) ?? "";
        }
      }

      return getConditionMet(fType, fieldValue, filterOption, filterValue);
    });
  };

  const getUpdatedFilterConditions = (rawConds: Record<string, FilterCondition>): Record<string, FilterCondition> => {
    // Handed to the toolbar / filter modal as `(filterConditionsObj: Record<string, unknown>) =>
    // unknown`, so nothing type-checks what it is called with, and the in-component caller passes
    // `filterConditions` — which a saved view can seed. The operator is therefore RESOLVED here
    // rather than trusted: the `Date` branches below key off `option`, and a condition that
    // reaches them without one has to keep behaving like the `equal` default it was seeded with.
    const conds = withResolvedFilterOptions(rawConds);
    const toValidDayjs = (value: unknown): Dayjs | null => {
      if (value === null || value === undefined || value === "") return null;
      if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
      // Anything dayjs cannot parse would come back as an Invalid Date and be dropped below, so it
      // is rejected here instead — same result, and no cast over an unchecked value.
      if (typeof value !== "string" && typeof value !== "number" && !(value instanceof Date)) return null;
      const d = dayjs(value);
      return d.isValid() ? d : null;
    };

    const updatedFilterConditions = Object.keys(conds).reduce<Record<string, FilterCondition>>((result, filterKey) => {
      const filterValue = conds[filterKey]!;

      if (filterValue.option === "between" && filterValue.type === "Date") {
        const { text, ...otherFields } = filterValue;
        const dateText: DateRangeText = isDateRangeText(text) ? text : {};
        const startDate = toValidDayjs(dateText.startDate);
        const endDate = toValidDayjs(dateText.endDate);
        if (startDate) {
          result[`${filterKey}_start`] = {
            ...otherFields,
            option: "greaterthanorequal",
            text: startDate.format(),
          };
        }
        if (endDate) {
          result[`${filterKey}_end`] = {
            ...otherFields,
            option: "lessthanorequal",
            text: endDate.format(),
          };
        }
        return result;
      }

      if (filterValue.type === "Date" && filterValue.option === "equal") {
        const { text, ...otherFields } = filterValue;
        const dateText: DateRangeText = isDateRangeText(text) ? text : {};
        const effectiveDate = toValidDayjs(dateText.date) ?? dayjs();
        result[`${filterKey}_start`] = {
          ...otherFields,
          option: "greaterthanorequal",
          text: effectiveDate.startOf("day").format(),
        };
        result[`${filterKey}_end`] = {
          ...otherFields,
          option: "lessthanorequal",
          text: effectiveDate.endOf("day").format(),
        };
        return result;
      }

      result[filterKey] = filterValue;
      return result;
    }, {});

    return updatedFilterConditions;
  };

  // `sortConds` is declared nullable because this is handed to ToolBar as a plain prop
  // (`sortDataset?: unknown` there), so nothing type-checks what it is called with.
  function sortDataset(dataArr: T[], sortConds: SortCondition[] | undefined): T[] {
    if (!sortConds || sortConds.length === 0) return dataArr;
    const columnDefs: RuntimeColumnDefinition<T>[] = props.columnDefinitions ?? [];
    return [...dataArr].sort((a, b) => {
      for (const condition of sortConds) {
        const columnId = condition.column;
        const sortAsc = condition.direction === "asc";

        // Find column definition - check by id first, then by field
        const columnDefinition = columnDefs.find((col) => col.id === columnId || col.field === columnId);
        const valueGetter = columnDefinition?._valueGetter;
        const fieldPath = columnDefinition?.field ? String(columnDefinition.field) : columnId;
        // For FileSize, sort on _filterValueGetter (raw bytes) not _valueGetter (formatted string like "900 KB")
        const filterValueGetter =
          columnDefinition?.filterType === "FileSize" ? columnDefinition._filterValueGetter : undefined;

        // Helper to resolve value - guards against legacy string _valueGetter
        const resolveVal = (row: T): unknown => {
          if (typeof filterValueGetter === "function") return filterValueGetter(row);
          if (typeof valueGetter === "function") return valueGetter(row);
          if (typeof valueGetter === "string") return get(row, valueGetter);
          return get(row, fieldPath);
        };

        const valA = resolveVal(a);
        const valB = resolveVal(b);

        // Use improved comparison with proper null handling
        // Nulls go to the end ("last"), regardless of sort direction
        const comparison = compareSortValues(valA, valB, sortAsc, "last");

        if (comparison !== 0) {
          return comparison;
        }
      }
      return 0;
    });
  }

  const buildModifiedQuery = (skip: number, take: number): QueryShape => {
    const modifiedQuery: QueryShape = { ...props.query, skip, take, requiresCounts: true };

    const liveFilters = stateRef.current?.filterPredicates || filterPredicates;

    const hasPredicates = liveFilters && typeof liveFilters === "object" && Object.keys(liveFilters).length > 0;

    if (hasPredicates) {
      const existingWhere = (props.query as QueryShape | undefined)?.where;
      if (existingWhere?.length) {
        modifiedQuery.where = [...existingWhere, liveFilters as Record<string, unknown>];
      } else {
        modifiedQuery.where = [liveFilters as Record<string, unknown>];
      }
    }

    if (sortConditions.length > 0) {
      modifiedQuery.sorted = sortConditions.map((c) => ({
        name: c.column,
        direction: c.direction === "asc" ? "ascending" : "descending",
      }));
    }

    const liveSearch = (searchTermRef.current || "").trim();
    if (liveSearch) {
      const restrictTo = props.searchFields as string[] | undefined;
      // Read at ColumnWithOptionalId: a runtime column can arrive without an `id`, and
      // `String(undefined)` would put the literal "undefined" into the global-search field list
      // (it is truthy and does not start with "_"), making the server search a field named
      // "undefined" on every row. "" is dropped by the filter below.
      const searchColumns: Array<ColumnWithOptionalId<T>> = props.columnDefinitions ?? [];
      const columnFields = searchColumns
        .map((col) => (typeof col.field === "string" && col.field ? col.field : String(col.id ?? "")))
        .filter((field) => Boolean(field) && !field.startsWith("_"));
      const searchableFields = [...new Set(restrictTo?.length ? restrictTo : columnFields)];
      if (searchableFields.length) {
        const existingWhere = modifiedQuery.where as unknown;
        const whereArray = Array.isArray(existingWhere) ? existingWhere : existingWhere ? [existingWhere] : [];
        modifiedQuery.where = [
          ...whereArray,
          { field: searchableFields.join(","), operator: "globalsearch", value: liveSearch },
        ] as QueryShape["where"];
      }
    }

    if (nextPageTokenRef.current) {
      modifiedQuery.nextPageToken = nextPageTokenRef.current;
    }

    return modifiedQuery;
  };

  const processRemoteResponse = (response: RemoteDataResponse<T>): T[] => {
    if (gridRef.current) {
      gridRef.current.actual = response;
    }

    let modifiedResponse: T[];
    if (props.beforeDataBound) {
      modifiedResponse = props.beforeDataBound(response);
    } else {
      modifiedResponse = response.data?.result ?? [];
    }

    if (response.data?.nextPageToken) {
      nextPageTokenRef.current = response.data.nextPageToken;
    }
    if (response.data?.isLast !== undefined) {
      isLastRef.current = response.data.isLast;
    }
    if (response.data?.count !== undefined) {
      setTotalItemCount(response.data.count);
    }
    return modifiedResponse;
  };

  const collectMissingParents = (
    filteredData: T[],
    idMap: Map<RowId, T>,
    neededIds: Set<RowId>,
    parentKey: string,
  ): T[] => {
    const buildParentChain = (child: T): T[] => {
      const chain: T[] = [];
      let current: T = child;

      for (;;) {
        // Dynamic parent-field read; `T`'s key is not statically known, so it is guarded.
        const parentId = (current as Record<string, unknown>)[parentKey];
        if (!isRowId(parentId) || neededIds.has(parentId)) break;

        const parent = idMap.get(parentId);
        if (!parent) break;

        neededIds.add(parentId);
        chain.push(parent);
        current = parent;
      }
      return chain;
    };

    const appendUnique = (targetArray: T[], seenSet: Set<RowId>, itemsToAppend: T[]): void => {
      for (let i = itemsToAppend.length - 1; i >= 0; i--) {
        const p = itemsToAppend[i]!;
        const key = readRowId(p);
        if (key !== undefined && !seenSet.has(key)) {
          targetArray.push(p);
          seenSet.add(key);
        }
      }
    };

    const missingParentData: T[] = [];
    const addedParentIds = new Set<RowId>();

    for (const child of filteredData) {
      const chain = buildParentChain(child);
      appendUnique(missingParentData, addedParentIds, chain);
    }

    return missingParentData;
  };

  const mergeUniqueItems = (missingParentData: T[], filteredData: T[]): T[] => {
    const out: T[] = [];
    const seen = new Set<RowId>();

    for (const item of [...missingParentData, ...filteredData]) {
      const key = readRowId(item);
      if (key !== undefined && !seen.has(key)) {
        seen.add(key);
        out.push(item);
      }
    }

    return out;
  };

  function updatePreHeaderVisibility(grid: SlickGridType): void {
    const visibleColumns = grid.getColumns();

    const hasGroups = visibleColumns.some(
      (col) => col.columnGroup && col.columnGroup !== "Standard Fields" && col.columnGroup.trim() !== "",
    );

    grid.setPreHeaderPanelVisibility(hasGroups);

    grid.resizeCanvas();
  }

  useEffect(() => {
    const leftVp = document.querySelector(".slick-viewport-left");
    const rightVp = document.querySelector(".slick-viewport-right") as HTMLElement | null;

    if (leftVp && rightVp) {
      leftVp.addEventListener("mouseenter", () => {
        rightVp.style.setProperty("overflow-y", "auto", "important");
      });

      leftVp.addEventListener("mouseleave", () => {
        rightVp.style.setProperty("overflow-y", "hidden", "important");
      });

      rightVp.addEventListener("mouseenter", () => {
        rightVp.style.setProperty("overflow-y", "auto", "important");
      });
    }
  }, [gridMounted]);

  const includeMissingParentsForTree = (filteredData: T[], items: T[], parentKey: string, treeGrid: boolean): T[] => {
    if (!treeGrid) return filteredData;

    // Rows without a usable id were previously stored under an `undefined` key, which no lookup
    // below could ever reach; they are simply left out now.
    const idMap = new Map<RowId, T>();
    for (const item of items) {
      const key = readRowId(item);
      if (key !== undefined) idMap.set(key, item);
    }
    const neededIds = new Set<RowId>();
    for (const item of filteredData) {
      const key = readRowId(item);
      if (key !== undefined) neededIds.add(key);
    }

    const missingParentData = collectMissingParents(filteredData, idMap, neededIds, parentKey);

    return mergeUniqueItems(missingParentData, filteredData);
  };

  interface FilterConditionWithIndex extends FilterCondition {
    originalKey: string;
    index: number;
  }

  const applyFiltersToData = (data: T[], updatedFilterConditions: Record<string, FilterCondition>): T[] => {
    if (!appliedFilters.length) return data;

    const filterKeys = Object.keys(updatedFilterConditions);
    if (filterKeys.length === 0) return data;

    const conditionsWithIndex: FilterConditionWithIndex[] = filterKeys.map((key) => {
      const match = /_(\d+)$/.exec(key);
      const index = match?.[1] ? Number.parseInt(match[1], 10) : 0;
      return {
        ...updatedFilterConditions[key]!,
        originalKey: key,
        index: index,
      };
    });

    conditionsWithIndex.sort((a, b) => a.index - b.index);

    if (conditionsWithIndex.length === 1) {
      const condition = conditionsWithIndex[0]!;
      // `option` is guaranteed present here: this map is what getUpdatedFilterConditions returned,
      // and that runs withResolvedFilterOptions over its input — which is also the only way an
      // unvalidated saved view (processFilterConditions, props.preAppliedFilters) reaches state.
      // The filter UI is the remaining source and only builds a condition once an operator is
      // picked (FilterModal `if (cond.field && cond.operator)`).
      return applyFilter(data, condition.id, condition.option, condition.text, condition.type);
    }

    const filterColumnDefs: ColumnDefinition<T>[] = props.columnDefinitions ?? [];
    return data.filter((item) => {
      let result = false;

      for (let i = 0; i < conditionsWithIndex.length; i++) {
        const condition = conditionsWithIndex[i]!;
        const columnDef = filterColumnDefs.find((col) => col.field === condition.id);

        // For FileSize, use _filterValueGetter (raw bytes) or raw field value
        let fieldValue: unknown;
        if (condition.type === "FileSize") {
          const filterValueGetter = columnDef?._filterValueGetter;
          fieldValue = filterValueGetter ? filterValueGetter(item) : get(item, condition.id) || 0;
        } else {
          const filterValueGetter = columnDef?._filterValueGetter;
          const valueGetter = columnDef?._valueGetter;
          // _filterValueGetter wins so userTag-style columns filter by name, not the id token.
          // ?? (not ||) so falsy values like 0 remain filterable
          if (typeof filterValueGetter === "function") {
            fieldValue = filterValueGetter(item);
          } else {
            fieldValue = valueGetter ? valueGetter(item) : (get(item, condition.id) ?? "");
          }
        }

        // See the single-condition branch above: every map that reaches here has been through
        // withResolvedFilterOptions, so `option` is the stored operator or its per-type default.
        const conditionMet = getConditionMet(condition.type, fieldValue, condition.option, condition.text);

        if (i === 0) {
          result = conditionMet;
        } else {
          const logic = condition.logic?.toLowerCase() || "and";
          result = applyLogicToResult(result, conditionMet, logic);
        }
      }

      return result;
    });
  };

  const applySearchToData = (rows: T[], term: string): T[] => {
    const cols: RuntimeColumnDefinition<T>[] = props.columnDefinitions ?? [];
    if (!cols.length) return rows;
    const restrictTo = props.searchFields as string[] | undefined;
    return rows.filter((row) =>
      cols.some((col) => {
        if (restrictTo?.length && !restrictTo.includes(String(col.field)) && !restrictTo.includes(String(col.id))) {
          return false;
        }
        let value: unknown;
        const filterValueGetter = col._filterValueGetter;
        if (typeof filterValueGetter === "function")
          value = filterValueGetter(row); // search the displayed value (e.g. userTag name), not the id token
        else if (typeof col._valueGetter === "function") value = col._valueGetter(row);
        else if (typeof col._valueGetter === "string") value = get(row, col._valueGetter);
        else if (col.field) value = get(row, String(col.field));
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      }),
    );
  };

  const buildFilteredData = (): T[] => {
    const updatedFilterConditions = getUpdatedFilterConditions(filterConditions);
    const sourceData = props.dataset ?? [];

    let data: T[];

    if (appliedFilters.length && Object.keys(updatedFilterConditions).length) {
      const filtered = applyFiltersToData(sourceData, updatedFilterConditions);
      data =
        props.treeGrid && props.parentIdMapping
          ? includeMissingParentsForTree(filtered, sourceData, props.parentIdMapping, props.treeGrid)
          : filtered;
    } else {
      data = sourceData;
    }

    const liveSearch = (searchTermRef.current || "").trim().toLowerCase();
    if (liveSearch) {
      const searched = applySearchToData(data, liveSearch);
      data =
        props.treeGrid && props.parentIdMapping
          ? includeMissingParentsForTree(searched, sourceData, props.parentIdMapping, props.treeGrid)
          : searched;
    }

    if (sortConditions.length) {
      data = sortDataset(data, sortConditions);
    }

    return data;
  };

  const processLocalDataset = (skip: number, take: number) => {
    if (skip === 0 || filteredDataRef.current.length === 0) {
      const built = buildFilteredData();
      filteredDataRef.current = built;
      setTotalItemCount(built.length);
    }

    const base = filteredDataRef.current;
    return base.slice(skip, skip + take);
  };

  useEffect(() => {
    if (gridRef.current?.slickGrid && gridMounted && !props.disableRepaintOnDataChange) {
      gridRef.current.slickGrid.invalidate();
    }
  }, [dataset, gridMounted, gridRef, props.disableRepaintOnDataChange]);

  const loadData = async (skip: number, take: number, appendNext = false): Promise<void> => {
    // When starting a fresh load (not appending), reset pagination state
    if (!appendNext) {
      nextPageTokenRef.current = null;
      isLastRef.current = false;
      isLoadingMoreRef.current = false;
    }

    let modifiedResponse: T[] = [];

    try {
      if (props.query && props.remoteDataSource) {
        const modifiedQuery = buildModifiedQuery(skip, take);
        const response = await callRemote(props.remoteDataSource, { value: modifiedQuery });
        modifiedResponse = processRemoteResponse(response);
      } else if (props.dataset) {
        modifiedResponse = processLocalDataset(skip, take);
      }

      if (props.treeGrid && modifiedResponse.length && props.hasChildMapping) {
        modifiedResponse = mapTreeGridFlags(modifiedResponse, props.hasChildMapping, props.initiallyExpanded);
      }

      modifiedResponse = ensureIds(modifiedResponse, props.idMapping);

      setLoaderOnPageChange(false);

      let finalDataset!: T[];
      if (appendNext) {
        const newWithIds = ensureIds(modifiedResponse, props.idMapping);
        const dataView = gridRef.current ? dataViewOf(gridRef.current) : undefined;
        const currentItems: T[] = dataView?.getItems() ?? [];
        const existingIds = new Set(currentItems.map((item) => item.id));
        const uniqueNew = newWithIds.filter((item) => !existingIds.has(item.id));
        const hasNewItems = uniqueNew.length > 0;

        finalDataset = [...currentItems, ...uniqueNew];

        if (hasNewItems && dataView) {
          dataView.beginUpdate();
          dataView.setItems(finalDataset);
          dataView.endUpdate();
          dataView.reSort();
          gridRef.current?.slickGrid.invalidate();
        }

        setDataset(finalDataset);
        setLoaderForInfiniteScroll(false);
        if (!hasNewItems || (totalItemCountRef.current > 0 && finalDataset.length >= totalItemCountRef.current)) {
          isLastRef.current = true;
        }
        isLoadingMoreRef.current = false;
      } else {
        finalDataset = modifiedResponse;
        setDataset(finalDataset);
        stateRef.current = {
          ...getStateSnapshot(),
          dataset: finalDataset,
        };
      }

      if (!appendNext) {
        if (
          !(enableSearchBar && restoreSelectionAfterSearchRef.current) &&
          (gridRef.current?.slickGrid.getSelectedRows().length ||
            gridRef.current?.dataView.getAllSelectedItems().length) &&
          !props.isEditMode
        ) {
          persistedSelectedItemsRef.current.clear();
          // `selectedRowIds` IS declared, but PROTECTED —
          // node_modules/@slickgrid-universal/common/dist/types/core/slickDataview.d.ts:74
          // (`protected selectedRowIds: DataIdType[]`) — so it cannot be reached through the
          // public DataView type, hence the one-property intersection. Clearing it is NOT made
          // redundant by the `setSelectedIds([])` two lines down: that method (esm/core/
          // slickDataview.js `setSelectedIds`) never assigns `selectedRowIds` itself, it only
          // notifies and delegates to `preSelectedRowIdsChangeFn` / `grid.setSelectedRows()`.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- protected vendor member, see comment above
          const gridWithInternals = gridRef.current as SlickgridReactInstanceX<T> & {
            dataView: { selectedRowIds: unknown[] };
          };
          gridWithInternals.dataView.selectedRowIds = [];
          gridRef.current.slickGrid.setSelectedRows([]);
          gridRef.current.dataView.setSelectedIds([], { isRowBeingAdded: true });
        }
      }

      if (setGridReadyRef.current) {
        setGridReadyRef.current(true, finalDataset.length);
      }

      firstCallRef.current = false;

      // When an excel export is pending, sync the DataView immediately so the
      // export that runs in the next requestAnimationFrame sees the FULL dataset.
      // Without this, the rAF fires before React's useEffect syncs `dataset` state
      // to the DataView, causing the export to use stale (partial) data.
      if (excelExportServiceRef.current && gridRef.current) {
        gridRef.current.dataView.beginUpdate();
        gridRef.current.dataView.setItems(finalDataset);
        gridRef.current.dataView.endUpdate();
        gridRef.current.slickGrid.invalidate();
      }

      requestAnimationFrame(() => {
        if (excelExportServiceRef.current) {
          if (stateRef.current?.groupConditions && stateRef.current.groupConditions.length > 0) {
            exportGroupedExcel(gridRef);
          } else {
            interface ExternalResource {
              className?: string;
              exportToExcel?: () => void;
            }
            type ExtensionServiceWithShared = {
              sharedService?: {
                externalRegisteredResources?: ExternalResource[];
              };
            };
            // `ExtensionService.sharedService` is declared PROTECTED —
            // node_modules/@slickgrid-universal/common/dist/types/services/extension.service.d.ts:15
            // (`protected readonly sharedService: SharedService`) — so the registered-resource list
            // it holds cannot be reached through the public type even though
            // `SharedService.externalRegisteredResources` itself is public (shared.service.d.ts:39).
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- protected vendor member, see comment above
            const extService = gridRef.current?.extensionService as ExtensionServiceWithShared | undefined;
            const exportService = extService?.sharedService?.externalRegisteredResources?.find(
              (service) => service.className === "ExcelExportService",
            );
            exportService?.exportToExcel?.();
          }
          onExcelExportCompleteRef.current?.();
        }
        excelExportServiceRef.current = false;
      });
    } catch (error) {
      console.error("SlickGrid1 loadData failed", error);
      if (appendNext) {
        setLoaderForInfiniteScroll(false);
        isLoadingMoreRef.current = false;
      } else {
        setLoaderOnPageChange(false);
        firstCallRef.current = false;
        setGridReadyRef.current?.(true, 0);
      }
      return Promise.reject(error);
    }
  };

  useEffect(() => {
    applyGrouping(groupConditions);
  }, [groupConditions, props.enableWrapText]);

  const metadataRef = useRef<Record<string, RowMetadata>>({});
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  // CUBE-29425: `.slick-cell` is border-box with `padding: 0 1rem` in wrap mode plus a 1px
  // inline-end border (index.css), so `column.width` is the OUTER box and that chrome must come
  // off before the wrapped-line count below - the old flat `padding = 8` over-stated the usable
  // width by ~20% and lost roughly one line in five. The rem resolves against the LIVE root
  // font-size: the web root is `clamp(0.75rem, 1vw, 1rem)` (src/index.css), i.e. 12-16px by
  // viewport, never a fixed 16. Read once per render - a getComputedStyle per cell would be
  // hundreds of style reads.
  const rootFontSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const cellChromeWidth = 2 * rootFontSizePx + 1; // `padding: 0 1rem` both sides + 1px border
  if (context) {
    // Canvas measureText requires pixel-exact font matching grid CSS text-sm (14px);
    // detached canvas can't reliably resolve rem. Kept in px intentionally. Name the actual
    // cell family (not the generic `sans-serif`, which is wider) so the width — and thus the
    // wrapped line count — tracks the rendered text and rows don't over-reserve blank lines.
    context.font = "14px 'Source Sans Pro', sans-serif";
  }

  function stripHtml(input: unknown): string {
    if (typeof input !== "string") return String(input || "");

    return input.replace(/<[^>]*>?/gm, "");
  }

  function getRenderedLines(
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
    itemId: RowId | undefined,
    colIndex: number,
  ): number {
    if (itemId == null) return 1;

    const item = dataViewItemById(dataView, String(itemId));
    if (!item) return 1;

    const gridColumn = slickGrid.getColumns()[colIndex];
    if (!gridColumn) return 1;
    const column = readGridColumn<T>(gridColumn);

    if (!column.wrapText) return 1;

    let textContent: string = String(item[column.field] ?? "");

    if (typeof column.formatter === "function") {
      const rowIndex = 0;
      const result = column.formatter(rowIndex, colIndex, item[column.field], column, item, slickGrid);

      if (result instanceof HTMLElement) {
        textContent = result.textContent || result.innerText || "";
      } else if (typeof result === "object" && result !== null && "text" in result) {
        textContent = String((result as { text: unknown }).text ?? "");
      } else {
        textContent = stripHtml(result);
      }
    }
    if (!textContent) return 1;
    if (context) {
      const textWidth = context.measureText(String(textContent)).width;
      const availableWidth = (column.width || 100) - cellChromeWidth;

      if (availableWidth <= 0) return 1;
      const lines = Math.ceil(textWidth / availableWidth);
      return lines > 0 ? lines : 1;
    }

    return 1;
  }

  function calculateMaxLinesForItem(
    item: T,
    columnDefs: Column<T>[],
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
  ): number {
    let maxLines = 1;
    for (const [colIndex] of columnDefs.entries()) {
      const totalLines = getRenderedLines(slickGrid, dataView, item.id, colIndex);
      maxLines = Math.max(maxLines, totalLines);
    }
    // Floor to 2 line-units: a single-line wrapped row then renders at a comfortable ~44px
    // (2 × the 22px wrap line-unit) instead of a cramped single line. Multi-line rows keep their
    // real count, so long rows still hug their text.
    return Math.max(2, maxLines);
  }

  function calculateRowSpans(
    dataArr: T[],
    columnDefs: Column<T>[],
    slickGrid: SlickGridType,
    dataView: SlickDataView<T>,
  ): RowSpanResult<T> {
    const metadata: Record<string, RowMetadata> = {};
    const newDataset: T[] = [];

    for (const item of dataArr) {
      if (item.__isDummy) continue;

      const maxRows = calculateMaxLinesForItem(item, columnDefs, slickGrid, dataView);
      const mainRow = createMainRow(item, undefined, maxRows);

      newDataset.push(mainRow);

      if (maxRows > 1 && mainRow.id) {
        metadata[String(mainRow.id)] = buildMetadataForRow(columnDefs, maxRows);

        const dummyRows = createDummyRows(item, mainRow.id, maxRows, gridRef);
        newDataset.push(...dummyRows);
      }
    }

    return { newDataset, metadata };
  }

  // `pluginName`/`init`/`destroy` are slickgrid's `SlickPlugin` contract; stating them lets
  // `getPluginByName` be called AT this type instead of asserting its result afterwards.
  type GroupItemMetadataProvider = {
    pluginName: string;
    init: (grid: SlickGridType) => void;
    destroy: () => void;
    getGroupRowMetadata: (item: T) => ItemMetadata | null;
    getTotalsRowMetadata: (item: T) => ItemMetadata | null;
  };

  const handleWrapUpdate = (): void => {
    const current = gridRef.current;
    if (!current) return;
    const slickGrid = current.slickGrid;
    const dataView = dataViewOf(current);
    const rawItems = dataView.getItems().filter((it) => !it.__isDummy);

    if (!props.enableWrapText) {
      dataView.beginUpdate();
      dataView.setItems(rawItems);
      dataView.endUpdate();

      dataView.getItemMetadata = (rowIndex: number): ItemMetadata | null => {
        const item = dataViewItemAt(dataView, rowIndex);
        if (!item) return null;
        const groupProvider =
          gridRef.current?.slickGrid.getPluginByName<GroupItemMetadataProvider>("GroupItemMetadataProvider");
        if (item.__group) {
          return groupProvider?.getGroupRowMetadata(item) ?? null;
        }
        if (item.__groupTotals) {
          return groupProvider?.getTotalsRowMetadata(item) ?? null;
        }
        return null;
      };

      slickGrid.invalidate();
      slickGrid.render();
    } else {
      const columns = slickGrid.getColumns();

      const { newDataset, metadata } = calculateRowSpans(rawItems, columns, slickGrid, dataView);

      metadataRef.current = metadata;

      gridRef.current?.slickGrid.remapAllColumnsRowSpan();

      requestAnimationFrame(() => {
        dataView.beginUpdate();
        dataView.setItems(newDataset);
        dataView.endUpdate();

        dataView.getItemMetadata = (rowIndex: number): ItemMetadata | null => {
          const item = dataViewItemAt(dataView, rowIndex);
          if (!item) return null;
          const groupProvider =
            gridRef.current?.slickGrid.getPluginByName<GroupItemMetadataProvider>("GroupItemMetadataProvider");
          if (item.__group) {
            return groupProvider?.getGroupRowMetadata(item) ?? null;
          }
          if (item.__groupTotals) {
            return groupProvider?.getTotalsRowMetadata(item) ?? null;
          }
          const cssClasses: string[] = [];
          if (item.__isDummy) cssClasses.push("dummy-row");
          if (item.disableBorder) cssClasses.push("disable-border");

          return {
            ...metadataRef.current[String(item.id)],
            cssClasses: cssClasses.join(" "),
          };
        };

        slickGrid.invalidate();
        slickGrid.render();
      });

      setTimeout(() => {
        if (props.treeGrid && !props.initiallyExpanded && !isTreeFilterOrSearchActive()) {
          gridRef.current?.treeDataService.toggleTreeDataCollapse(true);
        }
      }, 500);
    }
  };

  useEffect(() => {
    // For remote data sources + enableWrapText, the normal setItems path is gated behind
    // !enableWrapText so the dataView is never populated. Seed it here before handleWrapUpdate
    // runs so it has the fetched rows to process when the grid first mounts.
    if (props.enableWrapText && props.remoteDataSource && !props.treeGrid && gridRef.current && dataset.length > 0) {
      const rawRows = dataset.filter((item) => !(item as Record<string, unknown>).__isDummy);
      gridRef.current.dataView.setItems(rawRows);
    }
    handleWrapUpdate();
    if (!props.enableWrapText || !gridMounted) return;
    const sg = gridRef.current?.slickGrid;
    const dataView: SlickDataView<T> | undefined = gridRef.current?.dataView;
    if (!sg || !dataView) return;
    const originalFilter = dataView.getFilter();
    dataView.setFilter((item: T, args?: unknown) => {
      if (item.__isDummy && item.__hostRowId) {
        const hostRow = dataViewItemById(dataView, String(item.__hostRowId));
        if (hostRow) {
          return originalFilter ? originalFilter(hostRow, args) : true;
        }
        return false;
      }
      return originalFilter ? originalFilter(item, args) : true;
    });
    const onResize = makeDebounce(handleWrapUpdate, 150);

    sg.onColumnsResized.subscribe(onResize);
    return () => {
      sg.onColumnsResized.unsubscribe(onResize);
    };
  }, [props.enableWrapText, gridMounted]);

  const handleDataLoad = () => {
    if (excelExportServiceRef.current && props.remoteDataSource) {
      loadData(0, NO_OF_FETCHED_ROWS);
      return;
    }
    if (props.allowPagination) {
      loadData((currentPage - 1) * pageSize, pageSize);
    } else if (props.infiniteScroll) {
      if (excelExportServiceRef.current) {
        loadData(0, NO_OF_FETCHED_ROWS);
      } else {
        gridRef.current?.slickGrid.scrollTo(0);
        loadData(0, FETCH_SIZE);
      }
    } else {
      loadData(0, NO_OF_FETCHED_ROWS);
    }
  };

  const handleSelectAllRecords = () => {
    if (isLoadingMoreRef.current) return;
    const remaining = totalItemCountRef.current - currentLoadedCount;
    if (remaining <= 0) return;
    isLoadingMoreRef.current = true;
    selectAllAfterLoadRef.current = true;
    loadData(currentLoadedCount, remaining, true);
  };

  const getChildData = async (id: RowId | undefined): Promise<T[]> => {
    const predicate: QueryPredicate = {
      field: props.parentIdMapping || "parentId",
      operator: "equal",
      value: id,
      isComplex: false,
      ignoreCase: true,
      ignoreAccent: false,
    };

    const modifiedQuery: QueryShape = {
      ...props.query,
      where: [predicate],
    };

    if (!props.remoteDataSource) {
      return [];
    }
    const response = await callRemote(props.remoteDataSource, { value: modifiedQuery });

    // `callRemote` resolves to a `RemoteDataResponse<T>` — `{ data: { result: [...] } }`. It used to
    // be asserted to `T[] | { result: T[] }`, so the no-`beforeDataBound` path read `.result` off
    // the envelope, found nothing, and every tree child fetch came back empty.
    const items: T[] = props.beforeDataBound ? props.beforeDataBound(response) : (response.data?.result ?? []);
    const resultArray: T[] = items.map((item: T) => {
      if (!item.id) {
        return { ...item, id: item._id };
      }
      return item;
    });

    return resultArray;
  };

  const onMoveRows = async (_e: Event, args: RowMoveData & { grid: SlickGridType }): Promise<void> => {
    const { rows, insertBefore, grid } = args;

    if (gridRef.current) {
      gridRef.current.dataView.reSort();
    }

    const currentTop = topOfDragRowRef.current;
    const [row] = rows;
    if (row === undefined || insertBefore === null) return;
    let newParentId: ParentId = null;

    const gridDataView: SlickDataView<T> = grid.getData();
    const data = [...gridDataView.getFilteredItems()];
    const movedRowData = data[row];
    if (!movedRowData) return;
    const previousParentId = movedRowData.parentId;

    const droppedRowIndex = insertBefore === 0 ? null : insertBefore - 1;
    const droppedRowData = droppedRowIndex !== null ? (data[droppedRowIndex] ?? null) : null;

    const oddRowsArray = document.querySelectorAll(".slick-row.odd");
    const evenRowsArray = document.querySelectorAll(".slick-row.even");

    const droppedRowElement =
      droppedRowIndex === null ? null : getRowElement(droppedRowIndex, evenRowsArray, oddRowsArray);

    if (currentTop !== null) {
      newParentId = calculateNewParentId(currentTop, droppedRowElement, droppedRowData);
    }

    let treeData: T[] = gridRef.current ? dataViewOf(gridRef.current).getItems() : [];

    if (newParentId || insertBefore === 0) {
      setLoaderOnPageChange(true);
      const response = await rowDropRef.current?.({ parentId: newParentId || null, movedRowData });
      setLoaderOnPageChange(false);

      if (response) {
        updateParentChildRelationships(treeData, movedRowData, previousParentId, newParentId, droppedRowData);
      }
    }

    // When dragging downward, after filtering out the moved row the indices above shift down by one,
    // so the effective insertion position is one less than insertBefore.
    const adjustedInsertPosition = row < insertBefore ? insertBefore - 1 : insertBefore;
    treeData = treeData.filter((item: T) => item._id !== movedRowData._id);
    treeData.splice(adjustedInsertPosition, 0, movedRowData);

    setDataset(treeData);
    gridRef.current?.slickGrid.invalidate();
  };

  const onBeforeMoveRows = (e: Event, data: RowMoveData): boolean => {
    const target = data.insertBefore ?? null;
    setInsertBefore(target);
    setDataRow(data.rows[0] ?? null);

    if (target !== null) {
      for (const rowIndex of data.rows) {
        if (rowIndex === target - 1) {
          e.stopPropagation();
          return false;
        }
      }
    }
    return true;
  };

  const handleDrag = (): void => {
    if (!gridRef.current) return;

    const reorderGuide = document.querySelector<HTMLElement>(".slick-reorder-guide");
    if (!reorderGuide) return;

    if (dataRow === null || insertBefore === null) return;

    const datarows = [...dataViewOf(gridRef.current).getFilteredItems()];
    const currentTop = topOfDragRowRef.current;
    if (currentTop === null) return;

    const oddRowsArray = document.querySelectorAll(".slick-row.odd");
    const evenRowsArray = document.querySelectorAll(".slick-row.even");

    const movedRowElement = getRowElement(dataRow, evenRowsArray, oddRowsArray);

    const droppedRowIndex = insertBefore - 1;

    if (shouldSkipDrag(dataRow, insertBefore, droppedRowIndex)) {
      return;
    }

    const droppedRowData = datarows[droppedRowIndex] ?? null;
    const droppedRowElement = getRowElement(droppedRowIndex, evenRowsArray, oddRowsArray);

    if (!movedRowElement || !droppedRowElement) return;

    const { top: rowTop, height: rowHeight } = droppedRowElement.getBoundingClientRect();
    const midpointY = rowTop + rowHeight / 2;
    const movedRowData = datarows[dataRow];
    const tolerance = 10;

    if (!movedRowData) return;

    if (
      shouldHideReorderGuide({ currentTop, midpointY }, { droppedRowData, movedRowData }, { tolerance, insertBefore })
    ) {
      reorderGuide.style.display = "none";
      return;
    }

    reorderGuide.style.display = "block";

    if (isInMidpointRange(currentTop, midpointY, tolerance)) {
      reorderGuide.classList.add("parent-child-move");
    } else {
      reorderGuide.classList.remove("parent-child-move");
    }
  };

  const processFilterConditions = (rawConditions: Record<string, FilterCondition>): void => {
    const conditions = withResolvedFilterOptions(rawConditions);
    setAppliedFilters(Object.keys(conditions));
    setFilterConditions(conditions);
    setFilterPredicates(getFilterPredicates(conditions));
    setAppliedFilterConditions(
      Object.keys(conditions).map((cond: string) => {
        const condition = conditions[cond];
        return {
          field: cond,
          // `option` is resolved for every condition by withResolvedFilterOptions above, but only
          // to a non-empty string — not necessarily one operatorMapper has a label for, so the
          // lookup goes through operatorLabels and keeps the "" that `operator: string` promises.
          operator: condition ? (operatorLabels[condition.option] ?? "") : "",
          value: condition?.text ?? "",
          name: "",
          logic: "",
        };
      }),
    );
  };

  useEffect(() => {
    if (preventUpdateCurrentView.current) return;
    if (gridRef.current && gridMounted) {
      if (viewsLoaded) {
        handleUpdateCurrentView({ sortConditions, filterConditions, groupConditions });
      }
      // `onConditionsChange` reaches this component through SlickGridProps' catch-all index
      // signature, so it arrives as `unknown`. It is NOT promoted to a declared prop: SearchSet1,
      // its only caller, annotates the payload with the server's databank view types, whose
      // sort entries are keyed `field` where the grid emits `column` — declaring it would make
      // that pre-existing mismatch a compile error in a file this change does not own.
      const onConditionsChange = props.onConditionsChange;
      if (typeof onConditionsChange === "function") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- index-signature prop, see comment above: `typeof === "function"` is all that can be checked at runtime, and SearchSet1 is the only caller
        (onConditionsChange as (payload: ConditionsChangePayload) => void)({
          sortConditions,
          filterConditions,
          groupConditions,
        });
      }
    }
  }, [sortConditions, filterConditions, groupConditions]);

  const processColumns = (
    columns: Array<string | number>,
    enableColumnChooser: boolean,
    columnWidths?: Record<string, number>,
  ): void => {
    if (!columns.length || !enableColumnChooser) return;

    const allColumnDefinitions = gridRef.current?.gridService.getAllColumnDefinitions() as Column<T>[] | undefined;
    if (!allColumnDefinitions) return;

    // `formatter` is widened past slickgrid's own `Formatter<T>` because getFormatter returns this
    // file's formatter shape, and `groupable` / `initiallyHidden` are grid extras slickgrid's
    // `Column` does not declare. The array is asserted back at setColumns below.
    const tempColumns: Array<
      Omit<Column<T>, "formatter"> & {
        formatter?: FormatterFunction | Formatter<T>;
        groupable?: boolean;
        initiallyHidden?: boolean;
      }
    > = [];
    for (const column of columns) {
      const filteredColumn = allColumnDefinitions.filter((data: Column<T>) => data.id === column);
      const firstCol: ColumnWithValueGetters<T> | undefined = filteredColumn[0];
      if (filteredColumn.length && firstCol) {
        const originalCol = (props.columnDefinitions ?? []).find((c) => c.id === column);

        const colForFormatter = originalCol ?? readGridColumn<T>(firstCol);
        const customFormatter = getFormatter(
          originalCol ? { ...readGridColumn<T>(firstCol), ...originalCol } : colForFormatter,
        );
        if (firstCol.id === "_checkbox_selector") {
          tempColumns.push({
            ...firstCol,
            formatter: muiCheckboxFormatter as Formatter,
            sortable: false,
            filterable: false,
            groupable: false,
            minWidth: 60,
            originalWidth: 60,
            maxWidth: 60,
            name: "",
          });
        } else {
          const savedWidth = columnWidths?.[String(firstCol.id)];
          tempColumns.push({
            ...firstCol,
            initiallyHidden: false,
            ...(typeof savedWidth === "number" ? { width: savedWidth } : {}),
            ...(customFormatter ? { formatter: customFormatter } : {}),
            excelExportOptions: {
              valueParserCallback: (_dataObj: unknown, { dataContext }: { dataContext: T }) => {
                const src = originalCol ?? firstCol;
                if (typeof src._excelValueGetter === "function")
                  return normalizeExcelValue(src._excelValueGetter(dataContext));
                if (typeof src._valueGetter === "function") return normalizeExcelValue(src._valueGetter(dataContext));
                if (typeof src._valueGetter === "string")
                  return normalizeExcelValue(get(dataContext, src._valueGetter));
                return normalizeExcelValue(get(dataContext, String(firstCol.field)));
              },
            },
          });
        }
      }
    }
    gridRef.current?.slickGrid.setColumns(asSlickColumns<T>([...tempColumns]));
    gridRef.current?.slickGrid.invalidate();
  };

  // The half of a view that affects WHAT data is fetched (sort/filter/query conditions).
  // Pure React state — safe to apply before the grid exists; the resulting state changes
  // are what trigger the data-load effect, so applying this IS scheduling the fetch.
  const applyViewQueryConditions = (params: NonNullable<LayoutData["data"]>): void => {
    preventUpdateCurrentView.current = true;
    isInitialLoad.current = { filter: true, sort: true, group: true };
    setPreventUpdate(true);

    const { sortConditions = [], filterConditions = {}, queryConditions = null } = params;

    if (props.enableSortingOption) {
      setSortConditions(() => [...sortConditions]);
    }

    if (queryConditions) {
      setQueryConditions(() => ({ ...queryConditions }));
    }

    if (props.enableFilteringOption) {
      processFilterConditions(filterConditions);
    } else {
      setFilterConditions({});
    }

    setTimeout(() => {
      preventUpdateCurrentView.current = false;
    }, 0);

    onClickLayoutRef.current?.(params);

    requestAnimationFrame(() => {
      setPreventUpdate(false);
    });
  };

  // The half of a view that only affects HOW data is shown (columns/pinning/grouping).
  // Needs the mounted grid, and touches no state the data-load effect depends on —
  // applying it never causes a fetch.
  function applyViewPresentation(clickLayout: LayoutData & { isDefault?: boolean }): void {
    const params = clickLayout.data;
    if (!params || Object.keys(params).length === 0) return;

    preventUpdateCurrentView.current = true;
    setPreventUpdate(true);

    const {
      groupConditions = [],
      pinnedColumns = [],
      columns = [],
      columnWidths = {},
      rowHeight: savedRowHeight,
      enableWrapText: savedEnableWrapText,
    } = params;

    if (clickLayout.isDefault && !columns.length) {
      const defaultColumns = (props.columnDefinitions ?? [])
        .map((col: ColumnDefinition<T>) => {
          col.formatter = getFormatter(col);
          col.excelExportOptions = {
            // Priority: _excelValueGetter (parseback) > _valueGetter > raw field
            valueParserCallback: (_dataObj: unknown, { dataContext }: { dataContext: T }) => {
              if (typeof col._excelValueGetter === "function")
                return normalizeExcelValue(col._excelValueGetter(dataContext));
              if (typeof col._valueGetter === "function") return normalizeExcelValue(col._valueGetter(dataContext));
              // Support legacy string _valueGetter as a field path (graceful fallback)
              if (typeof col._valueGetter === "string") return normalizeExcelValue(get(dataContext, col._valueGetter));
              return normalizeExcelValue(get(dataContext, String(col.field)));
            },
          };
          return col;
        })
        .filter((col: ColumnDefinition<T>) => !col.initiallyHidden);
      gridRef.current?.slickGrid.setColumns(asSlickColumns<T>(defaultColumns));
    } else {
      processColumns(columns, props.enableColumnChooser ?? false, columnWidths);
    }

    if (props.enableGroupingOption) {
      setGroupConditions(() => [...groupConditions]);
    } else {
      if (pinnedColumns.length) {
        setPinnedColumns(() => {
          const next = [...pinnedColumns];
          const finalPinned = withBasePinned(next);
          reorderColumns(finalPinned);
          return finalPinned;
        });
      }
    }

    // Restore persisted row-height / wrap (CUBE-29114). Views saved before this feature lack
    // these keys, so the guards leave such views behaving exactly as they do today.
    if (typeof savedRowHeight === "number" && gridRef.current?.slickGrid) {
      const slickGrid = gridRef.current.slickGrid;
      if (slickGrid.getOptions().rowHeight !== savedRowHeight) {
        slickGrid.setOptions({ rowHeight: savedRowHeight });
        slickGrid.resizeCanvas();
        slickGrid.render();
      }
    }
    if (
      typeof savedEnableWrapText === "boolean" &&
      props.setEnableWrapText &&
      savedEnableWrapText !== !!props.enableWrapText
    ) {
      props.setEnableWrapText(savedEnableWrapText);
    }

    setTimeout(() => {
      preventUpdateCurrentView.current = false;
    }, 0);

    requestAnimationFrame(() => {
      setPreventUpdate(false);
    });
  }

  // Full view application for user-initiated view switches (grid is mounted by then).
  async function onClickLayout(clickLayout: LayoutData & { isDefault?: boolean }): Promise<void> {
    const params = clickLayout.data;
    if (!params || Object.keys(params).length === 0) return;
    applyViewPresentation(clickLayout);
    applyViewQueryConditions(params);
  }

  // Resolves the initial saved view exactly once per distinct result. Runs pre-grid-mount:
  // the query half is applied immediately (its state changes fire the single initial fetch,
  // and setViewsLoaded lands in the same batch so the load effect's null-gate is open);
  // the presentation half is deferred to the gridMounted effect below.
  const resolveInitialViews = (data: LayoutData | false): void => {
    const prev = viewsLoadedRef.current;
    if (prev !== null && (data === false || (prev && prev._id === data._id))) return;
    setViewsLoaded(data);
    const params = data ? data.data : undefined;
    if (data && params && Object.keys(params).length > 0) {
      applyViewQueryConditions(params);
    } else {
      setLoaderOnPageChange(true);
      handleDataLoad();
    }
  };

  // ViewsNewFlow re-fetches the selected layout when it mounts (with the toolbar, after the
  // first data load); resolveInitialViews dedupes that second signal by _id.
  const onViewsLoaded = (data: LayoutData | false): void => {
    resolveInitialViews(data);
  };

  // Fetch the saved view directly, at grid-component mount. It must NOT wait for ViewsNewFlow:
  // that mounts inside the toolbar, which renders only after the grid mounts, which happens
  // only after the first data load — waiting on it would deadlock the gated initial fetch.
  useEffect(() => {
    if (!viewsWillResolve) return;
    // Held on an object rather than a plain `let`: the flag is written by the cleanup closure and
    // read by the async one, which is exactly the shape control-flow analysis cannot follow.
    const fetchState = { cancelled: false };
    const viewsFetchOptions = props.viewsOptions;
    (async () => {
      let selectedLayout: LayoutData | false = false;
      try {
        const response = await axiosWrapper.post<GetSelectedViewerLayoutResponse>(
          `${getBackendUrl()}/layoutView/getSelectedViewerLayout`,
          {
            key: viewsFetchOptions?.selectedLayoutKey,
            ...(viewsFetchOptions?.fetchParams ?? {}),
          },
        );
        selectedLayout = response.data.data ?? false;
      } catch (_error) {
        console.error("Failed to load selected layout:", _error);
      }
      if (!fetchState.cancelled) resolveInitialViews(selectedLayout);
    })();
    return () => {
      fetchState.cancelled = true;
    };
  }, []);

  const handleFilterIconClick = useCallback(
    (
      // `null` for the context-menu path, which anchors off `contextEvent` instead.
      event: React.MouseEvent | MouseEvent | null,
      columnId: string,
      filterType: FilterType,
      contextEvent?: SlickEventArgs | Event,
    ): void => {
      if (contextEvent) {
        // The context-menu path hands over either the native DOM event or slickgrid's args object
        // wrapping it. Only a pointer event carries the page coordinates the popup anchors to.
        const nativeEvent = contextEvent instanceof Event ? contextEvent : contextEvent.originalEvent;
        nativeEvent?.stopPropagation();
        nativeEvent?.preventDefault();

        if (nativeEvent instanceof MouseEvent) {
          setAnchorEl({
            top: nativeEvent.pageY,
            left: nativeEvent.pageX,
          });
        }
      } else {
        const target = event?.currentTarget instanceof HTMLElement ? event.currentTarget : null;
        if (!target) return;
        const rect = target.getBoundingClientRect();

        setAnchorEl({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }

      setShowOptionDropdown(true);
      setCurrentColumnId(columnId);
      setFilterType(filterType);
    },
    [],
  );

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setShowOptionDropdown(false);
  };

  const handleOptionSelect = (selectedOption: DropdownOption): void => {
    if (!gridRef.current?.slickGrid) return;

    if (groupConditions.length && selectedOption.label === "Pin") {
      alert("Please remove grouping conditions to pin or pin columns before grouping");
      return;
    }

    if (selectedOption.label === "Pin") {
      setPinnedColumns((prev: string[]) => {
        const next = prev.includes(currentColumnId) ? prev : [...prev, currentColumnId];
        const finalPinned = withBasePinned(next);
        reorderColumns(finalPinned);
        return finalPinned;
      });
    } else if (selectedOption.label === "Unpin") {
      setPinnedColumns((prev: string[]) => {
        const next = prev.filter((id: string) => id !== currentColumnId || BASE_PINNED.includes(id));
        const finalPinned = withBasePinned(next);
        reorderColumns(finalPinned);
        return finalPinned;
      });
    } else if (selectedOption.label === "Hide") {
      const slickGrid = gridRef.current.slickGrid;
      const cols = slickGrid.getColumns();
      const updatedCols = cols.filter((col) => col.field !== currentColumnId && col.id !== currentColumnId);
      slickGrid.setColumns(updatedCols);
      if (props.enableViews && !preventUpdateCurrentView.current && viewsLoaded) {
        handleUpdateCurrentView({
          columns: updatedCols.map((data) => data.id),
        });
      }
      if (onSaveColumnsRef.current) {
        onSaveColumnsRef.current(updatedCols);
      }
    } else if (selectedOption.label === "Filter") {
      setTempCondition({
        logic: appliedFilters.length === 0 ? "" : "and",
        field: currentColumnId,
        operator: "",
        value: "",
      });
    }

    handleCloseDropdown();
  };

  const handleOnScroll = ($event: CustomEvent<{ args: ScrollEventArgs }>): void => {
    if (isLoadingMoreRef.current) return;

    const args = $event.detail.args;
    const viewportElm = args.grid.getViewportNode();

    if (!viewportElm) return;

    const isAtBottom =
      ["mousewheel", "scroll"].includes(args.triggeredBy || "") &&
      viewportElm.scrollTop > 0 &&
      Math.ceil(viewportElm.offsetHeight + args.scrollTop) >= args.scrollHeight;

    if (!isAtBottom) return;

    const startIdx = gridRef.current?.dataView.getItemCount() || 0;

    const totalLocal = filteredDataRef.current.length || props.dataset?.length || 0;

    const canLoadMore = startIdx < totalLocal;
    const isNotLast = !isLastRef.current;

    const shouldLoadMore = props.remoteDataSource ? isNotLast : canLoadMore;

    if (shouldLoadMore) {
      isLoadingMoreRef.current = true;
      setLoaderForInfiniteScroll(true);
      loadData(startIdx, FETCH_SIZE, true);
    }
  };

  const applyRowHoverFromEvent = (event: SlickEventData, isEnter: boolean): void => {
    const grid = gridRef.current?.slickGrid;
    if (!grid) return;

    const cell = grid.getCellFromEvent(event);
    const HOVER_KEY = "rowHover";
    grid.removeCellCssStyles(HOVER_KEY);

    if (isEnter && cell && cell.row >= 0) {
      // slickgrid's `Column` declares `id` required, but these are the columns this component gave
      // it, and a runtime column can have none (see ColumnWithOptionalId) — one would otherwise
      // write a style under the key "undefined" into the map handed to setCellCssStyles.
      const cols: Array<{ id?: string | number }> = grid.getColumns();
      const styles: Record<number, Record<string | number, string>> = {};
      const rowStyles: Record<string | number, string> = {};
      for (const c of cols) {
        if (c.id !== undefined) {
          rowStyles[c.id] = "row-hover";
        }
      }
      styles[cell.row] = rowStyles;
      grid.setCellCssStyles(HOVER_KEY, styles);
    }
    event.preventDefault();
  };

  // enableTreeEllipsis tooltips are covered by the delegated gridCellTooltip (it detects
  // clipped content on any .slick-cell descendant and matches the app's MUI tooltip look),
  // so tree cells no longer set the unstyled native `title` attribute.

  const onClick = async (
    e: CustomEvent<{ args: { row: number; cell: number; grid: SlickGridType }; eventData: { target: HTMLElement } }>,
    _args: SlickEventArgs,
  ): Promise<void> => {
    if (props.selectCheckBoxOnRowClick && !isEditModeRef.current) {
      const currentRow = e.detail.args.row;
      const isSelected = (gridRef.current?.slickGrid.getSelectedRows() || []).includes(currentRow);

      if (isSelected) {
        // Inside the argument list the optional call above has already established `current`.
        gridRef.current?.slickGrid.setSelectedRows(
          gridRef.current.slickGrid.getSelectedRows().filter((row: number) => row != currentRow),
        );
      } else if (gridOptions.multiSelect) {
        gridRef.current?.slickGrid.setSelectedRows([...gridRef.current.slickGrid.getSelectedRows(), currentRow]);
      } else {
        gridRef.current?.slickGrid.setSelectedRows([currentRow]);
      }
    }
    if (props.selectAtmostOneCheckBoxOnRowClick) {
      gridRef.current?.slickGrid.setSelectedRows([e.detail.args.row]);
    }

    const dataView = gridRef.current?.dataView;
    function toggleGroup(groupItem: T & { groupingKey?: string; value?: string; collapsed?: boolean }): void {
      const key = groupItem.groupingKey ?? groupItem.value;

      if (groupItem.collapsed) {
        dataView?.expandGroup(key);
      } else {
        dataView?.collapseGroup(key);
      }
    }

    const item = dataView && dataViewItemAt(dataView, e.detail.args.row);

    if (item && item.__group && !e.detail.eventData.target.classList.contains("slick-group-toggle")) {
      toggleGroup(item);
    }

    if (onClickRef.current) {
      onClickRef.current(e);
    }

    if (!props.treeGrid || e.detail.args.cell != props.dropdownColumn || props.disableChildDataFetch) {
      return;
    }

    const hasChildMapping = props.hasChildMapping;
    const clickedDataView: SlickDataView<T> = e.detail.args.grid.getData();
    const gridData = clickedDataView.getItems();
    const row = gridData[e.detail.args.row];

    if (!row || !hasChildMapping || !(row as Record<string, unknown>)[hasChildMapping] || row.isExpanded) return;

    setLoaderOnPageChange(true);
    const indentLevel = row.indentLevel ? row.indentLevel + 1 : 1;
    const data: T[] = gridRef.current ? dataViewOf(gridRef.current).getFilteredItems() : [];

    let childData = await getChildData(row.id);
    childData = childData.filter((item: T) => item._id != row._id);

    const parentIndex = data.findIndex((r: T) => r.id === row.id);

    const childRows = childData.map((child: T): T => {
      const rawHasChild = (child as Record<string, unknown>)[hasChildMapping];
      const childRow = {
        ...child,
        indentLevel,
        parentId: row.id,
        __hasChildren: Boolean(rawHasChild),
        treeLevel: indentLevel,
        __collapsed: true,
        isExpanded: false,
      };
      // `hasChildMapping` is a caller-supplied key, so it is written through the untyped view.
      (childRow as Record<string, unknown>)[hasChildMapping] = rawHasChild || false;
      return childRow;
    });

    const updatedData: T[] = [...data.slice(0, parentIndex + 1), ...childRows, ...data.slice(parentIndex + 1)];

    const parentRow = updatedData[parentIndex];
    if (parentRow) {
      updatedData[parentIndex] = { ...parentRow, isExpanded: true };
    }

    setDataset([...updatedData]);
    setLoaderOnPageChange(false);

    if (gridRef.current) {
      gridRef.current.dataset = [...dataset];
    }
  };

  // `selectedRowIds` is optional because this describes an event slickgrid dispatches; nothing
  // type-checks what actually arrives in `detail.args`.
  const onSelectedRowIdsChanged = (
    args: CustomEvent<{ args: { selectedRowIds?: RowId[]; added?: boolean } }>,
  ): void => {
    const selectedRowIds = args.detail.args.selectedRowIds ?? [];
    const dv: SlickDataView<T> | undefined = enableSearchBar ? gridRef.current?.dataView : undefined;
    if (dv) {
      if (typeof args.detail.args.added !== "undefined") {
        selectedRowIds.forEach((id) => {
          const item = dataViewItemById(dv, toDataViewId(id));
          if (item) persistedSelectedItemsRef.current.set(id, item);
        });
        const selectedIdSet = new Set<RowId>(selectedRowIds);
        [...persistedSelectedItemsRef.current.keys()].forEach((id) => {
          if (!selectedIdSet.has(id) && dataViewItemById(dv, toDataViewId(id)) !== undefined) {
            persistedSelectedItemsRef.current.delete(id);
          }
        });
      }
      setSelectedItems(selectedRowIds.filter((id) => dataViewItemById(dv, toDataViewId(id)) !== undefined).length);
    } else {
      setSelectedItems(selectedRowIds.length);
    }
    if (onSelectedRowIdsChangedRef.current) {
      // KNOWN MISMATCH: `onSelectedRowIdsChanged` is declared to take slickgrid's opaque
      // `SlickEventArgs`, but what this grid dispatches is the CustomEvent above — the consumers
      // that read `detail.args` already document the gap. Re-declaring the prop would re-check
      // every consumer that passes it — 117 files under src (`grep -rl onSelectedRowIdsChanged`).
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- declared prop type does not describe the dispatched event, see comment above
      onSelectedRowIdsChangedRef.current(args as unknown as SlickEventArgs);
    }
  };

  const reactGridReady = (reactGrid: SlickgridReactInstanceX<T>): void => {
    gridRef.current = reactGrid;

    if (enableSearchBar) {
      // `getAllSelectedItems()` is DECLARED to return `T[]`, but SlickDataView builds the array by
      // pushing `getItemById(id)` for every id still in `selectedRowIds` — `undefined` for a
      // selection kept across a refresh/filter, and group rows have no `_id` when grouping is on.
      // Reading and re-publishing it through a view that admits the holes is what makes the filter
      // below meaningful (and lets the replacement be assigned without asserting over the generic).
      const selectionApi: { getAllSelectedItems: () => Array<T | undefined> } = reactGrid.dataView;
      const originalGetAllSelectedItems = selectionApi.getAllSelectedItems.bind(reactGrid.dataView);
      selectionApi.getAllSelectedItems = () => {
        const present = originalGetAllSelectedItems().filter((item): item is T => item != null);
        const presentIds = new Set(present.map((item) => item.id));
        const preservedHidden = [...persistedSelectedItemsRef.current.values()].filter(
          (item) => !presentIds.has(item.id),
        );
        return [...present, ...preservedHidden];
      };
    }

    reactGrid.setFilterRowVisibility = (visible: boolean) => {
      reactGrid.slickGrid.setHeaderRowVisibility(visible);
    };

    // (e.g. treeGrid uses the filtering module) but the visual row is never wanted.
    reactGrid.slickGrid.setHeaderRowVisibility(false);

    type ExtensionServiceWithShared = {
      sharedService?: { externalRegisteredResources?: unknown[] };
    };
    // `ExtensionService.sharedService` is declared PROTECTED — see
    // node_modules/@slickgrid-universal/common/dist/types/services/extension.service.d.ts:15
    // (`protected readonly sharedService: SharedService`) — so the registered-resource list it
    // holds cannot be reached through the public type, even though
    // `SharedService.externalRegisteredResources` itself is public (shared.service.d.ts:39).
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- protected vendor member, see comment above
    const extService = reactGrid.extensionService as unknown as ExtensionServiceWithShared;
    // The list is declared `any[]`, so the entry is identified by a real `instanceof` against the
    // class this file imports: the resource registered here IS the `new ExcelExportService()`
    // handed to `externalResources` in gridOptions below, so the guard is exact — and it narrows,
    // where the previous `className === "ExcelExportService"` string test still needed a cast.
    const exportService = extService.sharedService?.externalRegisteredResources?.find(
      (service): service is ExcelExportService => service instanceof ExcelExportService,
    );
    if (exportService) {
      setExcelExportExtension(exportService);
    }
    const grid = reactGrid.slickGrid;

    const cols = grid.getColumns().map((col) => {
      if (col.id === "_checkbox_selector") {
        return {
          ...col,
          formatter: muiCheckboxFormatter as Formatter,
          sortable: false,
          filterable: false,
          groupable: false,
          minWidth: 60,
          originalWidth: 60,
          maxWidth: 60,
          name: "",
        };
      }
      return col;
    });

    grid.setColumns(cols);

    if (!props.disableContextMenu) {
      // Handlers are written against slickgrid's own `SlickEventData` (which carries the modifier
      // keys, the coordinates and `getNativeEvent()`), so `subscribe` types them contextually and
      // none of them needs a cast to `Handler<...>`.
      grid.onContextMenu.subscribe((e) => {
        e.preventDefault();

        const cell = grid.getCellFromEvent(e);

        if (!cell) return;

        const columns: Array<Column & { disableMenuOptions?: boolean; filterType?: FilterType }> = grid.getColumns();

        const column = columns[cell.cell];

        if (!column || column.disableMenuOptions) return;
        handleFilterIconClick(null, column.field, column.filterType || "Text", e.getNativeEvent());
      });
    }

    slickEventHandler.subscribe(grid.onMouseEnter, (e) => {
      applyRowHoverFromEvent(e, true);
    });
    slickEventHandler.subscribe(grid.onMouseLeave, (e) => {
      applyRowHoverFromEvent(e, false);
    });
    if (props.treeGrid) {
      slickEventHandler.subscribe(grid.onKeyDown, (e) => {
        const isSpace = e.key === " " || e.key === "Spacebar" || e.keyCode === 32;
        if (!isSpace || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
        const toggleElm = grid
          .getActiveCellNode()
          ?.querySelector<HTMLElement>(".slick-group-toggle.collapsed, .slick-group-toggle.expanded");
        if (!toggleElm) return;
        e.stopImmediatePropagation();
        e.preventDefault();
        toggleElm.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
      });
    }

    updatePreHeaderVisibility(grid);

    if (onReactGridCreatedRef.current) {
      (reactGrid as SlickgridReactInstance & { applyConditions?: (data: LayoutData) => void }).applyConditions = (
        data: LayoutData,
      ) => onClickLayout({ data, isDefault: false });
      onReactGridCreatedRef.current(reactGrid);
    }

    grid.onColumnsReordered.subscribe((e, args) => {
      const cols = [...(gridRef.current?.slickGrid.getColumns() ?? [])];
      const checkboxColumn = cols.find((c) => c.id === "_checkbox_selector");
      const moveColumn = cols.find((c) => c.id === "_move");
      const corrected = [
        ...(checkboxColumn ? [checkboxColumn] : []),
        ...(moveColumn ? [moveColumn] : []),
        ...cols.filter((c) => !["_checkbox_selector", "_move"].includes(String(c.id))),
      ];

      gridRef.current?.slickGrid.setColumns(corrected);

      if (!preventUpdateCurrentView.current && viewsLoadedRef.current) {
        handleUpdateCurrentView({
          columns: corrected.map((data) => data.id),
        });
      }

      onColumnsReorderedRef.current?.(e.getNativeEvent(), args);
    });

    // Persist column widths into the current view on user resize (CUBE-29114). Only real drags
    // carry a truthy triggeredByColumn; the wrap-text recalc fires a notify with "" and must be
    // ignored so it never marks the view dirty (which would even fork a default view).
    grid.onColumnsResized.subscribe((_e, args) => {
      if (!args.triggeredByColumn) return;
      if (!preventUpdateCurrentView.current && viewsLoadedRef.current) {
        handleUpdateCurrentView({ columnWidths: getColumnWidthsSnapshot() });
      }
    });

    setGridMounted(true);
    grid.invalidate();

    // requestAnimationFrame(() => updateAutoSizeColumns());

    if (!props.disableCustomSorting) {
      grid.onSort.subscribe(() => {
        /* intentionally empty */
      });
    }

    specialColumnRef.current = grid
      .getColumns()
      .filter((column) => ["_checkbox_selector", "_move"].includes(String(column.id)))
      .map((col) => ({
        ...col,
        sortable: false,
        filterable: false,
        groupable: false,
        reorderable: false,
        selectable: false,
      }));
  };

  useEffect(() => {
    isEditModeRef.current = props.isEditMode ?? false;
    if (props.isEditMode) {
      const currentRows = gridRef.current?.slickGrid.getSelectedRows() ?? [];

      // For "Add" flows, addRecord already sets editingRowIndexRef directly,
      // so only default to row 0 if it hasn't been set yet.
      if (editingRowIndexRef.current == null) {
        editingRowIndexRef.current = currentRows.length > 0 ? (currentRows[0] ?? 0) : 0;
      }
      if (currentRows.length === 0) {
        gridRef.current?.slickGrid.setSelectedRows([editingRowIndexRef.current]);
      }
    } else {
      editingRowIndexRef.current = null;
    }
  }, [props.isEditMode, gridRef]);

  useEffect(() => {
    preventUpdateCurrentView.current = preventUpdate;
  }, [preventUpdate]);

  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(totalItemCount / pageSize)));
  }, [pageSize, totalItemCount]);

  useEffect(() => {
    setCurrentPage(1);
  }, [totalPages]);

  useEffect(() => {
    stateRef.current = getStateSnapshot();
  }, [
    filterPredicates,
    filterConditions,
    sortConditions,
    appliedFilters,
    currentPage,
    pageSize,
    groupConditions,
    pinnedColumns,
    dataset,
    props.disableMenuOptions,
    props.enableWrapText,
    props.isEditMode,
    props.infiniteScroll,
    props.allowPagination,
  ]);

  useEffect(() => {
    let cssClass = " ";
    if (props.disableHeader) {
      cssClass = cssClass + " " + "disable-header";
    }
    if (props.disableMarginForNoData) {
      cssClass = cssClass + " " + "disable-margin-no-data";
    }
    if (enableSelectAllCheckbox && !props.disableCheckBoxSelection) {
      cssClass = cssClass + " " + "enable-selectall-checkbox";
    }
    if (props.enableWrapText) {
      cssClass = cssClass + " " + "enable-wrap-text";
    }
    if (props.disableRowBorder) {
      cssClass = cssClass + " " + "disable-row-border";
    }
    if (props.disableColumnBorder) {
      cssClass = cssClass + " " + "disable-column-border";
    }

    if (props.enableTreeEllipsis) cssClass = cssClass + " " + "tree-row-ellipsis";

    setCssClasses(cssClass);
  }, [
    props.disableHeader,
    props.disableMarginForNoData,
    enableSelectAllCheckbox,
    props.disableCheckBoxSelection,
    props.enableWrapText,
    props.disableRowBorder,
    props.disableColumnBorder,
    props.enableTreeEllipsis,
  ]);

  useEffect(() => {
    if (props.columnDefinitions?.length) {
      setAllPrerequisiteLoaded(true);
    }
  }, [dataset, props.columnDefinitions]);

  useEffect(() => {
    if (props.infiniteScroll) {
      setCurrentLoadedCount(dataset.length);
    }
  }, [dataset.length, props.infiniteScroll]);

  useEffect(() => {
    totalItemCountRef.current = totalItemCount;
  }, [totalItemCount]);

  // Local grids that mutate the dataView directly (e.g. add/delete rows) never
  // re-run loadData, so totalItemCount goes stale and the footer count is wrong.
  // For non-paginated, non-infinite, flat local datasets the dataView holds the
  // full set, so keep totalItemCount in sync with its live data-row count.
  // getItems() is used (not getLength) so grouping headers do not inflate the
  // count. A dataset-backed tree builds its total separately (processLocalDataset via props.dataset), so it
  // is excluded — but a surpassedDataset-backed tree has no such path (loadData's local branch needs
  // props.dataset), so it must sync from the dataView here too, else totalItemCount stays at its seed value.
  useEffect(() => {
    if (
      props.allowPagination ||
      props.infiniteScroll ||
      props.remoteDataSource ||
      (props.treeGrid && !(props.surpassedDataset && props.surpassedDataset.length > 0))
    )
      return;
    const dv = gridRef.current?.dataView;
    if (!dv) return;

    const syncTotalItemCount = () => setTotalItemCount(dv.getItems().length);
    syncTotalItemCount();
    dv.onRowCountChanged.subscribe(syncTotalItemCount);

    return () => {
      dv.onRowCountChanged.unsubscribe(syncTotalItemCount);
    };
  }, [
    props.allowPagination,
    props.infiniteScroll,
    props.remoteDataSource,
    props.treeGrid,
    props.surpassedDataset,
    gridMounted,
    gridRef,
  ]);

  useEffect(() => {
    if (!selectAllAfterLoadRef.current) return;

    const realCount = dataset.filter((r) => !r.__isDummy).length;
    const isAllLoaded = isLastRef.current || realCount >= totalItemCountRef.current;

    if (!isAllLoaded) return;

    const grid = gridRef.current?.slickGrid;
    if (grid) {
      const rowCount = grid.getDataLength();
      grid.setSelectedRows(Array.from({ length: rowCount }, (_, i) => i));
    }
    selectAllAfterLoadRef.current = false;
  }, [dataset, gridRef]);

  useEffect(() => {
    return () => {
      gridRef.current = null;
    };
  }, [gridRef]);

  useEffect(() => {
    if (!gridRef.current) return;

    gridRef.current.addRecord = (rowToAdd: T, position = 0): void => {
      const instance = gridRef.current;
      if (!instance) return;
      const currentItems = [...dataViewOf(instance).getItems()];

      if (position < 0 || position > currentItems.length) {
        position = currentItems.length;
      }
      if (rowToAdd.parentId) {
        const parent = currentItems.find(
          (item: T) => (item as Record<string, unknown>)[props.idMapping ?? "id"] === rowToAdd.parentId,
        );
        if (parent) {
          parent.__hasChildren = true;
          parent.hasChild = true;
          parent.__collapsed = false;
          parent.isExpanded = true;
        }
      }
      const updatedItems = [...currentItems.slice(0, position), rowToAdd, ...currentItems.slice(position)];

      // Set editing row index before dataset update so that when isEditMode

      editingRowIndexRef.current = position;
      setDataset(updatedItems);

      setTimeout(() => {
        if (gridRef.current) {
          const grid = gridRef.current.slickGrid;
          grid.setSelectedRows([position]);
        }
      }, 0);
    };

    gridRef.current.removeRecord = (position = 0, rowToRemove): void => {
      const instance = gridRef.current;
      if (!instance) return;
      let updatedItems: T[] = structuredClone(dataViewOf(instance).getItems());

      if (rowToRemove) {
        updatedItems = updatedItems.filter((item) => item.id !== rowToRemove.id);
        if (rowToRemove.parentId) {
          const parentStillHasChildren = updatedItems.some((item: T) => item.parentId === rowToRemove.parentId);
          if (!parentStillHasChildren) {
            const parent = updatedItems.find(
              (item: T) => (item as T & { itemId?: unknown }).itemId === rowToRemove.parentId,
            );
            if (parent) {
              parent.__hasChildren = false;
              parent.hasChild = false;
              parent.isExpanded = false;
            }
          }
        }

        setDataset(updatedItems);
      } else if (position >= 0 && position < updatedItems.length) {
        updatedItems.splice(position, 1);
        setDataset(updatedItems);
      }
    };
  });

  useEffect(() => {
    if (viewsLoaded === null) return;
    setLoaderOnPageChange(true);
    handleDataLoad();
  }, [
    pageSize,
    currentPage,
    props.query,
    props.dataset,
    sortConditions,
    appliedFilters,
    filterConditions,
    filterPredicates,
    props.enableWrapText,
    props.infiniteScroll,
    searchTerm,
  ]);

  useEffect(() => {
    stateRef.current = { appliedFilterConditions, sortConditions, groupConditions };
  }, [appliedFilterConditions, sortConditions, groupConditions]);

  const stateRefvar = useRef({ appliedFilterConditions, sortConditions, groupConditions });

  const checkActiveState = useCallback((col: Column): boolean => {
    const { appliedFilterConditions, sortConditions, groupConditions } = stateRefvar.current;
    const isFiltered = appliedFilterConditions.some(
      (f: AppliedFilterCondition & { column?: string }) =>
        removeLastIndex(f.field) === col.field || removeLastIndex(f.column || "") === col.field,
    );
    const isSorted = sortConditions.some((s: SortCondition) => s.column === col.field);
    const isGrouping = groupConditions.some((g: GroupCondition) => g.column === col.field);
    return isFiltered || isSorted || isGrouping;
  }, []);

  const updateHeaderClasses = useCallback((): void => {
    if (!gridRef.current) return;
    const grid = gridRef.current.slickGrid;

    const headerNodes = document.querySelectorAll(".slick-header-column");

    headerNodes.forEach((node: Element) => {
      if (!(node instanceof HTMLElement)) return;
      const htmlNode = node;
      const columnId =
        htmlNode.getAttribute("id")?.replace(grid.getUID(), "").replace("slickgrid_", "") || htmlNode.dataset.id;
      if (!columnId) return;

      const col = grid.getColumns().find((c) => c.id === columnId);
      if (!col) return;

      const columnNameSpan = htmlNode.querySelector(".slick-column-name");
      if (!columnNameSpan) return;

      const isActive = checkActiveState(col);

      if (isActive) {
        columnNameSpan.classList.add("header-active");
      } else {
        columnNameSpan.classList.remove("header-active");
      }
    });
  }, [checkActiveState, gridRef]);

  useEffect(() => {
    stateRefvar.current = { appliedFilterConditions, sortConditions, groupConditions };
    updateHeaderClasses();
  }, [appliedFilterConditions, sortConditions, groupConditions, updateHeaderClasses]);

  useEffect(() => {
    if (!gridMounted || !gridRef.current) return;
    const grid = gridRef.current.slickGrid;

    // Typed against slickgrid's own event args (with this file's header-renderer extras stated as
    // optional additions), so subscribe/unsubscribe take them as-is.
    const renderHeaderTemplate = (
      _e: SlickEventData<OnHeaderCellRenderedEventArgs>,
      args: OnHeaderCellRenderedEventArgs,
    ): void => {
      const col: Column & {
        _customHeaderRenderer?: () => string | HTMLElement;
        headerRenderer?: () => string | HTMLElement;
      } = args.column;
      const node = args.node;

      if (args.column.id === "_checkbox_selector" && enableSelectAllCheckbox) {
        const headerNode = args.node;

        headerNode.innerHTML = "";

        const container = document.createElement("div") as HTMLDivElement;
        container.style.height = "100%";
        container.style.display = "flex";
        container.style.justifyContent = "center";
        container.style.alignItems = "center";

        headerNode.appendChild(container);

        createRoot(container).render(<HeaderCheckbox grid={grid} />);
        return;
      }

      const columnNameSpan = node.querySelector(".slick-column-name");

      if (!columnNameSpan) return;

      if (col.headerRenderer || col._customHeaderRenderer) {
        const content = col._customHeaderRenderer ? col._customHeaderRenderer() : col.headerRenderer?.();

        if (typeof content === "string") {
          columnNameSpan.innerHTML = content;
        } else if (content instanceof Node) {
          columnNameSpan.innerHTML = "";
          columnNameSpan.appendChild(content);
        }
      }

      const isActive = checkActiveState(col);
      if (isActive) {
        columnNameSpan.classList.add("header-active");
      } else {
        columnNameSpan.classList.remove("header-active");
      }
    };

    const handleHeaderMouseEnter = (_e: SlickEventData<OnHeaderMouseEventArgs>, args: OnHeaderMouseEventArgs): void => {
      const column = args.column;

      const headerNode = headerColumnOf(grid, column.id);

      if (!headerNode) return;

      const textContainer =
        headerNode.querySelector(".custom-header") || headerNode.querySelector(".slick-column-name");

      const container = textContainer?.children[1];
      if (container instanceof HTMLElement) {
        const isTruncated = container.scrollWidth > container.clientWidth;

        if (isTruncated) {
          const nameStr = typeof column.name === "string" ? column.name : "";
          container.setAttribute("aria-label", nameStr);
        } else {
          container.removeAttribute("aria-label");
        }
      }
    };

    grid.onHeaderCellRendered.subscribe(renderHeaderTemplate);
    grid.onHeaderMouseEnter.subscribe(handleHeaderMouseEnter);

    // Render the header checkbox immediately for the already-rendered header cell.
    // onHeaderCellRendered fires during setColumns() which happens before gridMounted
    // is set to true, so the subscription above misses the initial render.
    const existingHeaderNode = headerColumnOf(grid, "_checkbox_selector");
    if (existingHeaderNode) {
      existingHeaderNode.innerHTML = "";

      const container = document.createElement("div") as HTMLDivElement;
      container.style.height = "100%";
      container.style.display = "flex";
      container.style.justifyContent = "center";
      container.style.alignItems = "center";

      existingHeaderNode.appendChild(container);

      createRoot(container).render(<HeaderCheckbox grid={grid} />);
    }

    updateHeaderClasses();

    return () => {
      grid.onHeaderCellRendered.unsubscribe(renderHeaderTemplate);
      grid.onHeaderMouseEnter.unsubscribe(handleHeaderMouseEnter);
    };
  }, [gridMounted]);

  useEffect(() => {
    if (!gridRef.current) return;
    gridRef.current.applyGrouping = applyGrouping;
    gridRef.current.gridState = getStateSnapshot();
    gridRef.current.refresh = () => {
      gridRef.current?.slickGrid.setSelectedRows([]);
      handleDataLoad();
    };

    gridRef.current.resetPagination = (skipApiCall = false): void => {
      nextPageTokenRef.current = null;
      isLastRef.current = false;
      isLoadingMoreRef.current = false;
      if (!skipApiCall) {
        if (props.infiniteScroll) {
          gridRef.current?.slickGrid.scrollTo(0);
          loadData(0, FETCH_SIZE);
        } else if (props.allowPagination) {
          setCurrentPage(1);
          loadData(0, pageSize);
        } else {
          loadData(0, NO_OF_FETCHED_ROWS);
        }
      } else if (props.allowPagination) {
        setCurrentPage(1);
      }
    };

    gridRef.current.suppressViewUpdates = (fn: () => void) => {
      preventUpdateCurrentView.current = true;
      try {
        fn();
      } finally {
        setTimeout(() => {
          preventUpdateCurrentView.current = false;
        }, 0);
      }
    };

    gridRef.current.toolbarModule = {
      enableItems: (ids: string[], enabled: boolean) => {
        setToolbarDisabledOverrides((prev) => {
          const next = { ...prev };
          for (const id of ids) {
            next[id] = !enabled;
          }
          return next;
        });
      },
    };

    gridRef.current.currentViewData = dataViewOf(gridRef.current).getItems();
  });

  useEffect(() => {
    if (props.treeGrid && !props.disableTreeCollapseOnDataChange && !props.initiallyExpanded) {
      gridRef.current?.treeDataService.toggleTreeDataCollapse(!isTreeFilterOrSearchActive());
    }
  }, [dataset, gridRef, props.disableTreeCollapseOnDataChange, props.initiallyExpanded, props.treeGrid]);

  // ── disabledIds support ──────────────────────────────────────────────
  // Port from old SlickGrid: apply a CSS class to every cell in disabled rows
  // so they appear greyed-out and non-interactive.
  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    const dv = gridRef.current?.dataView;
    if (!grid || !dv) return;

    const KEY = "disabled-rows";

    const applyDisabledStyles = () => {
      grid.removeCellCssStyles(KEY);

      const cols = grid.getColumns();
      const styles: Record<number, Record<string, string>> = {};

      // Rows disabled via disabledIds prop
      if (Array.isArray(props.disabledIds)) {
        for (const id of props.disabledIds) {
          const rowIdx = dv.getRowById(String(id));
          if (rowIdx == null || rowIdx < 0) continue;

          styles[rowIdx] = {};
          for (const col of cols) {
            styles[rowIdx]![String(col.id)] = "slick-disabled-row";
          }
        }
      }

      // Rows disabled via isRowDisabled flag in row data
      const items: GridRowBase[] = dv.getItems();
      for (const item of items) {
        if (item.isRowDisabled) {
          const rowIdx = dv.getRowById(String(item.id ?? item._id));
          if (rowIdx == null || rowIdx < 0) continue;

          if (!styles[rowIdx]) {
            styles[rowIdx] = {};
            for (const col of cols) {
              styles[rowIdx]![String(col.id)] = "slick-disabled-row";
            }
          }
        }
      }

      if (Object.keys(styles).length) {
        grid.setCellCssStyles(KEY, styles);
      }
    };

    applyDisabledStyles();

    const rerun = () => applyDisabledStyles();
    dv.onRowsChanged.subscribe(rerun);
    dv.onRowCountChanged.subscribe(rerun);

    return () => {
      grid.removeCellCssStyles(KEY);
      dv.onRowsChanged.unsubscribe(rerun);
      dv.onRowCountChanged.unsubscribe(rerun);
    };
  }, [props.disabledIds, gridMounted, dataset, gridRef]);

  useEffect(() => {
    if (props.dragAndDrop) {
      let intervalId: ReturnType<typeof setInterval> | null = null;
      let lastTop: number | null = null;

      const checkPosition = (): void => {
        const element = document.querySelector(".slick-reorder-proxy") as HTMLElement | null;
        if (!element) return;

        const { top } = element.getBoundingClientRect();

        if (top !== lastTop) {
          topOfDragRowRef.current = top;
          setTop(top);
          lastTop = top;
        }
      };

      const observer = new MutationObserver(() => {
        const element = document.querySelector(".slick-reorder-proxy");
        if (element && !intervalId) {
          intervalId = setInterval(checkPosition, 100);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      return () => {
        observer.disconnect();
        if (intervalId) clearInterval(intervalId);
      };
    }
  }, [props.dragAndDrop]);

  useEffect(() => {
    if (props.enableWrapText && gridRef.current) {
      // For remote data sources, the useEffect that calls dataView.setItems() is gated behind
      // !enableWrapText and never runs. Populate the dataView with raw rows here so that
      // handleWrapUpdate (triggered below via onColumnsResized) can read and process them.
      if (props.remoteDataSource && !props.treeGrid && dataset.length > 0) {
        const rawRows = dataset.filter((item) => !(item as Record<string, unknown>).__isDummy);
        gridRef.current.dataView.setItems(rawRows);
      }
      // Synthetic resize notification used purely to re-trigger the wrap recalculation. It carries
      // slickgrid's declared args (`grid` + an empty `triggeredByColumn`, which the persist handler
      // reads as "not a user drag") plus the `resizeHandle` the library's own listeners look for.
      const resizedArgs: OnColumnsResizedEventArgs & { resizeHandle: HTMLElement } = {
        grid: gridRef.current.slickGrid,
        triggeredByColumn: "",
        resizeHandle: document.createElement("div"),
      };
      gridRef.current.slickGrid.onColumnsResized.notify(resizedArgs);
    }
  }, [dataset, props.isEditMode, gridRef, props.enableWrapText, props.remoteDataSource, props.treeGrid]);

  useEffect(() => {
    handleDrag();
  }, [top]);

  useEffect(() => {
    // The saved view's query conditions were already applied when views resolved (pre-mount,
    // driving the single initial fetch); once the grid exists, apply the parts that need it.
    if (!gridMounted || !viewsLoaded) return;
    applyViewPresentation(viewsLoaded);
  }, [gridMounted, viewsLoaded]);

  useEffect(() => {
    if (!gridRef.current) return;

    // Skip if neither column definitions nor isEditMode have changed (prevents unnecessary re-renders).
    // When in edit mode we never skip: editorTemplate closures may have captured fresh state even
    // when column IDs are identical, and we need setColumns+invalidate to pick them up.
    const serializedColIds = JSON.stringify(
      props.columnDefinitions?.map((c) => [c.id, !!(c as { initiallyHidden?: boolean }).initiallyHidden]),
    );
    const isEditModeChanged = prevIsEditModeRef.current !== props.isEditMode;
    if (serializedColIds === prevColumnIdsRef.current && !isEditModeChanged && gridMounted && !props.isEditMode) return;
    prevColumnIdsRef.current = serializedColIds;
    prevIsEditModeRef.current = props.isEditMode;

    // Built on slickgrid's own `Column` (not `Column<T>`) with this file's column extras stated as
    // optional additions, so `getColumns()` assigns straight into it.
    type ExtendedColumn = Omit<Column, "formatter"> & {
      // `formatter` is widened past slickgrid's own `Formatter` because getFormatter returns this
      // file's formatter shape; `groupable` / `initiallyHidden` are grid extras `Column` does not
      // declare. Everything is handed back to setColumns through asSlickColumns.
      formatter?: FormatterFunction | Formatter;
      groupable?: boolean;
      initiallyHidden?: boolean;
      disableMenuOptions?: boolean;
      formatterType?: string;
      filterType?: string;
      _valueGetter?: ((ctx: T) => unknown) | string;
      _excelValueGetter?: (ctx: T) => unknown;
      editorTemplate?: (args: { value: unknown; updatedColumn: Column; args: T }) => React.ReactNode;
    };
    const allGridColumns: ExtendedColumn[] = gridRef.current.slickGrid.getColumns();
    const filteredColumns = allGridColumns.filter(
      (col) => !["_checkbox_selector", "_move"].includes(String(col.id)) && !col.initiallyHidden,
    );
    const columnDefinitions = filteredColumns.map((column: ExtendedColumn) => {
      let updatedColumn: ExtendedColumn = {
        ...column,
        sortable: !props.isEditMode,
        columnGroup: column.columnGroup ?? "Standard Fields",
        width: column.width ?? 200,
        originalWidth: column.originalWidth ?? 200,
        excelExportOptions: {
          // Priority: _excelValueGetter (parseback) > _valueGetter > raw field
          valueParserCallback: (_dataObj: unknown, { dataContext }: { dataContext: T }) => {
            if (typeof column._excelValueGetter === "function")
              return normalizeExcelValue(column._excelValueGetter(dataContext));
            if (typeof column._valueGetter === "function") return normalizeExcelValue(column._valueGetter(dataContext));
            // Support legacy string _valueGetter as a field path (graceful fallback)
            if (typeof column._valueGetter === "string")
              return normalizeExcelValue(get(dataContext, column._valueGetter));
            return normalizeExcelValue(get(dataContext, column.field));
          },
        },
      };

      // These columns come straight off the grid, so they are the very `ColumnDefinition<T>` this
      // component mounted it with — read back through the declared boundary (see readGridColumn).
      const columnAsDefinition = readGridColumn<T>(column);
      if (props.isEditMode && typeof column.editorTemplate === "function") {
        const editModeFormatter: FormatterFunction = (row, cell, value, columnDef, dataContext, grid) => {
          if (!dataContext) return "";
          if (editingRowIndexRef.current === row) {
            return renderEditorTemplate(columnAsDefinition, dataContext);
          }
          const formatter = getFormatter(columnAsDefinition);
          return formatter ? formatter(row, cell, value, columnDef, dataContext, grid) : "";
        };
        updatedColumn.formatter = editModeFormatter;
      } else {
        updatedColumn.formatter = getFormatter(columnAsDefinition);
      }

      // Auto-assign header icon if none exists
      type ColWithRenderers = ExtendedColumn & {
        headerRenderer?: () => string;
        headerTemplate?: unknown;
      };
      if (
        !props.disableAutoHeaderIcons &&
        !(column as ColWithRenderers).headerRenderer &&
        !(column as ColWithRenderers).headerTemplate
      ) {
        // `inferIconForColumn` only reads these four members, and the spread of a whole column is
        // not assignable to `FormatterColDef` (its `_valueGetter` takes an untyped row).
        const iconKey = inferIconForColumn({
          field: column.field,
          formatterType: column.formatterType,
          filterType: column.filterType,
          name: typeof column.name === "string" ? column.name : undefined,
        });
        if (iconKey) {
          (updatedColumn as ColWithRenderers).headerRenderer = () =>
            renderToStaticMarkup(<HeaderWithIcon icon={iconKey} label={String(column.name || "")} />);
        }
      }

      const isSpecialColumn = ["_checkbox_selector", "_move"].includes(String(column.id));
      if (isSpecialColumn || column.disableMenuOptions || props.isEditMode || props.disableMenuOptions)
        return updatedColumn;

      const buttoncssClass = "mdi mdi-dots-vertical text-slate-900 inline-block  cursor-pointer mt-0";

      const headerConfig: ColumnHeader = {
        buttons: [
          {
            cssClass: buttoncssClass,
            command: "toggle-highlight",
            tooltip: String(column.id),
          },
        ],
      };

      updatedColumn = {
        ...updatedColumn,
        header: headerConfig,
        sortable: true,
        groupable: true,
        filterable: true,
        type: "string",
      };

      updatedColumn.headerCssClass = (updatedColumn.headerCssClass || "") + " custom-header-column";

      return updatedColumn;
    });

    const tempCols = columnDefinitions.map((col: ExtendedColumn) => ({
      ...col,
      hidden: false,
    }));
    let columns: ExtendedColumn[] = groupConditions.length > 0 ? tempCols : columnDefinitions;
    {
      const grid = gridRef.current.slickGrid;
      const allCols = columns;

      const pinned = allCols.filter(
        (col: ExtendedColumn) => pinnedColumns.includes(String(col.id)) || pinnedColumns.includes(col.field),
      );

      const unpinned = allCols.filter(
        (col: ExtendedColumn) => !pinnedColumns.includes(String(col.id)) && !pinnedColumns.includes(col.field),
      );
      columns = [...pinned, ...unpinned];
      // CUBE-24186: edit mode calls setColumns WITHOUT the special columns (see just below), so they
      // must not be counted here either - otherwise frozenColumn points one column too far right.
      // When the column it lands on is `hidden`, the frozen header pane renders no cells, measures
      // 0 high, and slickgrid positions the row pane at top: 0 (behind the opaque header) - the
      // rows, including the one being renamed, vanish. The three other frozenColumn writers in this
      // file already use `pinned.length - 1` with no special-column term.
      const specialColumnCount = props.isEditMode ? 0 : specialColumnRef.current.length;
      if (pinned.length + specialColumnCount > 0 && !groupConditions.length) {
        grid.setOptions({ frozenColumn: pinned.length + specialColumnCount - 1 });
      } else {
        grid.setOptions({ frozenColumn: -1 });
      }
    }
    if (gridMounted) {
      if (props.isEditMode) {
        gridRef.current.slickGrid.setColumns(asSlickColumns<T>(columns));
      } else {
        gridRef.current.slickGrid.setColumns(asSlickColumns<T>([...specialColumnRef.current, ...columns]));
      }
      // Invalidate after setColumns so rows re-render with the new (editor) formatters
      gridRef.current.slickGrid.invalidate();
      gridRef.current.slickGrid.render();
    }
  }, [props.isEditMode, props.enableWrapText, pinnedColumns, gridMounted, props.columnDefinitions]);

  const isNormalizingRef = useRef(false);
  const prevColumnIdsRef = useRef<string>("");
  const prevIsEditModeRef = useRef<boolean | undefined>(undefined);

  const normalizeColumns = (columns: Column[]): Column[] => {
    return columns.map((column: Column & { disableMenuOptions?: boolean }) => {
      const isSpecialColumn = ["_checkbox_selector", "_move"].includes(String(column.id));
      if (
        isSpecialColumn ||
        column.disableMenuOptions ||
        stateRef.current?.isEditMode ||
        stateRef.current?.disableMenuOptions
      )
        return column;

      const buttoncssClass = "mdi mdi-dots-vertical text-slate-900 inline-block cursor-pointer mt-0";

      return {
        ...column,
        header: column.header ?? {
          buttons: [
            {
              cssClass: buttoncssClass,
              command: "toggle-highlight",
              tooltip: String(column.id),
            },
          ],
        },
        headerCssClass: (column.headerCssClass || "") + " custom-header-column",
        sortable: true,
        groupable: true,
        filterable: true,
        type: "string",
      };
    });
  };

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.appliedFilters = appliedFilters;
      if (onFilterChangeRef.current && appliedFilters.length > 0) {
        onFilterChangeRef.current(appliedFilters, filterConditions);
      }
    }
  }, [appliedFilters, filterConditions, gridRef]);

  useEffect(() => {
    if (props.preAppliedFilters && Object.keys(props.preAppliedFilters).length > 0) {
      // `preAppliedFilters` is the SECOND saved-view entry point (the first is
      // processFilterConditions): callers build it straight from a stored layout's
      // `filterConditions` — server JSON that is not validated on the way in — so it goes through
      // the same normaliser. Without this the `option` every consumer below reads off a
      // FilterCondition really can be missing, and applyFiltersToData/getUpdatedFilterConditions
      // would silently filter with an undefined operator.
      const resolved = withResolvedFilterOptions(props.preAppliedFilters);
      setAppliedFilters(Object.keys(resolved));
      setFilterConditions(resolved);
    }
  }, [props.preAppliedFilters]);

  useEffect(() => {
    if (props.showLoader !== undefined) {
      setLoaderOnPageChange(props.showLoader);
    }
  }, [props.showLoader]);

  useEffect(() => {
    if (gridRef.current?.deleteRow) {
      gridRef.current.deleteRow = false;
      gridRef.current.slickGrid.setSelectedRows([]);
    }
  }, [gridRef.current?.deleteRow, gridRef]);

  useEffect(() => {
    if (!gridRef.current || !gridMounted) return;

    const slickGrid = gridRef.current.slickGrid;
    const dataView = dataViewOf(gridRef.current);

    const dataToLoad = props.surpassedDataset && props.surpassedDataset.length > 0 ? props.surpassedDataset : dataset;

    // Allow dataView updates for:
    // - Non-tree grids
    // - Tree grids with surpassedDataset
    const shouldUpdateDataView = !props.enableWrapText && !(props.treeGrid && !props.surpassedDataset);

    if (shouldUpdateDataView) {
      dataView.setItems(dataToLoad);

      // After setItems, re-sort so appended infinite-scroll rows stay in order
      if (props.infiniteScroll) {
        dataView.reSort();
      }

      dataView.getItemMetadata = (rowIndex: number): ItemMetadata | null => {
        const item = dataViewItemAt(dataView, rowIndex);
        if (!item) return null;
        const groupProvider =
          gridRef.current?.slickGrid.getPluginByName<GroupItemMetadataProvider>("GroupItemMetadataProvider");
        if (item.__group) {
          return groupProvider?.getGroupRowMetadata(item) ?? null;
        }
        if (item.__groupTotals) {
          return groupProvider?.getTotalsRowMetadata(item) ?? null;
        }
        return null;
      };

      slickGrid.invalidate();

      if (restoreSelectionAfterSearchRef.current) {
        restoreSelectionAfterSearchRef.current = false;
        const staleIds = dataView
          .getAllSelectedIds()
          .filter((sid: string | number) => dataViewItemById(dataView, sid) === undefined);
        if (staleIds.length) {
          dataView.setSelectedIds(staleIds, {
            isRowBeingAdded: false,
            shouldTriggerEvent: false,
            applyRowSelectionToGrid: false,
          });
        }
        const presentIds = [...persistedSelectedItemsRef.current.keys()]
          .map((rid) => toDataViewId(rid))
          .filter((rid) => dataViewItemById(dataView, rid) !== undefined);
        if (presentIds.length) {
          dataView.setSelectedIds(presentIds, { isRowBeingAdded: true });
          const rows = presentIds
            .map((rid) => dataView.getRowById(String(rid)))
            .filter((r): r is number => typeof r === "number" && r >= 0);
          slickGrid.setSelectedRows(rows);
        } else {
          slickGrid.setSelectedRows([]);
        }
      }
    }
  }, [
    props.surpassedDataset,
    gridMounted,
    dataset,
    props.dataset,
    props.enableWrapText,
    gridRef,
    props.infiniteScroll,
    props.treeGrid,
  ]);

  useEffect(() => {
    const grid = gridRef.current?.slickGrid;
    if (!grid) return;

    const scrollHandler = (_e: SlickEventData<OnScrollEventArgs>, args: OnScrollEventArgs): void => {
      if (
        !gridRef.current ||
        stateRef.current?.loaderForInfiniteScroll ||
        stateRef.current?.loaderOnPageChange ||
        !stateRef.current?.allPrerequisiteLoaded
      ) {
        return;
      }

      const { scrollLeft } = args;
      if (gridRef.current._lastScrollLeft === scrollLeft) {
        return;
      }
      gridRef.current._lastScrollLeft = scrollLeft;
      const gridContainer: ParentNode = args.grid.getContainerNode();

      const frozenPanes = gridContainer.querySelectorAll(".slick-pane.frozen");
      const frozenHeaders = gridContainer.querySelectorAll(".slick-header-left");

      if (!frozenPanes.length) return;

      frozenPanes.forEach((pane: Element) => pane.classList.add("is-scrolling"));
      frozenHeaders.forEach((pane: Element) => pane.classList.add("is-scrolling"));

      clearTimeout(gridRef.current._scrollTimeout);

      gridRef.current._scrollTimeout = setTimeout(() => {
        frozenPanes.forEach((pane: Element) => pane.classList.remove("is-scrolling"));
        frozenHeaders.forEach((pane: Element) => pane.classList.remove("is-scrolling"));
      }, 30);
    };

    grid.onScroll.subscribe(scrollHandler);

    return () => {
      grid.onScroll.unsubscribe(scrollHandler);
      clearTimeout(gridRef.current?._scrollTimeout);
    };
  }, [gridMounted, gridRef]);

  const excelExportService = new ExcelExportService();
  // Same widening idiom as the legacy SlickGrid wrapper: attach the service to the global for the
  // export helpers that look it up there, without pretending globalThis is only that one property.
  (globalThis as typeof globalThis & { excelExportService?: ExcelExportService }).excelExportService =
    excelExportService;

  const excelExportOptions = {
    sanitizeDataExport: true,
    groupCollapsedSymbol: "",
    groupExpandedSymbol: "",
  };

  const onSaveColumns = (cols: Column[]): void => {
    if (props.enableViews && !preventUpdateCurrentView.current && viewsLoaded) {
      handleUpdateCurrentView({
        columns: cols.map((data) => data.id),
      });
    }
    if (onSaveColumnsRef.current) {
      onSaveColumnsRef.current(cols);
    }
  };

  const gridOptions: Partial<GridOption> = {
    sanitizer: (dirtyHtml: string) => DOMPurify.sanitize(dirtyHtml),
    enableHeaderMenu: false,
    enableContextMenu: false,
    enableHeaderButton: true,
    enableGridMenu: props.enableGridMenu ?? false,
    enableColumnPicker: props.enableColumnPicker ?? false,
    showCustomFooter: false,
    enableSorting: false,
    enableCellRowSpan: true,
    rowTopOffsetRenderType: "top",
    enableColumnReorder: true,
    enableCellNavigation: !props.isEditMode,
    forceFitColumns: false,
    enableAutoSizeColumns: true,
    headerButton: {
      // slickgrid DECLARES this first argument as its own `SlickEventData`, but SlickHeaderButtons
      // binds the button with a plain DOM listener and hands the native MouseEvent straight through
      // (menuBaseClass.populateSingleCommandOrOptionItem) — which is what the popup needs, since
      // SlickEventData carries no `currentTarget`. Widening the parameter states both.
      onCommand: (e: SlickEventData | MouseEvent, args: HeaderButtonOnCommandArgs) => {
        const column: Column & { filterType?: FilterType } = args.column;
        handleFilterIconClick(
          e instanceof MouseEvent ? e : null,
          column.field || "",
          column.filterType || "Text",
          undefined,
        );
      },
    },
    gridMenu: {
      onCommand: (_e: unknown) => {},
    },
    ...(props.hasChild && {
      // The vendored option is declared looser than what the shipped code passes:
      // `preRegisterExternalExtensions?: (eventPubSubService: BasePubSubService) => ...`
      // (node_modules/@slickgrid-universal/common/dist/types/interfaces/gridOption.interface.d.ts:393)
      // but ExtensionService calls it with its own `this.pubSubService`
      // (common/dist/esm/services/extension.service.js:307), which slickgrid-react constructs as
      // `new EventPubSubService()` and hands to `new ExtensionService(...)`
      // (slickgrid-react/dist/esm/components/slickgrid-react.js:150 and :163) — exactly the type
      // `SlickRowDetailView`'s constructor requires
      // (slickgrid-react/dist/types/extensions/slickRowDetailView.d.ts:22).
      preRegisterExternalExtensions: (pubSubService: BasePubSubService) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- vendor option declares the base interface, see comment above
        const rowDetail = new SlickRowDetailView(pubSubService as EventPubSubService);
        return [{ name: ExtensionName.rowDetailView, instance: rowDetail }];
      },
    }),
    externalResources: [excelExportService],
    enableExcelExport: true,
    excelExportOptions: excelExportOptions,
    // CUBE-27860 / Sentry CUBE-FRONTEND-C63. enableExcelCopyBuffer is on for Ctrl+C copy-out to
    // Excel only; nothing here persists a Ctrl+V paste (no grid sets `editable`, editorTemplate
    // cells are React formatters, and no caller wires onPasteCells), so a paste only mutated the
    // in-memory row until the next fetch. It also crashed: slickgrid 5.x reads
    // `columns[activeCell + x].hidden` BEFORE bounds-checking `destx < maxDestX`
    // (@slickgrid-universal/common@5.14.0 slickCellExternalCopyManager.js:284-293), so pasting
    // more columns than remain right of the anchor cell threw "Cannot read properties of
    // undefined (reading 'hidden')". readOnlyMode short-circuits only the Ctrl+V branch and
    // leaves copy intact. Upstream added `if (!column) break;` in 10.5.2 - 5.14.0 is the last
    // published 5.x, so drop this once the slickgrid 10 upgrade lands.
    enableExcelCopyBuffer: true,
    excelCopyBufferOptions: { readOnlyMode: true },
    createPreHeaderPanel: true,
    preHeaderPanelHeight: 28,
    // Wrap grids span N line-units per row (see rowResizeModalUtils.wrap); 22 ≈ one 1.3rem line
    // so wrapped rows track their text. Non-wrap grids keep the standard 48px "small" row.
    rowHeight: props.enableWrapText ? 22 : 48,
    multiColumnSort: false,
    multiSelect: props.enableMultiSelect ?? true,
    enableAutoResize: true,
    enableFiltering: Object.hasOwn(props, "enableFiltering")
      ? !!props.enableFiltering
      : !!(props.enableFilteringOption || props.treeGrid),
    enableDraggableGrouping: props.allowGrouping ?? false,
    enableRowSelection: !props.isEditMode,
    topHeaderPanelHeight: 35,
    enableCheckboxSelector: !props.isEditMode && !props.disableCheckBoxSelection,
    checkboxSelector: { toolTip: "" },
    rowSelectionOptions: { selectActiveRow: false },
    ...(props.treeGrid && {
      enableTreeData: true,
      treeDataOptions: {
        columnId: String(props.initialSortColumnId ?? ""),
        parentPropName: String(props.parentIdMapping ?? ""),
        levelPropName: props.levelPropName || "treeLevel",
        indentMarginLeft: 25,
        initiallyCollapsed: !props.initiallyExpanded,
        initialSort: {
          columnId: String(props.initialSortColumnId ?? ""),
          direction: props.initialSortDirection || "DESC",
        },
        titleFormatter: props.titleFormatter,
      },
    }),
    ...(props.dragAndDrop && {
      enableRowMoveManager: true,
      rowMoveManager: {
        columnIndexPosition: 0,
        cancelEditOnDrag: true,
        disableRowSelection: true,
        hideRowMoveShadow: false,
        onBeforeMoveRows,
        onMoveRows: (e: Event, args: RowMoveData & { grid: SlickGridType }) => {
          void onMoveRows(e, args);
        },
      },
    }),
    autoResize: {
      container: `#${id.replaceAll(":", "_")}`,
      resizeDetection: "container",
      calculateAvailableSizeBy: "container",
    },
    autoHeight: !!props.hasChild,
    ...props.gridOptions,
  };

  gridOptions.draggableGrouping = {
    dropPlaceHolderText: "Drop a column header here to group by the column",
    deleteIconCssClass: "mdi mdi-close text-color-danger",
    sortAscIconCssClass: "mdi mdi-arrow-up",
    sortDescIconCssClass: "mdi mdi-arrow-down",
  };

  const mergedToolbar = useMemo(() => {
    const base = props.toolbar || [];
    if (Object.keys(toolbarDisabledOverrides).length === 0) return base;
    return base.map((item) => {
      const overrideDisabled = item.id === undefined ? undefined : toolbarDisabledOverrides[item.id];
      if (overrideDisabled === undefined) return item;
      return { ...item, disabled: overrideDisabled };
    });
  }, [props.toolbar, toolbarDisabledOverrides]);

  const scrollHandlerProps: { onScroll?: typeof handleOnScroll } = props.infiniteScroll
    ? { onScroll: handleOnScroll }
    : {};

  // Defensive clone: slickgrid-react's componentWillUnmount mutates the columnDefinitions
  // array in-place (sets every index to null). Passing a shallow clone ensures the library
  // only ever corrupts the disposable copy, leaving the parent's memoized array intact.
  // useMemo keeps the clone reference-stable so SlickgridReact's componentDidUpdate doesn't
  // see a spurious column change on every render.
  // Also: attach a per-column valueParserCallback for the ExcelExportService so that
  // _excelValueGetter / _valueGetter are always honoured — even if SlickgridReact resets
  // columns from this prop and the useEffect that adds the callback hasn't re-run yet.
  const safeColumnDefinitions = useMemo(
    () =>
      [...(props.columnDefinitions ?? [])].map((col) => {
        const colDef: RuntimeColumnDefinition<T> = col;
        if (col.id === "_checkbox_selector") {
          return {
            ...col,
            formatter: muiCheckboxFormatter as Formatter,
            sortable: false,
            filterable: false,
            groupable: false,
            minWidth: 60,
            originalWidth: 60,
            maxWidth: 60,
            name: "",
          };
        }
        return {
          ...col,
          formatter: getFormatter(col),
          excelExportOptions: {
            ...(col as { excelExportOptions?: Record<string, unknown> }).excelExportOptions,
            valueParserCallback: (_dataObj: unknown, { dataContext }: { dataContext: T }) => {
              if (typeof colDef._excelValueGetter === "function") return colDef._excelValueGetter(dataContext);
              if (typeof colDef._valueGetter === "function") return colDef._valueGetter(dataContext);
              if (typeof colDef._valueGetter === "string") return get(dataContext, colDef._valueGetter) ?? "";
              return get(dataContext, String(col.field));
            },
          },
        };
      }),

    [props.columnDefinitions, props.enableWrapText],
  );

  /**
   * The shimmer takes its own minimal column shape (see Shimmer.tsx); project onto it rather than
   * handing over grid columns whose `field`/`headerRenderer` types it does not share.
   *
   * `headerRenderer` is the delicate one. The shimmer chooses between the column's own header and
   * the icon+label header it builds itself by testing `headerRenderer` for TRUTHINESS
   * (Shimmer.tsx:136) — NOT on what the renderer returns — and it can only render a string (as
   * HTML) or a React node. `ColumnDefinition.headerRenderer` may also return a detached HTMLElement
   * (the arm `_customHeaderRenderer` uses today, e.g. VariableRateModal's range headers, which
   * mount a React root into a `document.createElement("div")`), which the shimmer can render
   * neither way: handing it a wrapper that returns `null` for that case would leave the header cell
   * EMPTY, because the truthy wrapper is what the ternary tests. So for a non-string result the
   * property has to be ABSENT — undecidable without running the renderer, hence the getter:
   * resolved on FIRST READ, i.e. only when a shimmer actually renders and never on a grid that goes
   * straight to data, and cached so the shimmer's truthiness test and its call get one function.
   */
  const shimmerColumnDefinitions = useMemo(
    () =>
      (props.columnDefinitions ?? []).map((col) => {
        const renderHeader = col.headerRenderer;
        let headerRendererResolved = false;
        let stringHeaderRenderer: (() => string) | undefined;
        return {
          id: col.id,
          name: col.name,
          field: String(col.field),
          formatterType: col.formatterType,
          filterType: col.filterType,
          width: col.width,
          minWidth: col.minWidth,
          maxWidth: col.maxWidth,
          hidden: col.hidden,
          get headerRenderer(): (() => string) | undefined {
            if (!headerRendererResolved) {
              headerRendererResolved = true;
              const rendered = renderHeader?.();
              if (typeof rendered === "string") stringHeaderRenderer = () => rendered;
            }
            return stringHeaderRenderer;
          },
        };
      }),
    [props.columnDefinitions],
  );

  const showToolar = useMemo(
    () =>
      Boolean(
        !props.disableToolbar &&
        (props.toolbar ||
          props.enableFilteringOption ||
          props.enableSortingOption ||
          props.enableGroupingOption ||
          props.enableColumnChooser ||
          props.enableRowResize ||
          props.excelExport ||
          props.enableViews ||
          props.enableRefreshOption ||
          enableSearchBar) &&
        gridMounted,
      ),
    [
      props.disableToolbar,
      props.toolbar,
      props.enableFilteringOption,
      props.enableSortingOption,
      props.enableGroupingOption,
      props.enableColumnChooser,
      props.enableRowResize,
      props.excelExport,
      gridMounted,
      props.enableViews,
      props.enableRefreshOption,
      enableSearchBar,
    ],
  );

  return (
    <div
      dir="ltr"
      className={`h-full w-full cube-slick-grid1 rounded-lg overflow-hidden ${
        props.hideScrollbar ? "cube-slick-grid1--hide-scrollbar" : ""
      } ${props.disableOuterBorder ? "cube-slick-grid1--no-outer-border" : ""}`}
    >
      <div className="flex flex-col h-full min-w-full overflow-auto">
        {showToolar &&
          (() => {
            const toolbarElement = (
              <ToolBar
                toolbar={mergedToolbar}
                toolbarClickHandler={stableToolbarClickHandler}
                showToolbarBorder={props.showToolbarBorder}
                enableColumnChooser={props.enableColumnChooser}
                enableSearchBar={enableSearchBar}
                searchPlaceholder={props.searchPlaceholder ?? t("Search")}
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                searchDebounceMs={props.searchDebounceMs ?? 300}
                excelExportServiceRef={excelExportServiceRef}
                columnDefinitions={props.columnDefinitions}
                ref={gridRef}
                stateRef={stateRef}
                ellipseFormatterWithTooltip={ellipseFormatterWithTooltip}
                height={props.toolbarHeight}
                propertiesPanel={props.propertiesPanel}
                propertiesDisabled={props.propertiesDisabled}
                excelExportExtension={excelExportExtension}
                excelExport={props.excelExport}
                excelExportDisabled={props.excelExportDisabled}
                enableFilteringOption={props.enableFilteringOption}
                enableSortingOption={props.enableSortingOption}
                enableGroupingOption={props.enableGroupingOption}
                enableRefreshOption={props.enableRefreshOption}
                refreshOptionCallBack={props.refreshOptionCallBack}
                modalState={modalState}
                setModalState={setModalState}
                modalAnchor={modalAnchor}
                sortDataset={sortDataset}
                excelExportCallback={props.excelExportCallback}
                dataset={dataset}
                infiniteScroll={props.infiniteScroll}
                sortConditions={sortConditions}
                setSortConditions={setSortConditions}
                filterConditions={appliedFilterConditions}
                setFilterConditions={setAppliedFilterConditions}
                filterType={filterType}
                remoteDataSource={props.remoteDataSource}
                beforeDataBound={props.beforeDataBound}
                datasetForFetchingOptions={props.dataset}
                query={props.query}
                localData={!props.remoteDataSource}
                getUpdatedFilterConditions={getUpdatedFilterConditions}
                applyFilterFunction={applyFilter}
                setAppliedFilters={setAppliedFilters}
                appliedFilters={appliedFilters}
                setFilterPredicates={setFilterPredicates}
                setFilterConditionsObject={setFilterConditions}
                groupConditions={groupConditions}
                setGroupConditions={setGroupConditions}
                enableWrapText={props.enableWrapText}
                handleRowResizeOptionClick={onRowResizeOptionClick}
                enableRowResize={props.enableRowResize}
                currentColumnId={currentColumnId}
                enableViews={props.enableViews}
                callRemote={callRemote}
                viewsOptions={props.viewsOptions}
                onClickLayout={onClickLayout}
                createNewLayoutData={createNewLayoutData}
                triggerUpdateCurrentView={triggerUpdateCurrentView}
                onViewsLoaded={onViewsLoaded}
                specialColumns={specialColumnRef.current}
                tempCondition={tempCondition}
                callbackForVisibleColumnsUpdate={callbackForVisibleColumnsUpdateRef.current}
                setTempCondition={setTempCondition}
                setEnableWrapText={props.setEnableWrapText}
                onSaveColumns={onSaveColumns}
                gridId={id}
              />
            );
            // Portal the toolbar into a shared container when the host provides one (e.g. a common toolbar
            // spanning a tree + this grid); otherwise render it inline above the grid as before.
            return props.toolbarContainer ? createPortal(toolbarElement, props.toolbarContainer) : toolbarElement;
          })()}
        {!props.hideNoDataIllustration &&
          (props.surpassedDataset ? props.surpassedDataset.length === 0 : dataset.length === 0) &&
          !loaderOnPageChange &&
          !firstCallRef.current &&
          (props.noDataIllustration ? (
            props.noDataIllustration()
          ) : (
            <div className="border-t border-[#C6D8EC] flex flex-col items-center justify-center w-full h-full overflow-hidden">
              <IconComponent
                name="NoData"
                width="18.75rem"
                height="18.8125rem"
                className="flex overflow-hidden w-full h-full max-w-[18.75rem] max-h-[18.75rem]"
              />
              <div className="mt-4 !not-italic text-[#666A84] text-center font-semibold text-xl leading-[120%] ">
                <Text>{t("No Data")}</Text>
              </div>
            </div>
          ))}
        <div
          id={id.replaceAll(":", "_")}
          className={
            "relative flex h-full min-w-full overflow-auto " +
            (showToolar && !props.disableOuterBorder ? "border-t border-[#C6D8EC] " : "") +
            (props.isEditMode && props.treeGrid ? "slick-tree-edit-mode " : "") +
            " " +
            cssClasses +
            `${
              !props.hideNoDataIllustration &&
              (props.surpassedDataset ? props.surpassedDataset.length === 0 : dataset.length === 0) &&
              !loaderOnPageChange &&
              !firstCallRef.current
                ? " hidden"
                : ""
            }`
          }
        >
          {!allPrerequisiteLoaded || firstCallRef.current ? (
            <div className="w-full h-full bg-white">
              <GridShimmer
                rowCount={7}
                // Projected onto the shimmer's own column shape instead of being asserted into it:
                // its `field` is a plain string and its `headerRenderer` returns React nodes, so an
                // HTMLElement-returning renderer has no meaning there (it renders strings as HTML).
                columnDefinitions={shimmerColumnDefinitions}
                rowHeight={gridOptions.rowHeight || 48}
                enableCheckbox={!props.isEditMode && !props.disableCheckBoxSelection}
              />
            </div>
          ) : (
            /* Library boundary. VERIFIED: dropping this assertion makes tsc reject the call with
               TS2769 — `ColumnDefinition<T>[]` is not assignable to slickgrid's `Column<any>[]`,
               because `excelExportOptions.valueParserCallback` returns `unknown` where
               `GetDataValueCallback` (node_modules/@slickgrid-universal/common/dist/types/
               interfaces/columnExcelExportOption.interface.d.ts:43) returns
               `Date | string | number | ExcelColumnMetadata`, and `field` is `keyof T` here versus
               `Join<PathsToStringProps<T>, '.'>` there (column.interface.d.ts:110). Same
               vendor-narrower boundary `readGridColumn` / `asSlickColumns` cross above. */
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- library boundary, see comment above (verified: removing it yields TS2769 on `columnDefinitions`)
            React.createElement(SlickgridReact as unknown as React.ComponentType<Record<string, unknown>>, {
              ...props,
              dataset: props.treeGrid ? dataset : safePropsDataset,
              gridId: props.gridId ?? `${id.replaceAll(":", "_")}_grid18`,
              gridOptions,
              onReactGridCreated: ($event: CustomEvent<SlickgridReactInstanceX<T>>) => {
                reactGridReady($event.detail);
                // The pane elements are PROTECTED SlickGrid internals — declared
                // `protected _paneHeaderR / _paneTopL / _paneTopR: HTMLDivElement` at
                // node_modules/@slickgrid-universal/common/dist/types/core/slickGrid.d.ts:258-260,
                // so they are not reachable through the public type, and they only exist once the
                // grid has rendered its panes, hence the optional members and the `?.` reads below.
                // Used purely to tag test ids.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- protected vendor internals, see comment above
                const slickGrid = $event.detail.slickGrid as unknown as {
                  _paneHeaderR?: HTMLElement;
                  _paneTopL?: HTMLElement;
                  _paneTopR?: HTMLElement;
                };
                slickGrid._paneHeaderR?.setAttribute("data-testid", "SlickGridPaneHeader");
                slickGrid._paneTopL?.setAttribute("data-testid", "SlickGridPanePinContent");
                slickGrid._paneTopR?.setAttribute("data-testid", "SlickGridPaneContent");
              },
              columnDefinitions: safeColumnDefinitions,
              setTotalItemCount,
              onClick,
              onSelectedRowIdsChanged,
              ...scrollHandlerProps,
              onAfterSetColumns: () => {
                if (gridRef.current) {
                  let cols: Column[] = gridRef.current.slickGrid.getColumns();

                  // SlickgridReact may reset columns from columnDefinitions prop,
                  // stripping special columns (_checkbox_selector, _move). Re-add them.
                  if (specialColumnRef.current.length > 0 && !isEditModeRef.current) {
                    const specialIds = new Set(specialColumnRef.current.map((c) => c.id));
                    const hasAllSpecial = specialColumnRef.current.every((sc) => cols.some((c) => c.id === sc.id));
                    if (!hasAllSpecial) {
                      const userCols = cols.filter((c) => !specialIds.has(c.id));
                      cols = [...specialColumnRef.current, ...userCols];
                      isNormalizingRef.current = true;
                      gridRef.current.slickGrid.setColumns(cols);
                      isNormalizingRef.current = false;
                    }
                  }

                  if (handleUpdateCurrentViewPropRef.current) {
                    handleUpdateCurrentViewPropRef.current({
                      columns: cols.map((data) => data.id),
                    });
                  }
                  updatePreHeaderVisibility(gridRef.current.slickGrid);
                  if (isNormalizingRef.current) return;

                  const normalized = normalizeColumns(cols);

                  type ColWithHeader = Column & { headerCssClass?: string | null; header?: unknown };
                  const changed =
                    cols.length !== normalized.length ||
                    cols.some((col: ColWithHeader, i: number) => {
                      const norm: ColWithHeader | undefined = normalized[i];
                      return (
                        !norm ||
                        col.headerCssClass !== norm.headerCssClass ||
                        JSON.stringify(col.header) !== JSON.stringify(norm.header)
                      );
                    });

                  if (changed) {
                    isNormalizingRef.current = true;
                    gridRef.current.slickGrid.setColumns(normalized);
                    isNormalizingRef.current = false;
                  }

                  gridRef.current.slickGrid.remapAllColumnsRowSpan();
                  gridRef.current.dataView.refresh();

                  // Dynamically adjust auto-size based on total column width vs container
                  // updateAutoSizeColumns();
                }
              },
            })
          )}
          {loaderOnPageChange && !firstCallRef.current && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-[var(--surface-white)] opacity-70 pointer-events-auto">
              <div className="flex items-center justify-center">
                <CircularProgress />
              </div>
            </div>
          )}
        </div>
        {showPageCount && (
          <div className="h-15! w-full flex items-center justify-between p-3 border-t sticky bottom-0 border-[#C6D8EC] bg-[#FAFCFF] shadow-md">
            <PagerComponent
              ref={gridRef}
              pageSize={pageSize}
              currentPage={currentPage}
              totalItemCount={totalItemCount}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              totalPages={totalPages}
              allowPagination={props.allowPagination}
              selectedItems={selectedItems}
              gridOptions={gridOptions}
              currentLoadedCount={currentLoadedCount}
              infiniteScroll={props.infiniteScroll}
              rowUnitLabel={props.rowUnitLabel}
              totalRowCount={props.totalRowCount}
              isSelectAllVisible={
                !!props.infiniteScroll &&
                selectedItems > 0 &&
                selectedItems === currentLoadedCount &&
                currentLoadedCount < totalItemCount
              }
              onSelectAllRecords={handleSelectAllRecords}
            />
          </div>
        )}
        {showOptionDropdown && (
          <OptionDropDown
            anchorEl={anchorEl}
            handleClose={handleCloseDropdown}
            setModalState={setModalState}
            setModalAnchor={setModalAnchor}
            setGroupConditions={setGroupConditions}
            setSortConditions={setSortConditions}
            setFilterConditions={setAppliedFilterConditions}
            setAppliedFilters={setAppliedFilters}
            setFilterPredicates={setFilterPredicates}
            setFilterConditionsObject={setFilterConditions}
            currentColumnId={currentColumnId}
            pinnedColumns={pinnedColumns}
            sortConditions={sortConditions}
            hasCustomColumnChooser={props.hasCustomColumnChooser}
            groupConditions={groupConditions}
            filterConditions={filterConditions}
            onChange={(selectedOption: DropdownOption) => handleOptionSelect(selectedOption)}
            // KNOWN GAP: OptionDropDown declares its columns non-generically, as
            // `ValidatedColumnDefinition<GridRowBase>[] | null` (./OptionDropDown.tsx:40) — a column
            // of a concrete row type is not assignable to that (`field: keyof T` vs
            // `keyof GridRowBase`). Making OptionDropDown generic is the real fix and belongs in
            // that file.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- non-generic consumer prop, see comment above
            columnDefinitions={props.columnDefinitions as ValidatedColumnDefinition<GridRowBase>[]}
            enableColumnChooser={props.enableColumnChooser ?? false}
            enableFilteringOption={props.enableFilteringOption ?? false}
            enableGroupingOption={props.enableGroupingOption ?? false}
            enableSortingOption={props.enableSortingOption ?? false}
            gridId={id}
          />
        )}
      </div>
      {loaderForInfiniteScroll && (
        <output
          aria-live="polite"
          className="absolute bottom-10 start-1/2 -translate-x-1/2 inline-flex items-center justify-center gap-2
    rounded-lg bg-[#192048] text-white
    text-sm font-medium opacity-100
    py-2 px-4
    shadow-lg ring-1 ring-slate-900/5
    backdrop-blur-sm
    z-50
  "
        >
          <CircularProgress size={18} thickness={5} sx={{ color: "white" }} />
          <Text className="text-xs font-semibold text-slate-100">{t("Loading...")}</Text>
        </output>
      )}
    </div>
  );
  // Re-states the generic signature `forwardRef` erased above, replacing what used to be `as any`.
  // `Omit<…, "ref">` mirrors exactly what the public `SlickGrid` wrapper below hands it, so the one
  // call site stays checked rather than opaque.
}) as <T extends GridRowBase>(
  props: Omit<SlickGridProps<T>, "ref"> & { ref?: React.Ref<SlickgridReactInstanceX<T>> },
) => React.ReactElement;

/**
 * Type-safe SlickGrid component.
 *
 * IMPORTANT: Use `typedColumns<YourRowType>()` to create column definitions
 * for proper type safety.
 *
 * @example
 * interface MyRow extends GridRowBase {
 *   name: string;
 *   count: number;
 * }
 *
 * const columns = typedColumns<MyRow>([
 *   { id: "name", field: "name", name: "Name" },
 *   { id: "count", field: "count", name: "Count" }
 * ]);
 *
 * <SlickGrid
 *   dataset={myData}
 *   columnDefinitions={columns}
 * />
 */
// `forwardRef` erases the render function's own type parameter (its result is a plain
// `ForwardRefExoticComponent`), so the generic signature is re-stated below — the standard React
// idiom for a generic forwardRef component. It mirrors exactly what the render function accepts,
// so every call site stays checked.
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- forwardRef drops the render function's type parameter; see comment above
const SlickGrid = forwardRef(function SlickGrid<T extends GridRowBase>(
  props: Omit<SlickGridProps<T>, "ref">,
  ref: React.Ref<SlickgridReactInstanceX<T>>,
) {
  return <SlickGridInner {...props} ref={ref} />;
}) as <T extends GridRowBase>(
  props: SlickGridProps<T> & { ref?: React.Ref<SlickgridReactInstanceX<T>> },
) => React.ReactElement;

export default SlickGrid;
